import React, { useState, useEffect, useRef, startTransition, useMemo, useCallback, Suspense } from 'react';
import { Mic, MicOff, Heart, X, ChevronLeft, ChevronRight, ShoppingCart, Pin, Trash2, Keyboard, Send, Loader2, Camera, Film } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useConsent } from './consent/useConsent';
import { canCaptureAudio, DISCLOSURE, DEFAULT_CATEGORIES } from './consent/consentConfig';
import { ConsentGate } from './consent/ConsentGate';
import { ConsentCategoryList } from './consent/ConsentCategoryList';
import { PrivacyLauncher } from './consent/PrivacyLauncher';
import { PrivacyPolicy } from './consent/PrivacyPolicy';
import { VoiceOffPrompt } from './consent/VoiceOffPrompt';
import useMicBlocked, { voiceOffPromptProps, withVoice } from './consent/useMicBlocked';
import { setMicVoiceControl } from './consent/micVoiceControl';
import ParticlePill from './components/ParticlePill';
import CavalryViz, { warmCavalryModule } from './components/CavalryViz';
import { resolveVisualizerState } from './components/voiceVisualizerState';
import { useThinkingWatchdog } from './state/useThinkingWatchdog';
import { makeThinkingSilenceHandler } from './state/thinkingSilenceBeat';
import { useManualStop } from './state/useManualStop';
import { useIntroWatchdog } from './state/useIntroWatchdog';
import { createFilmTailGate, isTurnBoundary } from './state/filmAudioGate';
import { decayAgentEcho, roomBeatsEcho, ECHO_REJECT_RATIO_DEFAULT } from './state/echoGate';
import { attachMicTrackHealth, createRestartBudget } from './state/micTrackHealth';
// SPEC-26 Phase 4 boot sequence. Eagerly imported (not React.lazy like Projectionist/Scene3D):
// these are the first things a reel-mode guest sees, so a lazy chunk fetch would defeat the point.
import Loader, { LOADER_DISPLAY_MS } from './components/BootSequence/Loader';
import EnterBoutiqueGate, {
  DEFAULT_WORDMARK_SRC,
  DEFAULT_BACKGROUND_WEBP_SRC,
} from './components/BootSequence/EnterBoutiqueGate';
import { preloadReelAssets, configureReelSources } from './components/Projectionist/useChapterManifest';
import { applyHydration, buildSelectionOffer } from './state/sessionUiState';
import { matchAttributes } from './components/Projectionist/attributeMatcher';
import { transcriptStore, useLatestTranscript } from './state/transcriptStore';
import { hasSeenIntro } from './state/visitMemory';
import useVisitMemory from './state/useVisitMemory'; // SPEC-43 R1: 7-day returning-guest window
import useIntroReplayBridge from './state/useIntroReplayBridge'; // SPEC-43 R2: film gate around a replay
import { installQaReset } from './state/qaReset';
import { readAnonSid, resolveSessionId, clearStoredSessionId } from './state/sessionIdentity';
import { getPageId } from './state/pageIdentity'; // SPEC-67 D-1
import { isWaitingRoomFrame, wantsResumeParam } from './state/sessionResume'; // SPEC-67 D-9
import { useSessionResume } from './state/useSessionResume'; // SPEC-67 D-9
import useLogoSoftReset from './state/useLogoSoftReset'; // SPEC-74: the logo starts the visit over
import LogoResetButton from './components/ReelChrome/LogoResetButton'; // SPEC-74
import { readCopy as readTutorialCopy } from './state/firstVisitTutorial'; // SPEC-76
import { readUiExperiment } from './uiExperiment/toggles'; // SPEC-82 D-1
import useIntroExperiment from './uiExperiment/useIntroExperiment'; // SPEC-82 §4.2
import useRememberedReel from './uiExperiment/useRememberedReel'; // no_enter_gate: the rail before the agent connects
import useConnectingCue from './uiExperiment/useConnectingCue'; // mic_voice_control: 'Connecting to ...' until the first words
import useEarlyConnect from './uiExperiment/useEarlyConnect'; // no_enter_gate: a returning guest's agent connects silently at landing
import { micVoiceToggle } from './uiExperiment/micVoiceToggle'; // the footer mic button (mic_voice_control)
import { isScreenMove } from './uiExperiment/implicitRelease'; // no_enter_gate: only a screen move starts the conversation
import useVoiceFirstChrome from './uiExperiment/useVoiceFirstChrome'; // SPEC-82 §4.2
import usePanelIndicator from './state/usePanelIndicator'; // the indicator steps aside while a rail panel is open on a phone
import useImplicitRelease from './uiExperiment/useImplicitRelease'; // SPEC-82 Rev 3: a pre-tap request is the tap
import CollectionMenu from './components/VoiceFirst/CollectionMenu/CollectionMenu'; // SPEC-82 §4.8
import useGateHint from './components/VoiceFirst/GateHint/useGateHint'; // SPEC-82 §4.10
import TutorialPrimer from './components/WelcomeTutorial/TutorialPrimer'; // SPEC-76: the read-first panel on the gate
import { resetSpeechClock, scheduleSpeech } from './state/speechClock';
import { extendPlayoutDeadline, isWithinPlayout } from './state/agentPlayoutDeadline';
// SPEC-26 Phase 4 Revision — the real Figma chrome (concertina rail + flanked CC bar).
import IconRail from './components/ReelChrome/IconRail';
import ReelFooter from './components/ReelChrome/ReelFooter';
import MobileChrome from './components/ReelChrome/MobileChrome';
import MobileFooter from './components/ReelChrome/MobileFooter';
import usePanelScrollReporter from './components/ReelChrome/usePanelScrollReporter';
import { WORDMARK, RAIL_LEFT_PCT, RAIL_TOP_PCT, RAIL_UNIT, INDICATOR, topOffset, u, uMin } from './components/ReelChrome/railGeometry';
import { CLUSTER_W } from './components/Projectionist/overlays/CaptionOverlay';
import { MOBILE_UNIT, safeTop, muMin, WORDMARK as MOBILE_WORDMARK, CONNECTION_INDICATOR_GAP } from './components/ReelChrome/mobileGeometry';
import { useBreakpoint } from './components/Projectionist/useViewportSize';
import { itemQuantity, lineTotalAmount, cartTotalAmount, cartUnitCount, MAX_LINE_QUANTITY } from './utils/cartMath';
import OverlayPortal from './components/Projectionist/overlays/OverlayPortal';
import { isSheetWishlisted, isSheetInBag, sheetBagUnits } from './components/Projectionist/overlays/sheetSelection';
// SPEC-53 — the second, static collection stage beside the reel (carousel + piece card + black fade).
import { useStageMachine, Stage } from './components/CollectionStage/stageReducer';
import { useStageTransition, StageFade } from './components/CollectionStage/useStageTransition';
import StageHost from './components/CollectionStage/StageHost';
import { reelShortName } from './components/CollectionStage/pieceEnvelope';
import { useWaitlist } from './components/Waitlist/useWaitlist'; // SPEC-52
import { useCheckout } from './components/Checkout/useCheckout'; // SPEC-46
import CheckoutOverlay from './components/Checkout/CheckoutOverlay'; // SPEC-46 Rev 7
import WaitlistModal from './components/Waitlist/WaitlistModal'; // SPEC-52
import { WAITLIST_PRICE_LABEL } from './components/Waitlist/waitlistGeometry'; // SPEC-52
import { useReelViewToggle } from './components/ReelChrome/ViewToggle/useReelViewToggle'; // SPEC-57
import { reelSwipeNavProps } from './components/Projectionist/swipeNav/swipeNavConfig'; // SPEC-86
import { withGesture } from './components/Projectionist/swipeNav/gesturePayload'; // SPEC-92
import ReelViewToggle from './components/ReelChrome/ViewToggle/ReelViewToggle'; // SPEC-57
import { VIEW_TOGGLE_Z } from './components/ReelChrome/ViewToggle/viewToggleGeometry'; // SPEC-57
// SPEC-26 Phase 5 — orientation lock. Eager like the boot sequence: it has to be able to block the
// very first frame a wrongly-held device paints.
import OrientationGate from './components/OrientationGate';
const Scene3DBoutique = React.lazy(() => import('./components/Scene3DBoutique'));
const Projectionist = React.lazy(() => import('./components/Projectionist'));

// ─── Mic visualizer selector ───────────────────────────────────────────────
// Resolved at runtime from GET /api/config → visualizer_mode.
// 'original' (default) | 'pill' | 'cavalry' | 'none'
// Set VISUALIZER_MODE env var in Azure app settings or k8s configmap.
const DEFAULT_VISUALIZER_MODE = 'original';

// This pilot ships one experience — the reel — and its boot beat/gate/intro film are all local,
// bundled assets (public/reel/*), not backend-dependent. If /api/config is unreachable (backend
// down, guest offline), falling back to `{}` used to drop the guest onto the pre-redesign "Enter
// Boutique" screen because `feature_flags.reel_mode` was simply missing. The intro/gate must work
// with zero network connectivity, so an unreachable config falls back to this instead of empty.
const OFFLINE_FALLBACK_BRAND_CONFIG = {
  brand_name: 'Ophion',
  tab_title: 'Ophion Watches',
  favicon_path: 'favicon.ico?v=2', // versioned: nginx serves .ico immutable for 7 days (2026-10-14)
  feature_flags: { reel_mode: true },
};

// What THIS origin last said about gating, remembered so a later config failure does not have to
// guess. An unreachable `/api/config` used to leave `authRequired` at its `true` default on every
// host but localhost, which meant one blip — a pod rollout, a cold start, a dropped request — put
// an ungated storefront behind the OTP gate. On a brand that does not gate, `ALLOWED_DEMO_EMAILS`
// is empty by design, so `request_otp` answers "If authorized, an OTP was sent" and sends nothing:
// the guest is locked out by a gate whose code can never arrive, and only a reload clears it.
// (Observed on ophion.dev 2026-09-15, chasing VER-97.)
const AUTH_REQUIRED_CACHE_KEY = 'versai_require_auth';

function rememberAuthRequired(required) {
  try {
    localStorage.setItem(AUTH_REQUIRED_CACHE_KEY, required ? 'true' : 'false');
  } catch (e) {
    // Private mode / blocked storage: we simply do not get the hint next time.
  }
}

function recalledAuthRequired() {
  // `null` = this origin has never loaded a config here, so there is nothing to recall and the
  // caller keeps its conservative default. Never widen access on a guess.
  try {
    const stored = localStorage.getItem(AUTH_REQUIRED_CACHE_KEY);
    return stored === 'true' ? true : stored === 'false' ? false : null;
  } catch (e) {
    return null;
  }
}

// Same-origin at runtime: the browser hits the frontend host and nginx reverse-proxies
// /ws/agent to the in-cluster agent. Local dev (localhost) still uses the env/default so
// `npm start` points at a local agent.
const WS_URL = (() => {
  if (typeof window !== 'undefined' && window.location) {
    const host = window.location.hostname;
    const isLocal = host === 'localhost' || host === '127.0.0.1';
    if (!isLocal) {
      const proto = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      return `${proto}//${window.location.host}/ws/agent`;
    }
  }
  return process.env.REACT_APP_WS_URL || 'ws://127.0.0.1:8000/ws/agent';
})();
const AUDIO_SAMPLE_RATE = 24000;
const MIC_SAMPLE_RATE = 16000;

// 🎧 Small scheduling head-start (seconds) used ONLY when the playback queue has run dry. Starting a
// fresh utterance a hair in the future (instead of exactly "now") gives the audio thread a safety
// cushion, so a brief main-thread stall can't cause an underrun/crackle on the agent's first words.
// It is not cumulative — back-to-back chunks still schedule off nextStartTime, so latency is unaffected
// mid-sentence.
const PLAYBACK_LOOKAHEAD = 0.12;

const AZURE_BASE = 'https://mmdevukspublicstorage.blob.core.windows.net/ophion-images/';

// 🎬 Cinematic brand films (Azure Blob). Brand-level — not tied to any catalog item.
const VIDEO_BASE = process.env.REACT_APP_VIDEO_BASE || 'https://mmdevukspublicstorage.blob.core.windows.net/baumevideo/';
const BRAND_VIDEOS = {
  intro: `${VIDEO_BASE}SEVENFRIDAY_intro_lifted.mp4`,
  assembly: `${VIDEO_BASE}SEVENFRIDAY_assembled_mechanism.mp4`,
  exploding: `${VIDEO_BASE}SEVENFRIDAY_exploding_mechanism.mp4`,
};

const VESPER_LORE_ASSETS = {
  plan_night: `${AZURE_BASE}800px-Étienne-Louis_Boullée_Memorial_Newton_Night.jpg`,
  starry_v1: `${AZURE_BASE}N7701015_JPEG_1_1DM.jpg`,
  elevation_v1: `${AZURE_BASE}N7701015_JPEG_2_2DM.jpg`,
  sphere_exterior: `${AZURE_BASE}N7701015_JPEG_4_4DM.jpg`,
  interior_v1: `${AZURE_BASE}N7701015_JPEG_5_5DM.jpg`,
  opera: `${AZURE_BASE}Boullée_-_Projet_d_Opéra_-_élévation.jpg`,
  plan_circles: `${AZURE_BASE}Étienne-Louis_Boullée,_Cénotaphe_de_Newton_-_01_-_Plan_du_Cénotaphe_de_Newton.jpg`,
  starry_interior: `${AZURE_BASE}N7701015_JPEG_6_6DM.jpg`,
  sphere_night: `${AZURE_BASE}c2.jpg`,
  interior_rays: `${AZURE_BASE}c4.jpg`,
  conical: `${AZURE_BASE}Figure+02_Conical+Cenotaph.jpg`,
  macro: `${AZURE_BASE}Macro.png`,
  collage: `${AZURE_BASE}bu.jpg`

};

const formatPrice = (price, currency = 'EUR') => {
  if (!price && price !== 0) return '';
  if (typeof price === 'string') return price;
  try {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(Number(price));
  } catch {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'EUR' }).format(Number(price));
  }
};

// Price display: prefer the brand-agnostic normalized envelope (item.price.display),
// fall back to the legacy price_info.price string so existing brands are unchanged.
// SPEC-52 review finding, 2026-09-10: `item.waitlist` is checked EXPLICITLY here, never inferred
// from the price string happening to be empty — the same fix as CollectionOverlay.js/
// ReelProductCard.js, for the same reason (R6: a stale/partially-normalized row could still carry
// a figure in the legacy `price_info` shim even once `waitlist` is stamped).
const priceDisplay = (item) => (
  item?.waitlist ? WAITLIST_PRICE_LABEL : (item?.price?.display || formatPrice(item?.price_info?.price))
);

// Build the grouped attribute panel for the product detail view from the normalized
// envelope's attributeGroups, then append any legacy specifications/technical_specs not
// already shown as a "Further Details" group — so curated brands (watches) never lose a row.
const buildDetailGroups = (p) => {
  if (!p) return [];
  const groups = (p.attributeGroups || []).map((g) => ({ label: g.label, rows: [...(g.rows || [])] }));
  const shown = new Set(groups.flatMap((g) => g.rows.map((r) => String(r.k).toLowerCase())));
  const extras = [];
  const addExtras = (obj) => {
    if (!obj || typeof obj !== 'object') return;
    Object.entries(obj).forEach(([k, v]) => {
      const label = String(k).replace(/_/g, ' ');
      if (v && !shown.has(label.toLowerCase())) {
        extras.push({ k: label, v: Array.isArray(v) ? v.join(', ') : String(v) });
        shown.add(label.toLowerCase());
      }
    });
  };
  if (!groups.length) addExtras(p.specifications);
  addExtras(p.technical_specs);
  if (groups.length) addExtras(p.specifications);
  if (extras.length) groups.push({ label: 'Further Details', rows: extras });
  return groups;
};

// A spec row is highlighted when the agent references it; match both the prettified label
// and its underscore form against the highlightedFeatures list.
const isRowHighlighted = (highlightedFeatures, label) =>
  highlightedFeatures.includes(label) || highlightedFeatures.includes(String(label).replace(/ /g, '_'));

// Build a side-by-side comparison from the selected items' normalized attributeGroups.
// Computed client-side (the backend's comparisonAxes are over the search grid, not an arbitrary
// selection). Returns rows of { label, values[], diff } aligned across the selected products.
// 🧠 "Show the brain" — ambient widget surfacing the live Dynamic Intent Model read from the backend
// (intent / mood / trajectory + a confidence meter). Deliberately quiet and tasteful, bottom-left.
const TRAJECTORY_GLYPH = { escalating: '↗', steady: '→', cooling: '↘', pivoting: '⤵' };
const TRAJECTORY_TONE = {
  escalating: 'text-emerald-600', steady: 'text-neutral-500',
  cooling: 'text-amber-600', pivoting: 'text-indigo-600',
};
const DimBrainWidget = ({ dim, show }) => {
  // Only render once the guest has actually spoken (parent passes `show`); before that the read is a
  // welcome-state default and meaningless. intent/mood are the LLM's contextual read (accurate).
  if (!show || !dim || (!dim.intent && !dim.mood)) return null;
  const pct = Math.round(Math.min(1, Math.max(0, Number(dim.confidence) || 0)) * 100);
  const traj = (dim.trajectory || 'steady').toLowerCase();
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
      className="pointer-events-none fixed bottom-5 left-5 z-[60] w-52 bg-white/70 backdrop-blur-xl border border-black/10 rounded-2xl px-4 py-3 shadow-[0_4px_24px_rgba(0,0,0,0.06)]"
    >
      <div className="flex items-center justify-between mb-2">
        <span className="text-[8px] uppercase tracking-[0.3em] text-neutral-400">Live read</span>
        <span className={`text-xs font-medium ${TRAJECTORY_TONE[traj] || 'text-neutral-500'}`} title={`trajectory: ${traj}`}>
          {TRAJECTORY_GLYPH[traj] || '→'} {traj}
        </span>
      </div>
      <div className="space-y-0.5">
        {dim.intent && (
          <div className="text-sm font-light text-neutral-900 capitalize leading-tight">{dim.intent}</div>
        )}
        {dim.mood && dim.mood.toLowerCase() !== 'neutral' && (
          <div className="text-[11px] italic text-neutral-500 capitalize">{dim.mood}</div>
        )}
      </div>
      {dim.context && (
        // How the agent is therefore speaking + CTA posture (the DIM's governance decision).
        <div className="mt-2 pt-2 border-t border-black/5">
          <div className="text-[8px] uppercase tracking-[0.2em] text-neutral-400">Responding</div>
          <div className="text-[10px] text-neutral-600 leading-snug mt-0.5">{dim.context}</div>
        </div>
      )}
      <div className="mt-2.5">
        <div className="h-1 w-full bg-black/5 rounded-full overflow-hidden">
          <motion.div className="h-full bg-neutral-800 rounded-full"
            initial={{ width: 0 }} animate={{ width: `${pct}%` }} transition={{ duration: 0.5 }} />
        </div>
        <div className="text-[8px] uppercase tracking-widest text-neutral-400 mt-1">Confidence {pct}%</div>
      </div>
    </motion.div>
  );
};

const buildComparison = (items) => {
  if (!items || items.length < 2) return { rows: [], groups: [] };
  const perItem = items.map((it) => {
    const map = {};
    (it.attributeGroups || []).forEach((g) =>
      (g.rows || []).forEach((r) => { if (!(r.k in map)) map[r.k] = r.v; }));
    return map;
  });
  // Preserve the attribute-GROUP structure (Cloth & Pattern, Cut & Construction, ...) using the
  // first item that defines each group, so the comparison reads as tidy sections, not a flat list.
  const groupOrder = [];
  items.forEach((it) => (it.attributeGroups || []).forEach((g) => {
    if (!groupOrder.find((x) => x.label === g.label)) {
      groupOrder.push({ label: g.label, keys: (g.rows || []).map((r) => r.k) });
    } else {
      const existing = groupOrder.find((x) => x.label === g.label);
      (g.rows || []).forEach((r) => { if (!existing.keys.includes(r.k)) existing.keys.push(r.k); });
    }
  }));
  const mkRow = (label) => {
    const values = perItem.map((m) => (m[label] != null && m[label] !== '' ? m[label] : '—'));
    const diff = new Set(values.map((v) => String(v).toLowerCase())).size > 1;
    return { label, values, diff };
  };
  const groups = groupOrder.map((g) => ({ label: g.label, rows: g.keys.map(mkRow) }));
  const rows = groups.flatMap((g) => g.rows); // flat fallback (back-compat)
  return { rows, groups };
};

// ==============================================================================
// 🎙️ CUSTOM HOOK: Voice Activity Detection (VAD) & DSP Engine
// ==============================================================================
const useVoiceActivityDetection = ({
  micAnalyser,
  agentAnalyser,
  isMicOn,
  isKeyboardOpen,
  // 🎙️ Tenant-gated (`feature_flags.manual_interrupt`): may the mic keep listening with the text
  // field up? Ophion's reel says yes — the guest talks AND types, and the STOP button covers the
  // barge-in that is withheld there. Baume/Huntsman leave it false and keep the original gate
  // byte-for-byte, because on those tenants `onSpeechStart` is far from a no-op (it closes a Veo
  // try-on film, sends `video_state`, dismisses the intro, perturbs the thinking watchdog) and
  // ambient room noise must not be able to fire it while somebody is typing.
  micLiveWithKeyboard = false,
  isAgentSpeakingRef,
  currentVolumeRef,
  speakingThreshold,
  listeningThreshold,
  // 🔇 How loud the room must be, RELATIVE to the agent's own output, before energy counts
  // toward a barge-in while the agent is talking. See the echo gate in updateVolume.
  echoRejectRatio = ECHO_REJECT_RATIO_DEFAULT,
  isIntroPlayingRef,
  onSpeechStart,
  onSpeechEnd,
  onInterrupt
}) => {
  // 1. Stable State Ref: Prevents the RAF loop from tearing down when React state changes.
  //
  // The CALLBACKS live here too, and that is a fix, not a tidy-up. They used to be in the effect's
  // dependency array, and App.js passes fresh inline closures every render, so the whole loop was
  // torn down and rebuilt on EVERY render — which reset `isSpeechActive`, `noiseEnergy` and the
  // falling-edge timer. Any re-render mid-utterance (and typing causes a great many) silently ate
  // that utterance's falling edge: `onSpeechEnd` never ran, so `isUserSpeaking` stayed true over
  // silence and `userHasSpokenRef` stayed stranded true, which is the mic gate that keeps the
  // agent's own voice out of its own ear. Reading them out of a ref keeps the closures fresh
  // without making the loop's edge state a casualty of rendering.
  const liveState = useRef({ isMicOn, isKeyboardOpen, micLiveWithKeyboard, speakingThreshold, listeningThreshold, echoRejectRatio });
  const callbacks = useRef({ onSpeechStart, onSpeechEnd, onInterrupt });
  useEffect(() => {
    liveState.current = { isMicOn, isKeyboardOpen, micLiveWithKeyboard, speakingThreshold, listeningThreshold, echoRejectRatio };
    callbacks.current = { onSpeechStart, onSpeechEnd, onInterrupt };
  });

  // QA secret reset: Ctrl+Alt+Shift+X wipes all guest storage and reloads as a first-time visitor.
  useEffect(() => installQaReset(), []);

  useEffect(() => {
    // 2. Pre-allocate memory ONCE (Fixes the GC memory leak)
    const dataArray = new Uint8Array(128);
    let animationFrameId;
    let noiseEnergy = 0;
    let silenceTimer = null;
    // 🔇 Decaying peak of the agent's OWN output, the reference the echo gate compares the
    // room against. A peak with a slow release rather than the instantaneous value, for two
    // reasons: the loopback reaches the mic a few milliseconds late, so a per-frame comparison
    // misaligns at every onset; and the agent's voice dips to nothing between words, which would
    // drop the reference to zero exactly during the gaps where echo is most audible.
    let agentEcho = 0;

    // 3. Edge-Trigger State: Prevents continuous firing
    let isSpeechActive = false;
    // Previous frame's `isAgentSpeakingRef.current`, so the loop can see the moment the agent STARTS
    // talking rather than only that it is talking. See the agent-start edge reset in updateVolume.
    let wasAgentSpeaking = false;

    // Helper: Calculate true acoustic loudness (Time-Domain RMS)
    const calculateRMS = (analyser) => {
      if (!analyser) return 0;
      analyser.getByteTimeDomainData(dataArray);
      let sum = 0;
      for (let i = 0; i < dataArray.length; i++) {
        const normalized = (dataArray[i] - 128) / 128.0;
        sum += normalized * normalized;
      }
      return Math.sqrt(sum / dataArray.length);
    };

    const updateVolume = () => {
      const { isMicOn, isKeyboardOpen, micLiveWithKeyboard, speakingThreshold, listeningThreshold, echoRejectRatio } = liveState.current;

      // 🎬 While the intro film plays, the mic graph is already live (built on the welcome screen) but
      // it must be invisible to the VAD: the film's unmuted audio leaks into the mic, and if we let it
      // build energy here it would fire a phantom turn the instant the intro ends. Treat mic as silent
      // during the intro — exactly as it was before the graph was built early.
      //
      // 🎙️ `isKeyboardOpen` used to be an unconditional third term here, and for the reel it was
      // wrong (owner, 2026-09-01): the guest wants to talk AND type. It never actually muted
      // anything — `startMicrophone` keys only on `isMicOn`, so PCM has always kept flowing to the
      // backend with the keyboard up. All this gate did was blind the CLIENT: no
      // speech_start/speech_end markers, no visualizer reaction, `isUserSpeaking` pinned false.
      // That is precisely the "mic reads as muted" report.
      //
      // It is now TENANT-GATED rather than deleted (`micLiveWithKeyboard` ← `manual_interrupt`).
      // Deleting it outright was not zero-diff for Baume/Huntsman: with no STOP button and no
      // rising-edge suppression reachable there, ambient noise while a guest typed would have fired
      // the full `onSpeechStart` — closing a Veo film mid-play, resetting backend video state,
      // dismissing the intro and perturbing the thinking watchdog. Those tenants keep the original
      // gate exactly. The one case the keyboard still changes on the reel is the barge-in dispatch
      // below, which is a different hazard and is handled there rather than by killing the loop.
      const micActive = isMicOn && (micLiveWithKeyboard || !isKeyboardOpen) && !isIntroPlayingRef?.current;

      const agentVol = calculateRMS(agentAnalyser.current);
      const micVol = micActive ? calculateRMS(micAnalyser.current) : 0;
      agentEcho = decayAgentEcho(agentEcho, agentVol);

      // Update visualizer (RMS values are mathematically smaller than FFT, so we multiply by 2 for visual bounce)
      // The MAX is deliberate and is exactly the right signal for the visualizer: the cluster shows
      // one 'speaking' shape for whoever is talking, and whoever is talking is the louder side.
      if (currentVolumeRef) {
        currentVolumeRef.current = Math.min(Math.max(agentVol, micVol) * 2.5, 1.0);
      }

      // 🎙️🛑 AGENT-START EDGE RESET — BUG FIX (2026-09-01), not a feature. It repairs a regression
      // introduced the same day by this hook's own lifecycle refactor, in a file CLAUDE.md freezes
      // for new logic; it edits the existing VAD loop and adds nothing to App.js's surface.
      //
      // Narrowing this effect's deps to the four refs was right, but it removed a reset nobody had
      // noticed was load-bearing. The three callbacks used to sit in the dep array as inline arrows,
      // so the whole loop was torn down and rebuilt on EVERY commit — and App commits several times
      // a second while the agent talks (`setLiveAgentText` — then also a per-message watchdog
      // counter, made render-free in the 2026-09-01 mobile jank pass). Each rebuild
      // re-ran `let isSpeechActive = false`. That accidental teardown WAS the reset for this latch.
      //
      // With the loop now permanent the latch sticks true across the agent's whole turn: the rise
      // branch below clears `silenceTimer` on every scrap of energy over threshold — the agent's own
      // voice through a laptop's speakers is enough, and it only takes one frame per 700ms — so the
      // falling edge never completes, while the `else` branch that would reset it needs
      // `micActive === false`, unreachable with the mic on, the keyboard shut and the intro over.
      // A latched `isSpeechActive` means the rising edge below CANNOT fire, and that rising edge is
      // the only route to `onInterrupt` — which is in turn the ONLY live audio flush on the voice
      // path (the backend never sends `control stop_audio`, and the other flush site is the STOP
      // button, which by design does not exist with the keyboard shut). Confirmed live: a voice
      // barge-in produced a `speech_end` with no `speech_start` — reachable only with the latch
      // already true — the queued answer played on over the guest, and `userHasSpokenRef` stayed
      // stranded true, so `handleMicFrame` streamed the agent's own unplayed tail back to Gemini as
      // guest audio and its server-side VAD killed the reply. The 4s watchdog was the symptom.
      //
      // The fix: reset the edge state on the false→true transition of `isAgentSpeakingRef`. Once the
      // agent is speaking, the model has already endpointed whatever the guest was saying — that is
      // WHY it is answering — so the NEXT saturation is by definition a barge-in and must be allowed
      // through to `onInterrupt`. This restores what the per-render teardown used to provide without
      // giving up the persistent loop or the falling edge that teardown was eating. It is symmetric
      // with the `micActive` drop-out branch below, which resets the same three pieces of state.
      //
      // A falling edge that was already OWED is fired here rather than binned along with its timer,
      // and the condition on it is the whole safety of this branch — in both directions.
      //
      // Fire it when the guest has stopped. `onSpeechEnd` is the only thing that clears
      // `userHasSpokenRef`, the barge-in exemption `handleMicFrame` reads to keep streaming while
      // the agent talks. Clearing the latch while silently dropping the pending `silenceTimer`
      // strands that flag on exactly the same turn, through a different door, and hands the bug
      // above straight back. (Verified: with this callback removed, the 'owed falling edge is not
      // eaten' test below reports no `speech_end` on the wire at all.)
      //
      // Do NOT fire it while the guest is still saturating the detector, which is not a nicety
      // either. The voice path has no discard gate: after a barge-in the backend keeps streaming the
      // abandoned tail, and every one of those chunks re-arms `isAgentSpeakingRef` — a fresh
      // false→true transition, several per second. Revoking the exemption on each would gate the mic
      // for the ~250ms the guest needs to re-saturate, over and over, so Gemini would never hear
      // enough of the barge-in to stop talking and the interrupt would cancel itself. Nothing is
      // stranded by staying quiet there: the guest is talking, so they re-saturate, `onInterrupt`
      // re-latches `isSpeechActive`, and the falling edge stays owed exactly as it was.
      //
      // `noiseEnergy >= 100` is the test for "still talking" because saturation is already the
      // loop's own definition of it — the same measure the rising edge uses. Not `silenceTimer`:
      // that only exists once energy has reached ZERO, so keying on it would miss the whole ~400ms
      // drain, which is a stopped guest with nothing owed to them yet and is where most first
      // chunks actually land. A guest caught in a word gap (energy dipped below 100 mid-utterance)
      // pays the 250ms re-saturation instead, which is what every other barge-in already costs.
      //
      // Known residual, deliberately not chased here: a guest saturating at the exact frame the
      // agent starts who then trails off inside the following ~250ms never re-saturates, so nothing
      // closes their turn and the exemption survives that answer. Strictly NARROWER than the
      // pre-fix behaviour, which stranded it on every turn; closing it means reworking how the rise
      // branch cancels a pending falling edge on echo, which is its own change with its own tests.
      const agentSpeaking = isAgentSpeakingRef.current;
      if (agentSpeaking && !wasAgentSpeaking) {
        const owedFallingEdge = isSpeechActive && noiseEnergy < 100;
        isSpeechActive = false;
        noiseEnergy = 0;
        if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
        // Loop state first, callback last: `onSpeechEnd` re-renders App, and the edge state must
        // already be consistent before anything downstream can read back into this loop.
        if (owedFallingEdge) callbacks.current.onSpeechEnd();
      }
      wasAgentSpeaking = agentSpeaking;

      // 🌟 RMS THRESHOLDS (Tuned lower because RMS < FFT peaks)
      const noiseThreshold = agentSpeaking ? speakingThreshold : listeningThreshold;

      // 🔇 Is the room actually the guest, or the agent's own voice coming back in through the
      // mic? See state/echoGate.js — this is the fix for the welcome cutting itself off.
      const roomIsNotEcho = roomBeatsEcho({
        micVol, agentEcho, agentSpeaking, ratio: echoRejectRatio,
      });

      if (micActive) {
        // ENERGY CALCULATION
        if (micVol > noiseThreshold && roomIsNotEcho) {
          noiseEnergy += isAgentSpeakingRef.current ? 7 : 15;

          // 🔄 Any renewed energy means the user hasn't actually stopped talking. A brief
          // dip below threshold (a breath, a soft consonant, a word gap) can drain noiseEnergy
          // to 0 and arm the falling-edge timer below mid-utterance; if speech resumes before
          // that timer fires, it must be cancelled here. The rising-edge block further down
          // only clears it when `!isSpeechActive`, i.e. once per utterance — it stays true for
          // the whole time the user is talking, so without this it never runs again and the
          // stale timer fires "thinking" ~700ms later regardless of the resumed speech.
          if (silenceTimer) {
            clearTimeout(silenceTimer);
            silenceTimer = null;
          }
        } else {
          noiseEnergy -= 4;
        }
        noiseEnergy = Math.max(0, Math.min(100, noiseEnergy));

        // 🎯 RISING EDGE TRIGGER (Fires EXACTLY once when saturated)
        if (noiseEnergy >= 100 && !isSpeechActive) {
          if (isAgentSpeakingRef.current && isKeyboardOpen) {
            // 🛑 Keyboard mode does not barge in by voice. With the field up the guest is typing,
            // often on a laptop with speakers, and the agent's own output loops back into the mic —
            // letting that saturate into an interrupt is the "silent turn amplifier" the frame gate
            // at handleMicFrame exists to stop. Interrupting a speaking agent from keyboard mode is
            // the STOP button's job (useManualStop), which is deterministic and cannot be triggered
            // by a room.
            //
            // Suppressed HERE rather than by switching `micActive` off, and that matters: the loop
            // stays live, so an utterance that began BEFORE the agent started answering still drains
            // its energy and fires its own falling edge. Killing the loop instead would freeze
            // `isSpeechActive`/`noiseEnergy` mid-utterance and strand `userHasSpokenRef` true, which
            // is exactly the flag that lets mic frames through during agent speech. `isSpeechActive`
            // is deliberately NOT latched: nothing started, so nothing may end.
            noiseEnergy = 0;
          } else {
            isSpeechActive = true;

            if (isAgentSpeakingRef.current) {
              // The levels that won, so a barge-in that should not have happened can be read off
              // the console instead of guessed at. See the echo gate above.
              callbacks.current.onInterrupt({ micVol, agentEcho, ratio: echoRejectRatio });
            } else {
              callbacks.current.onSpeechStart();
            }
          }
        }

        // 🪂 FALLING EDGE TRIGGER (Fires EXACTLY once when drained)
        if (noiseEnergy === 0 && isSpeechActive && !silenceTimer) {
          silenceTimer = setTimeout(() => {
            isSpeechActive = false;
            callbacks.current.onSpeechEnd();
            silenceTimer = null;
          }, 700);
        }
      } else if (isSpeechActive || noiseEnergy > 0 || silenceTimer) {
        // 🎙️ The mic went away mid-utterance — muted, the intro film took over, or (on tenants
        // without `micLiveWithKeyboard`) the keyboard opened. Both edge triggers live inside the
        // branch above, so from here the energy can never drain and the falling edge can never
        // fire. Now that the loop survives re-renders, a latch left true here would survive with
        // it: `isSpeechActive` would stay true for the rest of the session and the guest's NEXT
        // utterance would produce no rising edge at all — a permanently deaf client. Reset it.
        //
        // `onSpeechEnd` is deliberately NOT invoked: it sends `speech_end` and pushes the agent to
        // 'thinking', and a guest who merely muted or opened the keyboard has not finished a turn.
        // The visible half (`isUserSpeaking`) is cleared by the dedicated effect further down —
        // which watches `isMicOn` and `isKeyboardOpen`, i.e. the two inputs a GUEST can change, but
        // not `isIntroPlayingRef` (a ref, unobservable from an effect) and not the analysers. The
        // intro is the gap, and it is covered elsewhere: the film owns the screen and the mic is
        // dropped when it ends, so there is no route to a stranded flag through it.
        isSpeechActive = false;
        noiseEnergy = 0;
        if (silenceTimer) { clearTimeout(silenceTimer); silenceTimer = null; }
      }

      animationFrameId = requestAnimationFrame(updateVolume);
    };

    // Kick off the single, stable RAF loop
    updateVolume();

    return () => {
      cancelAnimationFrame(animationFrameId);
      if (silenceTimer) clearTimeout(silenceTimer);
    };
    // Hook only rebuilds if the actual hardware analysers change. Every one of these is a `useRef`
    // container, so in practice the loop is built once per mount and torn down once — which is the
    // point: its edge state (`isSpeechActive`, `noiseEnergy`, `silenceTimer`) is per-utterance, not
    // per-render, and must outlive rendering. The callbacks and the tunables are read out of refs
    // above precisely so they can stay out of this list; adding either back re-introduces the
    // lost-falling-edge bug documented at `liveState`.
  }, [micAnalyser, agentAnalyser, isAgentSpeakingRef, currentVolumeRef]);
};

// Mirrors Python ShoppingService._garment_slot — one piece per slot; used to keep non-searched
// pieces visible when outfitSearchOverride replaces only the browsed slot in the right panel.
function garmentSlot(item) {
  const t = (item?.title || '').toLowerCase();
  if (t.includes('suit')) return 'suit';
  if (t.includes('trouser') || t.includes('trousers')) return 'bottom';
  if (t.includes('tie')) return 'tie';
  if (t.includes('jacket') || t.includes('blazer')) return 'jacket';
  if (['shirt', 'knitwear', 'polo', 'jumper', 'sweater', 'knit', 'roll neck'].some(w => t.includes(w))) return 'top';
  return 'other:' + t;
}

const App = () => {


  const [isExperienceStarted, setIsExperienceStarted] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [view, setView] = useState('GRID');
  // viewRef mirrors view for use inside long-lived closures (WS onmessage) where view is stale.
  const viewRef = useRef('GRID'); // eslint-disable-line react-hooks/rules-of-hooks
  useEffect(() => { viewRef.current = view; }, [view]); // eslint-disable-line react-hooks/exhaustive-deps
  const [sceneExiting, setSceneExiting] = useState(false);
  const [showReconnectBanner, setShowReconnectBanner] = useState(false);
  const [overlay, setOverlay] = useState(null);
  // The agent's `scroll_panel` tool (backend) drives the already-open cart/wishlist concertina
  // panel — a nonce rather than a boolean/enum so the same {panel, direction} command fired twice
  // in a row (e.g. "scroll down" said twice) still re-triggers the effect that reads it.
  const [scrollCommand, setScrollCommand] = useState(null);
  const scrollCommandNonceRef = useRef(0);
  // 🔐 GDPR consent (VER-40): the mic never starts until the guest explicitly opts in.
  const { consent, needsDecision, hasAudioConsent, saveConsent } = useConsent();
  const [consentGateOpen, setConsentGateOpen] = useState(false);
  const [privacyOpen, setPrivacyOpen] = useState(false);
  // Non-reel tenants' PRIVACY drawer body (generic-theme ConsentCategoryList — see the overlay
  // block below). Re-seeded from the stored record each time the drawer opens, same contract the
  // old PreferencesCenter had.
  const [privacySelected, setPrivacySelected] = useState(() => ({ ...DEFAULT_CATEGORIES, ...(consent?.categories || {}) }));
  useEffect(() => {
    if (overlay === 'PRIVACY') setPrivacySelected({ ...DEFAULT_CATEGORIES, ...(consent?.categories || {}) });
  }, [overlay, consent]);
  // Imperative mirror so startMicrophone can hard-gate getUserMedia synchronously,
  // independent of React state/effect timing (the entry paths set it before capturing).
  const hasAudioConsentRef = useRef(hasAudioConsent);
  useEffect(() => { hasAudioConsentRef.current = hasAudioConsent; }, [hasAudioConsent]);
  // 🎬 Cinematic brand/collection storytelling overlay (BRAND_DISCOVERY / COLLECTION_SHOWCASE)
  // and a subtle agent advisory (CLARIFY / SUBSTITUTE) — backend signals previously dropped.
  const [discovery, setDiscovery] = useState(null);
  const [advisory, setAdvisory] = useState(null);

  // --- SECURE AUTH STATE ---
  // Whether a guest must sign in is the BACKEND's answer (`require_auth` from /api/config), never
  // the hostname. A host-based rule used to live here and treated every *.staging.versai.world as
  // an open demo; it did not actually skip the gate (that has always followed /api/config), but it
  // did strip the minted token off the agent WebSocket, so a gated staging tenant let a guest
  // through the login screen and then closed the socket with 1008.
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  // SPEC-26 Phase 4: the Ophion boot beat (Figma 45:1314), shown between the config/auth handshake
  // resolving and the Enter Boutique gate. Reel-mode tenants only — see the render gate below.
  // Deliberately time-based and NOT gated on preloadReelAssets(): the manifest and intro film it
  // warms aren't needed until *after* the guest clicks Enter Boutique, so blocking the gate on them
  // would delay interactivity for zero benefit while they're still reading the welcome copy.
  const [showBootLoader, setShowBootLoader] = useState(true);
  const [authEmail, setAuthEmail] = useState('');
  const [authOtp, setAuthOtp] = useState('');
  const [authStep, setAuthStep] = useState(1);
  const [errorMessage, setErrorMessage] = useState('');
  const [isResending, setIsResending] = useState(false);
  const API_URL = WS_URL.replace('wss://', 'https://').replace('ws://', 'http://').split('/ws/agent')[0];

  const [products, setProducts] = useState([]);
  const [brandName, setBrandName] = useState('');
  // 🏷️ Full tenant handshake config from /api/config (brand-agnostic: agent_name, currency,
  // theme, assets_base, intro_video, feature_flags). Lets the storefront render any brand
  // without hardcoded constants.
  const [brandConfig, setBrandConfig] = useState({});
  // Browser tab title/icon come from the /api/config handshake (tab_title/favicon_path,
  // driven by the backend's TENANT_PROFILE) so branding is single-sourced in the backend
  // instead of duplicated as a frontend build-time env var.
  useEffect(() => {
    if (brandConfig.tab_title) {
      document.title = brandConfig.tab_title;
    }
    if (brandConfig.favicon_path) {
      let link = document.querySelector("link[rel~='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        link.type = 'image/png';
        link.sizes = 'any';
        document.head.appendChild(link);
      }
      link.href = `${process.env.PUBLIC_URL}/${brandConfig.favicon_path}`;
    }
  }, [brandConfig.tab_title, brandConfig.favicon_path]);
  // Background: Huntsman keeps its heritage texture; a brand whose profile names a
  // `background_image` (tenant_profiles.py → /api/config) gets that texture over its theme
  // colour; every other brand uses its theme colour alone. Falls back to a neutral dark until
  // the handshake resolves.
  const backgroundStyle = React.useMemo(() => {
    const theme = brandConfig.theme || {};
    const isHuntsman = (brandConfig.brand_name || '').toLowerCase().includes('huntsman');
    if (isHuntsman) {
      return {
        backgroundImage: "url('/huntsmanimage.png')",
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.6)',
        backgroundBlendMode: 'screen',
      };
    }
    if (brandConfig.background_image) {
      return {
        backgroundImage: `url('${brandConfig.background_image}')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundColor: theme.bg || '#0d0d0d',
      };
    }
    return {
      backgroundColor: theme.bg || '#0d0d0d',
      backgroundSize: 'cover',
      backgroundPosition: 'center',
    };
  }, [brandConfig]);
  const agentName = brandConfig.agent_name || 'Yris';
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null); // chosen size for size-based products
  // 🧠 The agent's live read of the guest (DIM intent + mood), shown as an ambient chip.
  const [agentRead, setAgentRead] = useState({ intent: null, mood: null });
  // 🧠 Live Dynamic Intent Model read ("show the brain"): intent/mood/trajectory/confidence,
  // emitted by the backend as a dim_read sidecar. This is backend truth, not an LLM self-report.
  const [dimRead, setDimRead] = useState(null);
  // 👔 Agent-assembled outfit ("Complete the Look") from suggest_pairings — { anchor, pieces, occasion }.
  const [outfit, setOutfit] = useState(null);
  // 👔 When a GRID search arrives while in OUTFIT view, store results here and stay in OUTFIT layout
  // (anchor pinned left, search results on right). Cleared on fresh OUTFIT signal or any transition away.
  const [outfitSearchOverride, setOutfitSearchOverride] = useState(null);
  // 📸 SPEC-5/6 outfit builder: generated "styled look" composite + the analysing state during upload.
  const [styledLook, setStyledLook] = useState(null); // { image, pieces[], kind, occasion }
  const [isAnalysing, setIsAnalysing] = useState(false);
  // 🎬 SPEC-7 Veo try-on: generated motion video + the rendering state (slow, ~3-4 min).
  const [styledVideo, setStyledVideo] = useState(null); // { video, kind, occasion }
  const [isAnimating, setIsAnimating] = useState(false);
  const [uploadHint, setUploadHint] = useState(false);          // agent invited a photo → highlight button
  const imageUploadRef = useRef(null);
  // The DIM widget only makes sense once the guest has actually spoken — gates out the welcome-state
  // default read ("Just Browsing"/"Neutral") that otherwise shows before anyone says anything.
  const [hasUserSpoken, setHasUserSpoken] = useState(false);
  // 🧠 Full DIM semantic_intent (clusters / comparison axes / continuity) for the "why these" panel.
  const [semanticIntent, setSemanticIntent] = useState(null);
  const [showWhy, setShowWhy] = useState(false);
  const [pinnedItems, setPinnedItems] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  // 💾 DEMO PERSISTENCE (device spoof): a KNOWN client's wishlist persists across launches via
  // SPEC-14: wishlist storage key — identified guests use localStorage keyed by ?customer= (survives
  // new tabs); anonymous guests use a per-session key backed by the stable sid (SPEC-39 OQ3: now
  // localStorage too, so it survives closing the tab and matches the 30-day backend snapshot).
  // Backend selection_state hydration is the authoritative source on reconnect; localStorage is a
  // fast-paint cache that pre-populates before the first WebSocket message arrives.
  const wishlistStorageKey = (() => {
    const customer = new URLSearchParams(window.location.search).get('customer');
    if (customer) return `versai_wishlist_${customer.trim().toLowerCase()}`;
    const anonSid = readAnonSid();
    return anonSid ? `versai_wishlist_anon_${anonSid}` : '';
  })();
  const [wishlistItems, setWishlistItems] = useState(() => {
    if (!wishlistStorageKey) return [];
    try {
      const saved = localStorage.getItem(wishlistStorageKey);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  // Persist wishlist on every change — works for both identified and anonymous sessions.
  useEffect(() => {
    if (!wishlistStorageKey) return;
    try { localStorage.setItem(wishlistStorageKey, JSON.stringify(wishlistItems)); } catch { }
  }, [wishlistItems, wishlistStorageKey]);

  // SPEC-35: ref mirrors for the WS-open selection offer (G2) — onopen closes over refs,
  // never state, so the offer always reads the latest paint without re-binding the socket.
  const wishlistItemsRef = useRef(wishlistItems);
  useEffect(() => { wishlistItemsRef.current = wishlistItems; }, [wishlistItems]);
  const cartItemsRef = useRef(cartItems);
  useEffect(() => { cartItemsRef.current = cartItems; }, [cartItems]);

  // Audio & State
  const [isMicOn, setIsMicOn] = useState(true);
  // Voice consent given but the BROWSER refused the mic (prompt dismissed/blocked, no device): consent alone never saw this, so the
  // guest sat in a silent boutique with no way back. Allowing it from site settings restarts capture (setIsMicOn re-runs the effect).
  const micBlocked = useMicBlocked({ onGranted: () => setIsMicOn(true) });
  const [isAgentSpeaking, setIsAgentSpeaking] = useState(false);
  // Reactive mirror of userHasSpokenRef so the voice visualizer can react to the
  // user talking (the ref alone never re-renders). Drives the 'speaking' shape —
  // which the art team confirmed is the right animation for the guest's voice as
  // well as the agent's. See voiceVisualizerState.js for the precedence.
  const [isUserSpeaking, setIsUserSpeaking] = useState(false);
  const [agentState, setAgentState] = useState('listening');
  // Cart/wishlist "reaction" animation: takes priority over speaking/listening for a
  // fixed duration when a tool call lands, then falls back to whatever state applies
  // at that moment (still speaking -> speaking, otherwise listening/agentState).
  const [reactionState, setReactionState] = useState(null);
  const reactionTimeoutRef = useRef(null);
  const triggerReaction = (kind) => {
    if (reactionTimeoutRef.current) clearTimeout(reactionTimeoutRef.current);
    setReactionState(kind);
    reactionTimeoutRef.current = setTimeout(() => setReactionState(null), 1500);
  };

  // Subtitles off by default. Sourced from the external transcriptStore (SPEC-26 D4) rather than
  // local state — same shape/behaviour as before, but now shareable with the reel split-CC pair.
  const activeSubtitle = useLatestTranscript();
  const [ccEnabled, setCcEnabled] = useState(false);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [textInput, setTextInput] = useState('');

  // 🛠️ DEV CONTROLS STATE
  const [vadSpeakingThreshold, setVadSpeakingThreshold] = useState(0.12); // 0.13 needed shouting, 0.11 let the agent's own echo interrupt it and empty turns (stalls): 0.12 is the A/B middle
  const [vadListeningThreshold, setVadListeningThreshold] = useState(0.08);
  // 🔇 See the echo gate in useVoiceActivityDetection. 1 = the room must be at least as loud
  // as the agent's own output before it can interrupt it.
  const [vadEchoRejectRatio, setVadEchoRejectRatio] = useState(ECHO_REJECT_RATIO_DEFAULT);

  // track state for the visual cues for onboarding
  const [hasInteracted, setHasInteracted] = useState(false);
  const [hasWelcomed, setHasWelcomed] = useState(false);
  // Drives the welcome-screen copy ("Welcome back"). Seeded from the intro marker so a guest who
  // has been here before is greeted as such even with an empty cart, and additionally flipped on
  // by selection_state hydration below when the backend reports saved items.
  const [isReturningGuest, setIsReturningGuest] = useState(hasSeenIntro);
  const [suggestionIndex, setSuggestionIndex] = useState(0);

  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  // ✨ MAGIC LENS STATE
  const [showLens, setShowLens] = useState(false);
  const [lensPos, setLensPos] = useState({ x: 0, y: 0, bgX: 0, bgY: 0 });
  const imgContainerRef = useRef(null);

  // 🔦 HIGHLIGHT STATE
  const [highlightedFeatures, setHighlightedFeatures] = useState([]);
  const [liveAgentText, setLiveAgentText] = useState('');
  const highlightedThisTurn = useRef(new Set());

  const ws = useRef(null);
  // SPEC-52: flag-gated waitlist capture — absent from `/api/config` for any tenant with the flag
  // off, so `collection`/`modalCopy` are empty/null there and the hook's own initial state (closed)
  // is all that build ever renders.
  const waitlist = useWaitlist({
    // VER-89: report whether the frame actually left. This used to be a silent no-op on a
    // closed socket while the panel had already moved to `submitting`, stranding the guest
    // on "Sending…" with no timeout and no way back.
    sendMessage: (msg) => {
      if (ws.current?.readyState !== WebSocket.OPEN) return false;
      ws.current.send(JSON.stringify(msg));
      return true;
    },
    collection: brandConfig.waitlist?.collection || '',
    modalCopy: brandConfig.waitlist?.modal || null,
  });
  // SPEC-46: the checkout CTA's own state (offer or not). Same shape as `waitlist` above — one
  // hook call, App.js only holds the socket and the `ui_update` dispatch, the hook owns the rest.
  const checkout = useCheckout({
    sendMessage: (msg) => {
      if (ws.current?.readyState !== WebSocket.OPEN) return false;
      ws.current.send(JSON.stringify(msg));
      return true;
    },
    // SPEC-46 Rev 7 — the payment panel mounts in-page, so the browser needs Stripe's
    // PUBLISHABLE key. The backend sends it only when its own three-way checkout gate is up.
    // An absent key here does NOT hide the CTA (JC AC-30, D-23): the button always renders,
    // and useCheckout makes `requestCheckout` a silent no-op when there is no key — the gap is
    // invisible to the guest rather than a rendered error or a disappearing button.
    publishableKey: brandConfig.stripe_publishable_key || '',
  });
  // Tells the backend where an open cart/wishlist panel is scrolled to, so "scroll down" on a list
  // already at its end answers "you're at the bottom" instead of no-op'ing (client, 2026-09-01).
  // All of the behaviour — the `panel_scroll` wire shape, the OPEN guard and the per-panel dedupe
  // that keeps a drag from emitting one message per frame — lives in the hook; App.js only holds
  // the socket and hands the returned callback to whichever chrome is mounted.
  const reportPanelScroll = usePanelScrollReporter(ws);
  const sessionIdRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const audioContext = useRef(null);
  const processorRef = useRef(null);
  const micContextRef = useRef(null);   // 🎙️ dedicated mic AudioContext (16kHz) — closed on stop to avoid leaks
  const micSessionRef = useRef(0);       // 🎙️ generation token guarding against fast mic on/off races
  const streamRef = useRef(null);
  const micTrackDetachRef = useRef(null); // 🎙️ detach fn for the track-health listeners (state/micTrackHealth.js)
  const micRestartBudgetRef = useRef(createRestartBudget()); // 🎙️ cap on health-triggered capture restarts
  const micRestartPendingRef = useRef(null); // 🎙️ a health restart deferred until the intro film ends
  const filmTailGateRef = useRef(createFilmTailGate()); // 🎬 swallows the tail of a turn spoken UNDER the film
  const nextStartTime = useRef(0);
  const activeAudioSourcesRef = useRef([]);
  const isAgentSpeakingRef = useRef(false);
  const speakingTimeoutRef = useRef(null);
  // 🕰️ The same playout queue as `nextStartTime`, kept in WALL clock so it cannot be frozen by a
  // suspended AudioContext. Watchdogs `isAgentSpeakingRef` in `sendInteraction` — see
  // state/agentPlayoutDeadline.js for the two ways that flag latches and silences every tap.
  const speechEndsAtRef = useRef(0);
  const currentVolume = useRef(0);
  const agentAnalyser = useRef(null);
  const micAnalyser = useRef(null);
  const userHasSpokenRef = useRef(false);
  const hasAgentSpokenOnceRef = useRef(false);
  const silenceTimerRef = useRef(null);
  const lastProcessedSubtitleRef = useRef(null);

  const isGalleryLockedRef = useRef(false);
  const productImagesRef = useRef([]);
  const lastAutoSwapRef = useRef(0); // 📸 timestamp of the last auto image swap (min 3s between swaps)

  const [throttledAgentText, setThrottledAgentText] = useState('');
  const throttleTimerRef = useRef(null);

  const [activeLoreImage, setActiveLoreImage] = useState(null);

  // 🎬 Brand intro film (plays on entry) + agent-triggered movement video overlay
  const [showIntro, setShowIntro] = useState(false);
  // 🎬 Gate the agent WS connect until the intro is actually playing, so the welcome search/audio
  // doesn't compete with the video's first-frame decode (removes the first-second jank).
  const [agentConnectArmed, setAgentConnectArmed] = useState(false);
  const agentArmTimerRef = useRef(null);
  // SPEC-11: bumped on every reconnect attempt so the WS-connect effect below re-fires even
  // though agentConnectArmed itself never needs to change. A false->true toggle of the same
  // boolean was tried first but is unreliable: two setState calls that net out to the SAME
  // value within one batch coalesce into a no-op in React (no re-render, no effect re-run) —
  // a monotonically increasing value can't collide with itself this way.
  const [reconnectNonce, setReconnectNonce] = useState(0);
  const [videoOverlay, setVideoOverlay] = useState(null); // { url, title, kind }
  const [isVideoPaused, setIsVideoPaused] = useState(false);
  const movementVideoRef = useRef(null);
  const introVideoRef = useRef(null); // 🎬 intro film plays UNMUTED (its own audio); others stay muted
  const showIntroRef = useRef(false); // 🎬 live mirror of showIntro for mic/VAD gating (avoids stale closures)
  const introFinishedSentRef = useRef(false);
  // 🎬 This browser has already sat through the opening film once. Read ONCE, at mount and via a
  // lazy initializer — before markIntroSeen() can possibly run — so the value is stable for the
  // whole session and every consumer agrees on it: entry gating below, the Projectionist's boot
  // chapter, and the `returning` flag the backend words its welcome from.
  // Deliberately NOT the same thing as `isReturningGuest` above, which also turns true on saved
  // items arriving mid-session. The film decision must be a fixed fact known before connect.
  // SPEC-43 R1: the hook keeps that read-once contract and, once the guest enters, re-stamps the
  // record so the 7-day returning-guest window counts from the last VISIT (state/useVisitMemory.js).
  const { hasSeenIntroBefore } = useVisitMemory({ entered: isExperienceStarted });
  // SPEC-76: the read-first tutorial copy from /api/config (absent = the tenant has none = nothing renders).
  const tutorialCopy = useMemo(() => readTutorialCopy(brandConfig.first_visit_tutorial), [brandConfig.first_visit_tutorial]);
  // SPEC-82 D-1: the dev-only UI experiment toggles ({} on every tenant whose /api/config has no `ui_experiment`).
  const ux = useMemo(() => readUiExperiment(brandConfig.ui_experiment), [brandConfig.ui_experiment]);
  const gateHint = useGateHint({ ux, hasPrimer: !!tutorialCopy }); // SPEC-82: voice_hint / primer_collapsible (flag-off: today's gate)
  // SPEC-82 §4.2: skip-the-film + tap-to-talk, composed. Wrappers resolve the later-declared funnel at call time.
  const introX = useIntroExperiment({
    ux, reelMode: !!brandConfig.feature_flags?.reel_mode, hasSeenIntroBefore, showIntro, showIntroRef,
    sendIntroFinished: () => sendIntroFinished(),
    isSocketOpen: () => ws.current?.readyState === WebSocket.OPEN,
    started: isExperienceStarted, enter: () => requestEnter(), // no_enter_gate (dev): the landing's tap is the entry
    setShowIntro, // first_visit_film: the film goes up before the landing
  });
  // no_enter_gate: the boutique renders before the entry tap (audio, mic and agent still wait for it), so the render gates below
  // read this instead of isExperienceStarted. Flag-off it IS isExperienceStarted.
  const inBoutique = isExperienceStarted || introX.landing.engaged;
  const landingGateOpen = introX.landing.engaged && consentGateOpen;
  // no_enter_gate (owner 2026-10-08): a returning guest with consent on file has the agent connected silently at the landing - the
  // welcome is held (intro_playing=1, the funnel defers it) until their tap, as staging holds it behind the CTA. Never before a
  // first consent decision. Arming raises the hold flag synchronously: the connect effect builds the URL from it (trap T-1).
  const connectEarly = useEarlyConnect({
    enabled: introX.landing.engaged && introX.landing.returning && !needsDecision && isAuthenticated,
    // returning=1 too: doEnter sets it at entry - too late for this URL, and without it the backend's reel branch injects its
    // "film is playing, stay silent" instruction, which suppresses the welcome-back (backend main.py, measured 2026-08-25).
    onArm: () => { showIntroRef.current = true; introSkippedForReturningRef.current = true; setAgentConnectArmed(true); },
  });
  const agentWanted = isExperienceStarted || connectEarly;
  const earlyHeld = connectEarly && !isExperienceStarted;
  // SPEC-82 Rev 3 (bug 2): a pre-tap request that makes the agent speak releases like the tap; it is sent after the welcome.
  const implicitRelease = useImplicitRelease({
    introX, agentSpeaking: isAgentSpeaking, isSocketOpen: () => ws.current?.readyState === WebSocket.OPEN,
  });
  // Whether the film was ACTUALLY skipped this entry (returning guest AND a reel tenant), as
  // opposed to the raw browser fact above. This is what the backend is told, so the welcome copy
  // only says "welcome back" when the guest genuinely did not just watch the opening.
  const introSkippedForReturningRef = useRef(false);
  // Set when an intro-finished trigger fired against a closed socket, so onopen can deliver it
  // instead of the welcome being lost. See sendIntroFinished.
  const introFinishedPendingRef = useRef(false);
  const productsRef = useRef([]); // live mirror of products for onopen stale-closure avoidance
  // SPEC-8: 3D boutique scene ref + pre-mount command buffer
  const sceneRef = useRef(null);
  const pendingSceneCommandsRef = useRef([]);
  // Reel pilot: Projectionist component ref
  const reelRef = useRef(null);
  // SPEC-53: which stage the reel screen is in — the film, a static collection's carousel, or one
  // of its piece cards. Driven by `stage_command`; the fade swaps the stage while the black is
  // opaque and replays the reel commands that arrived during a return (review R5).
  const stageFade = useStageTransition();
  const stageMachine = useStageMachine({
    onReelCommand: (msg) => reelRef.current?.handleReelCommand(msg),
    fade: stageFade,
    // SPEC-59 D-10: the ONE film predicate the gate reads. The state, never `showIntroRef` — the
    // ref trails it by a commit on the reel path (`sendReelIntroFinished` writes only the state).
    filmActive: showIntro,
  });
  const reelOffStage = stageMachine.state.stage !== Stage.REEL;
  // SPEC-26 D5/Phase 2: the reel_product envelope + D3 tag-eligibility map, dispatched once per
  // connection at the intro_finished seam. { product, tagEligibility, tagRegions } | null until
  // it arrives.
  const [reelProduct, setReelProduct] = useState(null);
  // no_enter_gate: a returning guest's carousel shows the last visit's rail until this connection's envelope lands (flag-off: live).
  const reelShown = useRememberedReel({ enabled: introX.landing.engaged, live: reelProduct });
  // SPEC-27 D18: what the agent is currently talking ABOUT, when it is not what is on screen.
  const [reelSubject, setReelSubject] = useState(null);
  // SPEC-34 D7: the PDP spec sheet for the showcased variant. State drives the overlay;
  // the ref mirrors it for sendReelState's variant enrichment (§3.3) without re-binding
  // the beat-reporter callback. V1: does not survive refresh (E16 — upgraded by SPEC-35).
  const [reelSpecSheet, setReelSpecSheet] = useState(null);
  const reelSpecSheetRef = useRef(null);
  // 🎬 movement-overlay auto-close bookkeeping
  const videoNarrationStartedRef = useRef(false); // agent has spoken at least once since overlay opened
  const videoManuallyHeldRef = useRef(false);     // user paused -> keep the overlay open (no auto-close)
  const videoCloseTimerRef = useRef(null);

  // SPEC-57: the manual reel-view toggle. `sendInteraction` is declared further down this
  // function, so it is wrapped rather than passed directly — the wrapper only resolves it at call
  // time, after the whole component body (and so the real `sendInteraction`) has run once.
  const viewToggle = useReelViewToggle({
    enabled: Boolean(brandConfig.feature_flags?.reel_view_toggle),
    stage: stageMachine.state.stage,
    sendInteraction: (...args) => sendInteraction(...args),
    collections: brandConfig.static_collections,
    reelRailTitle: reelProduct?.rail?.title,
  });

  // SPEC-67 D-9: a same-page reconnect waits for the backend's `session_resume` before `onopen`
  // resets the screen. The lifecycle lives in the hook; the senders are wrapped for the same reason
  // as `viewToggle` above — they are declared further down this function body.
  const sessionResume = useSessionResume({
    isExperienceStarted,
    reopen: () => ws.current?.onopen?.({ type: 'open' }),
    deps: {
      stageMachine,
      productsRef,
      showIntroRef,
      introFinishedPendingRef,
      introFinishedSentRef,
      sendInteraction: (...args) => sendInteraction(...args),
      sendGridContext: (...args) => sendGridContext(...args),
      sendIntroFinished: () => sendIntroFinished(),
    },
  });

  // The one place the `intro_finished` frame is built. `returning` tells the backend to word the
  // welcome as "welcome back" instead of a first-time introduction to the house. Deduped by
  // introFinishedSentRef here and again by `intro_prompt_sent` on the backend.
  const sendIntroFinished = () => {
    if (introFinishedSentRef.current) return;
    if (introX.isHolding()) return; // SPEC-82 D-6: tap-to-talk holds the welcome until the guest taps
    if (introX.landing.isWaiting()) return; // no_enter_gate: held until the entry tap; useLanding releases it at entry
    if (ws.current?.readyState !== WebSocket.OPEN) {
      // The socket isn't up yet (or is mid-reconnect). Remember that the intro is over so onopen
      // can deliver it. A returning guest has TWO fire-and-forget triggers — the 500ms post-connect
      // flush and the collection chapter ending — and no film to hold the experience open while
      // they land, so both can no-op against a closed socket and the guest gets no welcome at all.
      introFinishedPendingRef.current = true;
      return;
    }
    introFinishedSentRef.current = true;
    introFinishedPendingRef.current = false;
    ws.current.send(JSON.stringify({
      type: 'intro_finished',
      returning: introSkippedForReturningRef.current,
      ...introX.frameExtras(), // SPEC-82 §4.2: `film_played:false` under the experiment only ({} otherwise)
    }));
    introX.afterIntroFinishedSent(); // SPEC-82 D-7
  };

  // 🎬 Intro finished naturally (or skipped): reveal the grid and ask the backend to place the
  // default selection NOW (deferred until the film ends so the search/render doesn't jank the video).
  // Barge-in does NOT call this — if the guest speaks they are driving, and their request fills the grid.
  const finishIntro = (notifyBackend) => {
    setShowIntro(false);
    // The ref normally trails by a render (the mirror effect below). Set it here so everything that
    // keys on "is the film up" — the agent-audio guard, the mic-frame gate, the VAD, and the
    // deferred restart immediately below — agrees the moment the film is over, in this same tick.
    showIntroRef.current = false;
    // 🎙️ A capture restart the track-health monitor asked for during the film (see
    // restartMicrophone). Now is the first moment it is free: the film's audio is done, so the
    // Windows ducking blip it causes costs nothing, and the guest is about to need the mic.
    if (micRestartPendingRef.current) {
      const reason = micRestartPendingRef.current;
      micRestartPendingRef.current = null;
      restartMicrophone(reason);
    }
    // The guest has now seen the opening in full (or chosen to skip past it) — next visit goes
    // straight to the reel. Written here rather than at entry so a guest who closes the tab
    // mid-film still gets the film next time.
    introX.markSeen(); // SPEC-82 D-7 (is markIntroSeen() unless the tap gate holds)
    if (notifyBackend) sendIntroFinished();
  };
  // BUG FIX (2026-09-07): the grid's plain <video> only ended the intro on `ended`/`error`; a
  // stalled load left showIntroRef true for the whole session (VAD silent, every mic frame
  // dropped, no welcome). Duration + grace watchdog, grid film only — the reel has its own.
  const introWatchdogActive = showIntro && !brandConfig.feature_flags?.reel_mode;
  const onIntroWatchdogExpire = React.useCallback(() => finishIntro(true), []); // eslint-disable-line react-hooks/exhaustive-deps
  useIntroWatchdog({ videoRef: introVideoRef, active: introWatchdogActive, onExpire: onIntroWatchdogExpire });

  // Reel pilot: beat reporter → backend. Silent state sync (no model turn); the concierge
  // reads it back via get_current_context so narration can reference what is on screen.
  const sendReelState = (payload) => {
    viewToggle.observeReelState(payload); // SPEC-57 R2: selection follows story, tap or voice alike
    if (ws.current?.readyState === WebSocket.OPEN) {
      // SPEC-34 §3.3: on a variant chapter the playhead report names the piece being
      // shown. The catalogue id arrives with the spec-sheet payload — the frontend
      // holds no catalogue knowledge of its own; everywhere else `variant` stays
      // whatever the manifest said ('shared').
      const sheet = reelSpecSheetRef.current;
      const enriched = (sheet && payload?.story && payload.story === sheet.chapter_id)
        ? { ...payload, variant: sheet.variant_id }
        : payload;
      ws.current.send(JSON.stringify({ type: 'reel_state', ...enriched }));
    }
  };

  // Reel pilot: the intro film completed (or was skipped) — same dedupe as finishIntro so the
  // agent's first spoken welcome fires exactly once. Also clears showIntro so the VAD ref
  // (showIntroRef) is released — the Projectionist owns the intro in reel_mode so there is
  // no old <video> onEnded to do this.
  const sendReelIntroFinished = () => {
    setShowIntro(false);
    // Same marker as the legacy path. For a returning guest the Projectionist fires this at boot
    // (its silent collection chapter has no film to talk over) and the write is simply idempotent.
    introX.markSeen(); // SPEC-82 D-7
    sendIntroFinished();
  };

  const closeVideoOverlay = () => {
    clearTimeout(videoCloseTimerRef.current);
    videoNarrationStartedRef.current = false;
    videoManuallyHeldRef.current = false;
    setVideoOverlay(null);
    setIsVideoPaused(false);
    // 🎬 Tell the backend the film is gone so the turn watchdog resumes normal stalled-turn recovery.
    if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify({ type: 'video_state', active: false }));
  };

  // Drive play/pause/close on the <video> element from agent commands or button taps
  const applyVideoControl = (command) => {
    if (command === 'close') {
      closeVideoOverlay();
      return;
    }
    const el = movementVideoRef.current;
    if (command === 'pause') {
      // Pausing is an explicit "keep watching" — suppress the agent-finished auto-close.
      videoManuallyHeldRef.current = true;
      clearTimeout(videoCloseTimerRef.current);
      setIsVideoPaused(true);
      try { el?.pause(); } catch (e) { }
    } else if (command === 'play') {
      videoManuallyHeldRef.current = false;
      setIsVideoPaused(false);
      try { el?.play(); } catch (e) { }
    }
  };

  // 🎬 Auto-close the movement overlay when the agent FINISHES narrating (it may speak longer than
  // the clip, so we close on the agent going quiet — not on the video ending). Once the agent has
  // spoken since the overlay opened, a sustained silence closes it. This also makes "close the video"
  // work when the guest barges in (barge-in stops the agent → silence → overlay closes), while a
  // manual pause keeps it open. Closing by voice while the agent is idle still goes via the tool.
  useEffect(() => {
    if (!videoOverlay) return;
    if (isAgentSpeaking) {
      videoNarrationStartedRef.current = true;
      clearTimeout(videoCloseTimerRef.current);
      return;
    }
    if (videoNarrationStartedRef.current && !videoManuallyHeldRef.current) {
      clearTimeout(videoCloseTimerRef.current);
      videoCloseTimerRef.current = setTimeout(() => closeVideoOverlay(), 1800);
    }
    return () => clearTimeout(videoCloseTimerRef.current);
  }, [videoOverlay, isAgentSpeaking]);

  // Keep viewRef in sync so WebSocket closure always reads current view (avoids stale closure)
  useEffect(() => { viewRef.current = view; }, [view]);

  // SPEC-8: replay buffered scene_commands when the scene mounts (view switches to SCENE_3D)
  useEffect(() => {
    if (view === 'SCENE_3D' && sceneRef.current && pendingSceneCommandsRef.current.length > 0) {
      pendingSceneCommandsRef.current.forEach(cmd => sceneRef.current.handleSceneCommand(cmd));
      pendingSceneCommandsRef.current = [];
    }
  }, [view]);

  // DYNAMIC, BRAND-AGNOSTIC, IN-CONTEXT SUGGESTION ENGINE
  // Suggestions are derived from the live catalogue (the normalized envelope's attributes), the
  // current view, and the brand's feature flags — never hardcoded to a specific brand or product.
  const currentSuggestions = useMemo(() => {
    const flags = brandConfig.feature_flags || {};
    const brand = brandConfig.brand_name || 'the house';

    // Pull an attribute value off a normalized item by fuzzy key match (specifications is the flat
    // {label: value} legacy mirror of attributeGroups). Returns '' if absent.
    const spec = (item, ...needles) => {
      const specs = item?.specifications || {};
      for (const key of Object.keys(specs)) {
        const lk = key.toLowerCase();
        if (needles.some((n) => lk.includes(n))) {
          const v = specs[key];
          if (v) return String(v);
        }
      }
      return '';
    };
    const lower1 = (s) => (s ? s.charAt(0).toLowerCase() + s.slice(1) : s);
    const uniq = (arr) => [...new Set(arr.filter(Boolean))];

    let suggestions = [];

    // 1. Cart
    if (overlay === 'CART' && cartItems.length > 0) {
      suggestions = [
        "I'm ready to checkout",
        "Remove the first piece",
        "Close the cart for now",
        "Can we review what's in my cart?",
      ];
    }
    // 2. Wishlist
    else if (overlay === 'WISHLIST' && wishlistItems.length > 0) {
      suggestions = [
        "Move these to my cart",
        "Clear my saved items",
        "Close the wishlist",
        "Let's look at the first one again",
      ];
    }
    // 3. Product deep-dive — built from THIS piece's own attributes + the brand's features.
    else if (view === 'PRODUCT' && selectedProduct) {
      const colour = spec(selectedProduct, 'colour', 'color', 'colorway');
      const material = spec(selectedProduct, 'material', 'cloth', 'composition');
      const type = spec(selectedProduct, 'type', 'category') || 'piece';

      suggestions = [
        flags.image_angles ? "Can I see it from another angle?" : "Show me the next photo",
        colour ? `Do you have this in another colour?` : "Do you have this in another finish?",
        material ? `Tell me about the ${lower1(material)}` : "Tell me how it's made",
        "Add this to my cart",
      ];
      if (flags.compare !== false) suggestions.push("Keep this aside so I can compare it");
      if (flags.size_selector || (selectedProduct.variants && selectedProduct.variants.options?.length))
        suggestions.push("What sizes do you have?");
      suggestions.push(`What makes this ${lower1(type)} special?`);
    }
    // 4. Grid browsing — mine the actual results on screen for concrete, in-context prompts.
    else if (view === 'GRID' && products.length > 0) {
      const types = uniq(products.map((p) => spec(p, 'type', 'category')));
      const colours = uniq(products.map((p) => spec(p, 'colour', 'color', 'colorway')));
      const materials = uniq(products.map((p) => spec(p, 'material', 'cloth', 'composition')));
      const firstTitle = products[0]?.title || 'the first one';

      suggestions = [`Tell me more about the ${firstTitle}`];
      if (types[1]) suggestions.push(`Show me only the ${lower1(types[1])} pieces`);
      else if (types[0]) suggestions.push(`Show me more ${lower1(types[0])} pieces`);
      if (colours[0]) suggestions.push(`Do you have anything in ${lower1(colours[0])}?`);
      if (materials[0]) suggestions.push(`Something in ${lower1(materials[0])}, please`);
      suggestions.push("Something for a special occasion", "What's new this season?");
    }
    // 5. Welcome / discovery — agnostic, anchored on the brand name.
    else {
      suggestions = [
        `What is the story behind ${brand}?`,
        "What's new this season?",
        "I'm looking for a gift",
        "Show me a few of your finest pieces",
        "What would you recommend for me?",
      ];
    }

    // Shuffle so the chips feel fresh each time the view changes.
    return suggestions.filter(Boolean).sort(() => 0.5 - Math.random());
  }, [view, overlay, selectedProduct?.id, products, cartItems.length, wishlistItems.length, brandConfig]);

  //const currentSuggestions = getContextualSuggestions();

  // ✨ THE MASTER VESPER LORE SCANNER (V6 - With Conceptual Fallback)
  useEffect(() => {
    // Added isAgentSpeakingRef.current so late transcripts don't trigger ghost images!
    if (view === 'PRODUCT' && selectedProduct?.title.includes('Vesper') && liveAgentText && isAgentSpeakingRef.current) {
      const text = liveAgentText.toLowerCase();
      let targetImg = null;

      // 1. SPECIFIC TRIGGERS (High Priority)
      if (text.match(/\b(plan|concentric|geometry|rings|circles)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.plan_circles;
      }
      else if (text.match(/\b(starry|night sky|celestial|stars)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.starry_interior;
      }
      else if (text.match(/\b(opera|elevation|facade|columns|theatre)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.opera;
      }
      else if (text.match(/\b(newton|cenotaph|monument|sphere|spherical)\b/i)) {
        targetImg = text.includes('night') ? VESPER_LORE_ASSETS.sphere_night : VESPER_LORE_ASSETS.sphere_exterior;
      }
      else if (text.match(/\b(light|rays|beams|shadows|atmosphere|lighting)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.interior_rays;
      }
      else if (text.match(/\b(conical|cone|pyramid|geometric bodies)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.conical;
      }
      else if (text.match(/\b(macro|detail|close up|indexes|markers|3d)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.macro;
      }

      // 2. CONCEPTUAL CATCH-ALL (Fallback for broad questions)
      // This triggers the Collage (bu.jpg) when the user asks vague questions
      else if (text.match(/\b(design|ideas|philosophy|concept|background|story|inspiration|visionary|logic|history|style)\b/i)) {
        targetImg = VESPER_LORE_ASSETS.collage;
      }

      if (targetImg && targetImg !== activeLoreImage) {
        let dynamicDelay = 0;
        if (audioContext.current && nextStartTime.current) {
          const queueLength = nextStartTime.current - audioContext.current.currentTime;
          dynamicDelay = Math.max(0, queueLength * 1000);
        }

        setTimeout(() => {
          setActiveLoreImage(targetImg);
        }, dynamicDelay + 200);
      }
    }
  }, [liveAgentText, view, selectedProduct]);


  // 📸 PRODUCT DEEP DIVE AUTO-SCROLL — by default, gently cycle through the product's images on a
  // fixed 5-second timer so the guest sees front / side / back without asking. A manual or agent-tool
  // swap ("show me the side", switch_gallery_image / show_product_view) sets isGalleryLockedRef, which
  // pauses the cycle and hands control to the guest for the rest of the deep dive. The interval is
  // re-created whenever the product changes; the lock is reset when a fresh product opens (see the
  // PRODUCT branch of the ui_update handler).
  useEffect(() => {
    if (view !== 'PRODUCT' || !selectedProduct || !(selectedProduct.images?.length > 1)) return;
    const count = selectedProduct.images.length;
    const id = setInterval(() => {
      // Manual/tool override is active — leave the chosen image alone and stop auto-cycling.
      if (isGalleryLockedRef.current) return;
      setCurrentImageIndex(prev => (prev + 1) % count);
    }, 3000); // ⏱️ 5s between swaps (calm, gallery-like pacing)
    return () => clearInterval(id);
  }, [view, selectedProduct]);

  // 🌟 UNIFIED INITIALIZATION EFFECT (PASTE THIS)
  useEffect(() => {
    const initApp = async () => {
      let authRequired = true;

      // 1. Fetch Config First
      try {
        // Backstop only: a refused/5xx config fetch already falls through to the offline
        // fallback instantly, but a HUNG fetch used to mean a blank page forever. 12s is
        // generous for a cold pod behind nginx; past it, the reel boots offline as designed.
        const res = await fetch(`${API_URL}/api/config`, {
          signal: (typeof AbortSignal !== 'undefined' && AbortSignal.timeout)
            ? AbortSignal.timeout(12000)
            : undefined,
        });
        const data = await res.json();
        if (data.brand_name) setBrandName(data.brand_name);
        setBrandConfig(data || {});
        // Deploy-time reel sources (backend configmap/.env → /api/config → here): must apply
        // BEFORE preloadReelAssets() below so the warm fetches hit the same URLs the
        // Projectionist will load. No-ops when the backend doesn't send them.
        configureReelSources({
          specUrl: data.reel_spec_url,
          mediaBase: data.reel_media_base,
          introUrl: data.intro_video,
          // SPEC-34: the carousel segment map — empty/absent means no variant chapters
          // and the reel behaves exactly as before (E25).
          carouselSpecUrl: data.carousel_spec_url,
        });
        if (data.feature_flags?.reel_mode) {
          // Warm the Projectionist's JS chunk, its manifest JSON, and the intro film's
          // connection now — well before "Enter Boutique" is clicked — so mounting it
          // afterwards doesn't race a cold chunk fetch + fresh cross-origin handshake.
          // Same specifier as the React.lazy() call below, so it's the same chunk.
          import('./components/Projectionist').catch(() => {});
          preloadReelAssets();
        }
        if ((data.visualizer_mode || DEFAULT_VISUALIZER_MODE) === 'cavalry') {
          // Fetch + compile the 12.6 MB Cavalry WASM during gate idle time instead of the
          // post-Enter window, where it used to compete with the intro stream and the
          // next-chapter preload for bandwidth.
          const warm = () => warmCavalryModule();
          if ('requestIdleCallback' in window) requestIdleCallback(warm, { timeout: 4000 });
          else setTimeout(warm, 1500);
        }
        // The authoritative answer, cached for the failure path below.
        rememberAuthRequired(data.require_auth !== false);
        if (data.require_auth === false) {
          authRequired = false;
          // Drop any credential this origin is still holding from when it WAS gated. The token
          // is now forwarded whenever one exists, so leaving it here would keep a stale bearer
          // turning up in an open agent's handshake logs for as long as the browser kept it.
          localStorage.removeItem('versai_token');
          localStorage.removeItem('versai_token_timestamp');
          localStorage.removeItem('versai_token_expires_at');
          setIsAuthenticated(true);
        }
      } catch (e) {
        console.error("Failed to fetch brand config:", e);
        // The reel gate/intro must render with zero backend connectivity — see
        // OFFLINE_FALLBACK_BRAND_CONFIG above.
        setBrandName(OFFLINE_FALLBACK_BRAND_CONFIG.brand_name);
        setBrandConfig(OFFLINE_FALLBACK_BRAND_CONFIG);
        import('./components/Projectionist').catch(() => {});
        preloadReelAssets();
        // Config unreachable on localhost → skip auth gate (can't send OTP emails to localhost anyway)
        if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
          authRequired = false;
          setIsAuthenticated(true);
        } else if (recalledAuthRequired() === false) {
          // This origin has told us before that it does not gate. A fetch failure is not new
          // information about that, so honour what it said rather than inventing a gate the guest
          // cannot pass — see AUTH_REQUIRED_CACHE_KEY. A brand that DOES gate, and one we have
          // never successfully loaded, both keep the gated default: the fallback may only ever
          // repeat an answer the backend gave, never soften one it did not.
          authRequired = false;
          setIsAuthenticated(true);
        }
      }

      // 2. Check Token if auth is required (even on local dev environments)
      if (authRequired) {
        const token = localStorage.getItem('versai_token');
        const timestamp = localStorage.getItem('versai_token_timestamp');
        const expiresAt = localStorage.getItem('versai_token_expires_at');

        if (token && timestamp) {
          // Prefer the expiry the backend actually stated at verify time. The 8h fallback is for
          // tokens written before that was stored — without it, this deploy would sign out every
          // guest holding a live session.
          //
          // Every comparison against NaN is false, so parsing unreadable metadata and comparing
          // it naively reads as "not expired" and opens the gate. Unreadable means expired.
          const statedExpiry = Number(expiresAt);
          const issuedAt = Number(timestamp);
          const isExpired = expiresAt !== null
            ? !Number.isFinite(statedExpiry) || Date.now() > statedExpiry
            : !Number.isFinite(issuedAt) || (Date.now() - issuedAt) > 28800000;
          if (isExpired) {
            localStorage.removeItem('versai_token');
            localStorage.removeItem('versai_token_timestamp');
            localStorage.removeItem('versai_token_expires_at');
            setIsAuthenticated(false);
          } else {
            setIsAuthenticated(true);
          }
        } else {
          setIsAuthenticated(false);
        }
      }

      // 3. Unmask the UI
      setIsCheckingAuth(false);
    };

    initApp();
  }, [API_URL]);

  // SPEC-26 Phase 4: hold the boot loader for one branded beat once the handshake resolves, then
  // reveal the Enter Boutique gate. Runs for every tenant (harmless — only reel mode renders the
  // Loader at all), so non-reel brands are unaffected either way.
  // Also waits on isAuthenticated, not just the handshake: the loader renders after the auth gate,
  // so on a tenant that requires OTP the beat would otherwise tick away behind the login form and
  // the guest would never see it.
  useEffect(() => {
    if (isCheckingAuth || !isAuthenticated) return undefined;
    // The Enter Boutique gate that follows is a full-bleed photograph. Warming it during the beat
    // means the gate paints complete instead of assembling itself while the guest reads it. The
    // WebP is what any browser that supports it will actually request from the <picture> there;
    // the handful that fall back to the JPEG simply fetch it cold, which is the old behaviour.
    // WebP unconditionally rather than feature-detected: it is what every browser that supports it
    // will actually request from the <picture>, and the few that don't waste 78 KB, not 10 MB.
    const bg = new Image();
    bg.src = DEFAULT_BACKGROUND_WEBP_SRC;
    const timer = setTimeout(() => setShowBootLoader(false), LOADER_DISPLAY_MS);
    return () => clearTimeout(timer);
  }, [isCheckingAuth, isAuthenticated]);

  // SPEC-26 Phase 4: captions are part of the authored reel composition (the Figma footer bar shows
  // live dialogue in every chapter frame), so reel tenants start with CC on. Legacy tenants keep the
  // shipped off-by-default. Keyed on the flag rather than on brandConfig's identity so a later
  // re-render can't silently re-enable captions the guest has since switched off.
  const reelModeEnabled = Boolean(brandConfig.feature_flags?.reel_mode);
  useEffect(() => {
    if (reelModeEnabled) setCcEnabled(true);
  }, [reelModeEnabled]);

  // SPEC-26 Phase 4b: which authored composition the reel chrome renders. Read here, above the
  // boot-loader and gate early-returns below, because it is a hook and must run on every render
  // path. As of Phase 6 this is gated on the DEVICE, not the viewport's shape: only an actual phone
  // earns the mobile composition, so a desktop window dragged narrow (or zoomed) stays desktop and
  // an iPad gets desktop in either orientation. See useViewportSize's `resolveComposition`.
  const composition = useBreakpoint();
  const isMobileComposition = composition === 'mobile';
  // SPEC-82: every voice-first prop in one call — footers (null when flag-off), top-chrome layout, Views switch, menu.
  // mic_voice_control: from the start of the conversation until the agent's first words (see useConnectingCue).
  const connectingCue = useConnectingCue({
    enabled: !!ux.mic_voice_control, started: isExperienceStarted && !showIntro && !introX.holding, agentSpeaking: isAgentSpeaking,
  });
  const voiceFirst = useVoiceFirstChrome({
    ux, introX, brandName, composition, footerShown: inBoutique && !showIntro,
    connecting: connectingCue,
    menuInputs: {
      stage: stageMachine.state.stage, collections: brandConfig.static_collections, reelRailTitle: reelProduct?.rail?.title,
      stageReelTitle: stageMachine.state?.collection?.reel_title, reelCollection: brandConfig.waitlist?.collection, // Rev 3: the label at landing
      viewToggleButtons: viewToggle.buttons, fadePhase: stageFade.phase,
      showIntro, privacyLabel: DISCLOSURE.launcherLabel, onTogglePanel: (kind) => setOverlay(prev => (prev === kind ? null : kind)),
    },
    // Rev 3 (bug 4): the consent panel opens under the hamburger, with the rail's own handlers.
    privacyInputs: { overlay, consent, onSave: (c) => handlePrefsSave(c), onOpenPrivacyPolicy: () => setPrivacyOpen(true) },
  });
  const panelIndicator = usePanelIndicator({ expanded: voiceFirst.railExpanded, composition });

  // Mirrored into a ref for the WebSocket handlers, which are bound once and would otherwise close
  // over the composition at bind time. Same pattern as wishlistItemsRef/cartItemsRef above.
  const isMobileCompositionRef = useRef(isMobileComposition);
  useEffect(() => { isMobileCompositionRef.current = isMobileComposition; }, [isMobileComposition]);

  useEffect(() => {
    if (view === 'PRODUCT' && selectedProduct && liveAgentText) {
      const text = liveAgentText.toLowerCase();

      const allSpecs = {
        ...(selectedProduct.specifications || {}),
        ...(selectedProduct.technical_specs || {})
      };

      // SPEC-26 Phase 1 (AC-10): direct key/value matching is extracted into the generalized,
      // brand-agnostic matcher — shared with the Ophion reel point-tags, no hardcoded vocabulary.
      const newlyFoundKeys = matchAttributes({
        text: liveAgentText,
        product: selectedProduct,
        exclude: highlightedThisTurn.current,
      });

      // Category hard-mapping stays inline, untouched — this is brand/domain-specific bonus
      // matching that Baume/Huntsman's existing highlighter behaviour must keep exactly as-is
      // (SPEC-26 §7); it deliberately does not move into the generalized module. Only evaluated
      // for keys the matcher above didn't already catch.
      Object.keys(allSpecs).forEach((key) => {
        if (highlightedThisTurn.current.has(key) || newlyFoundKeys.includes(key)) return;
        const lowerKey = key.toLowerCase();
        let matched = false;
        if (['movement', 'architecture', 'power_reserve', 'movement_details'].includes(lowerKey) && text.match(/\b(movement|caliber|calibre|rotor|barrel|engine|grenaille)\b/i)) matched = true;
        if (['dial_color', 'dial_finish', 'dial_construction'].includes(lowerKey) && text.match(/\b(dial|face|guilloch|guilloche|numerals|indexes)\b/i)) matched = true;
        if (['hands', 'hand_style'].includes(lowerKey) && text.match(/\b(hand|hands|arrow|rhodium)\b/i)) matched = true;
        if (['case_material', 'case_color', 'case_finish', 'case_size', 'crystal', 'thickness_with_crystal', 'shape'].includes(lowerKey) && text.match(/\b(case|steel|titanium|sapphire|crystal|glass|polish|round|mm)\b/i)) matched = true;
        if (['strap_color', 'strap_material', 'lug_design', 'lug_width'].includes(lowerKey) && text.match(/\b(strap|bracelet|band|leather|lug|lugs|teardrop)\b/i)) matched = true;
        if (matched) newlyFoundKeys.push(key);
      });

      newlyFoundKeys.forEach((key) => highlightedThisTurn.current.add(key)); // Mark as triggered so it doesn't loop!

      if (newlyFoundKeys.length > 0) {
        // 🎧 DYNAMIC AUDIO SYNC: Calculate exactly how much audio is waiting in the queue
        let dynamicDelay = 0;
        if (audioContext.current && nextStartTime.current) {
          const queueLength = nextStartTime.current - audioContext.current.currentTime;
          dynamicDelay = Math.max(0, queueLength * 1000);
        }

        // Add a tiny 200ms buffer so it lights up right as she speaks the syllable
        const syncTime = dynamicDelay + 200;

        setTimeout(() => {
          setHighlightedFeatures(prev => {
            const filteredNewKeys = newlyFoundKeys.filter(k => !prev.includes(k));
            return [...prev, ...filteredNewKeys];
          });

          newlyFoundKeys.forEach(key => {
            setTimeout(() => {
              setHighlightedFeatures(curr => curr.filter(k => k !== key));
            }, 4500);
          });
        }, syncTime); // <--- We use the dynamic math here instead of a hardcoded number!
      }
    }
  }, [throttledAgentText, view, selectedProduct]);

  useEffect(() => {
    if (isAgentSpeaking) {
      hasAgentSpokenOnceRef.current = true;
    }
    else if (!isAgentSpeaking && hasAgentSpokenOnceRef.current) {
      setHasWelcomed(true);
    }
  }, [isAgentSpeaking]);


  //useEffect(() => { isAgentSpeakingRef.current = isAgentSpeaking; }, [isAgentSpeaking]);

  useEffect(() => {
    setSuggestionIndex(0);
    setHasInteracted(false);
    lastProcessedSubtitleRef.current = null;
  }, [view, overlay, selectedProduct]);

  useEffect(() => {
    if (hasWelcomed && !hasInteracted) {
      const interval = setInterval(() => {
        setSuggestionIndex((prev) => (prev + 1) % currentSuggestions.length);
      }, 8000);
      return () => clearInterval(interval);
    }
  }, [hasWelcomed, hasInteracted, currentSuggestions.length]);

  useEffect(() => {
    const isNewSubtitle = activeSubtitle?.text !== lastProcessedSubtitleRef.current;

    if (textInput.length > 0 || (hasWelcomed && activeSubtitle?.sender === 'user' && isNewSubtitle)) {
      setHasInteracted(true);
      lastProcessedSubtitleRef.current = activeSubtitle?.text;
    }
  }, [textInput, activeSubtitle, hasWelcomed]);

  const handleSendEmail = async () => {
    setErrorMessage('');
    setIsResending(true);
    const cleanEmail = authEmail.trim().toLowerCase();

    try {
      // encodeURIComponent, or a `+` in an address arrives at the backend as a space and can
      // never match the allowlist — the guest is then refused exactly like a stranger.
      const res = await fetch(`${API_URL}/api/auth/request-otp?email=${encodeURIComponent(cleanEmail)}`, { method: 'POST' });

      // A 5xx is not the distinction we owe neutrality to. Advancing on one strands the guest
      // waiting for mail that no server ever attempted to send.
      if (!res.ok) {
        setErrorMessage("Service unavailable. Please try again.");
        setIsResending(false);
        return;
      }

      // Beyond that, deliberately no success/rejection branch. The backend answers identically
      // whether or not the address is allowlisted, precisely so this endpoint cannot be used to
      // enumerate who is allowed in; branching on the answer here would hand that oracle back.
      // An unrecognised address simply never receives a code.
      setAuthEmail(cleanEmail); // Save the cleaned email
      setAuthStep(2);
    }
    catch (e) {
      setErrorMessage("Service unavailable. Please try again.");
    }
    finally {
      setIsResending(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/auth/verify-otp?email=${encodeURIComponent(authEmail)}&otp=${encodeURIComponent(authOtp)}`,
        { method: 'POST' }
      );
      if (res.ok) {
        const data = await res.json();
        localStorage.setItem('versai_token', data.token);
        localStorage.setItem('versai_token_timestamp', Date.now().toString());
        // The backend already knows when this token dies — its TTL is bounded by the session
        // window, which a second device shortens. Storing the returned figure keeps the two from
        // drifting; assuming a fixed 8h here meant the UI looked signed in while the agent was
        // already closing the socket with 1008.
        //
        // Cleared FIRST, unconditionally: a previous session's expiry surviving a new sign-in
        // would govern the new token, signing the guest out on their next reload.
        localStorage.removeItem('versai_token_expires_at');
        if (Number.isFinite(data.expires_in_hours) && data.expires_in_hours > 0) {
          localStorage.setItem('versai_token_expires_at', String(Date.now() + data.expires_in_hours * 3600000));
        }
        setIsAuthenticated(true);
      } else {
        setErrorMessage("Invalid or expired code."); // 🌟 Better error UX
      }
    } catch (e) {
      setErrorMessage("Failed to verify. Please try again.");
    }
  };

  // SPEC-8: exit 3D scene — returns to GRID and clears the pending command buffer
  const handleSceneExit = useCallback(() => {
    if (sceneExiting) return; // idempotent — one-way pilot constraint
    setSceneExiting(true);
    viewRef.current = 'GRID'; // sync update so WS closure sees it immediately
    setView('GRID');
    pendingSceneCommandsRef.current = [];
    sceneRef.current = null;
  }, [sceneExiting]);

  // SPEC-8 Phase 5: RJJ telemetry — frontend emits viewport state, backend applies decision rules
  const handleViewportStateSync = useCallback((payload) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: 'scene_telemetry', ...payload }));
    }
  }, []);

  const doEnter = async (withMic) => {
    if (brandConfig?.feature_flags?.scene_control && sceneExiting) {
      console.warn('[SPEC-8] 3D re-entry blocked — one-way pilot constraint');
      return;
    }
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: AUDIO_SAMPLE_RATE });
      await ctx.resume();
      audioContext.current = ctx;
      agentAnalyser.current = ctx.createAnalyser();
      agentAnalyser.current.fftSize = 256;
      agentAnalyser.current.connect(ctx.destination);
      // 🎬 Only play a brand intro film if this tenant has one (driven by brandConfig.intro_video
      // from /api/config — both Baume and Huntsman now ship one). If a tenant has no film, skip
      // straight to the boutique and connect the agent immediately.
      const introUrl = brandConfig.intro_video;
      // In reel_mode the Projectionist owns the intro film (its boot chapter). Treat it
      // the same as a legacy introUrl: suppress VAD + defer agent connect until it ends.
      const reelModeActive = !!brandConfig.feature_flags?.reel_mode;
      // 🎬 Returning guest: the Projectionist boots into the collection sweep instead of the
      // film, so there is nothing to gate the mic behind and nothing to delay the agent for —
      // everything below that exists to protect the film is skipped and entry behaves like a
      // no-film tenant. Scoped to reel_mode deliberately: Baume and Huntsman keep their own
      // intro film on every visit, which is outside this change.
      const skipFilmForReturningGuest = reelModeActive && hasSeenIntroBefore;
      introSkippedForReturningRef.current = skipFilmForReturningGuest;
      const willPlayIntro = (!!introUrl || reelModeActive) && !skipFilmForReturningGuest && !introX.skipFilm; // SPEC-82 skip_intro_film

      // 🎤 Build the FULL mic capture graph NOW — during the welcome gesture, BEFORE the intro film
      // plays / before the agent connects. This does two things at once on the welcome screen (not
      // over the agent's first words):
      //   1. fires the browser/iOS permission prompt (and absorbs the accept-hiccup here), and
      //   2. constructs getUserMedia + the mic AudioContext + worklet, the heavy work that used to
      //      stutter the video / first words when it ran at agent-init time.
      // When an intro film WILL play, gate the mic as "intro" from build-time: handleMicFrame drops
      // every frame while showIntroRef is true (and the WS isn't open yet) and the VAD treats the mic
      // as silent during the intro. Tenants with no intro keep showIntroRef false — frames are still
      // dropped because the WS isn't open until the agent connects below. We await the build so the
      // prompt + graph fully complete before either the film starts or the agent connects.
      if (willPlayIntro) showIntroRef.current = true;
      introX.arm(withMic); // SPEC-82 tap_to_talk: raises the same gate synchronously (trap T-1)
      // 🔐 VER-40: only build the mic capture graph when the guest consented to recording.
      // Without consent we still enter (text/browse mode); the mic simply never initializes.
      if (withMic) {
        await startMicrophone();
      } else {
        setIsMicOn(false);
      }
      // BUG FIX (2026-09-07, watches demo — audio jumps in the first seconds of the film): on the
      // grid path the unmuted <video> mounted the instant startMicrophone() resolved, so the OS
      // side-effects of a freshly opened capture stream (echo-cancellation engaging,
      // communications ducking, the 16 kHz mic context opening its output) landed inside the
      // film's first seconds. The reel never heard this because its Projectionist mount adds a
      // natural settle. Give the grid film the same breath — BEFORE the welcome screen comes
      // down, so the guest never sees an empty boutique behind the wait (review 2026-09-07).
      if (willPlayIntro && !reelModeActive) await new Promise(resolve => setTimeout(resolve, 450));
      setIsExperienceStarted(true);
      const scene3dEnabled = brandConfig.feature_flags?.scene_control || process.env.REACT_APP_SCENE_CONTROL === 'true';
      console.log('[SPEC-8] handleEnterBoutique — scene_control flag:', scene3dEnabled, '| brandConfig.feature_flags:', brandConfig.feature_flags);
      if (scene3dEnabled) {
        viewRef.current = 'SCENE_3D'; // sync update before WS connects
        setView('SCENE_3D');
        console.log('[SPEC-8] setView(SCENE_3D) called — viewRef now:', viewRef.current);
      }
      if (willPlayIntro) {
        // 🎬 Suppress the HTML5 overlay in reel_mode (Projectionist owns the film).
        // setShowIntro(true) still runs so the reactive effect keeps showIntroRef.current
        // true across re-renders until sendReelIntroFinished clears it.
        setShowIntro(true);
        // Connect the agent shortly after entry so it's ready when the intro ends.
        clearTimeout(agentArmTimerRef.current);
        agentArmTimerRef.current = setTimeout(() => setAgentConnectArmed(true), 1200);
      } else {
        setAgentConnectArmed(true);  // no intro film (or a returning guest) — connect right away
      }
    } catch (err) { console.error("Failed to initialize audio:", err); }
  };

  // 🔐 VER-40 entry point for the welcome button. First-time guests (or after a policy
  // version bump) must clear the blocking consent gate; returning guests enter directly,
  // honoring their stored choice.
  const requestEnter = () => {
    if (needsDecision) { setConsentGateOpen(true); return; }
    hasAudioConsentRef.current = canCaptureAudio(consent);
    doEnter(hasAudioConsentRef.current);
  };

  // Guest resolved the gate: persist, sync the imperative guard, then enter.
  const handleConsentSave = (categories) => {
    saveConsent(categories);
    hasAudioConsentRef.current = categories.audioRecording === true;
    setConsentGateOpen(false);
    doEnter(hasAudioConsentRef.current);
  };

  // The enable-voice / enable-microphone widget (see voiceOffPromptProps). A retry asks the browser again from this tap.
  const voiceOffPrompt = voiceOffPromptProps({
    started: isExperienceStarted, landing: introX.landing, hasAudioConsent, needsDecision, mic: micBlocked,
    covered: overlay === 'CART' || overlay === 'WISHLIST' || overlay === 'PRIVACY' || stageMachine.state.stage === Stage.PIECE,
    micVoiceControl: !!ux.mic_voice_control, openPrivacy: () => setOverlay('PRIVACY'),
    enterWithVoice: () => enterWithVoice(),
    retryMic: () => { micBlocked.markRetry(); if (isMicOn) startMicrophone(); else setIsMicOn(true); },
  });

  // Preferences saved mid-session: withdrawing audio stops the mic immediately; re-granting
  // it turns voice back on (setIsMicOn(true) re-runs the lifecycle effect → startMicrophone).
  const handlePrefsSave = (categories) => {
    saveConsent(categories);
    const allowed = categories.audioRecording === true;
    hasAudioConsentRef.current = allowed;
    if (!allowed) {
      setIsMicOn(false);
      stopMicrophone();
    } else if (isExperienceStarted) {
      setIsMicOn(true);
    }
  };
  // mic_voice_control (dev + staging): the mic button is the one voice control - without voice consent its tap turns voice ON
  // (records it and starts the mic) instead of sending the guest to the privacy panel, which carries no voice row there.
  const enableVoice = () => handlePrefsSave(withVoice(consent?.categories));
  const enterWithVoice = () => handleConsentSave(withVoice(consent?.categories));
  // The footer mic button and its shown state (uiExperiment/micVoiceToggle.js; flag-off: exactly the old toggle and values).
  const { onToggleMic, micShownOn, micRecording } = micVoiceToggle({
    enabled: !!ux.mic_voice_control, landing: introX.landing, hasAudioConsentRef, hasAudioConsent, needsDecision, isMicOn, setIsMicOn,
    micBlocked, startMicrophone: () => startMicrophone(), enableVoice, enterWithVoice, openPrivacy: () => setOverlay('PRIVACY'),
  });
  useEffect(() => { setMicVoiceControl(!!ux.mic_voice_control); }, [ux.mic_voice_control]);

  // ✨ MAGIC LENS CALCULATOR
  const handleLensMove = (e) => {
    if (!imgContainerRef.current) return;

    const { left, top, width, height } = imgContainerRef.current.getBoundingClientRect();

    // Get mouse coordinates relative to the container
    const x = e.clientX - left;
    const y = e.clientY - top;

    // Calculate percentage for the zoomed background image
    const bgX = (x / width) * 100;
    const bgY = (y / height) * 100;

    setLensPos({ x, y, bgX, bgY });
  };

  /*useEffect(() => {
    let timeout;
    if (activeSubtitle) {
      timeout = setTimeout(() => { setActiveSubtitle(null); }, 8000);
    }
    return () => clearTimeout(timeout);
  }, [activeSubtitle]);

  useEffect(() => {
    let silenceInterval;
    if (isConnected && !isMicOn) {
      silenceInterval = setInterval(() => {
         if (ws.current?.readyState === WebSocket.OPEN) {
           const silentBuffer = new Float32Array(2048);
           ws.current.send(floatTo16BitPCM(silentBuffer));
         }
      }, 128); 
    }
    return () => clearInterval(silenceInterval);
  }, [isConnected, isMicOn]);*/

  const base64ToArrayBuffer = (base64) => {
    let cleanBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');
    const pad = cleanBase64.length % 4;
    if (pad) cleanBase64 = pad === 1 ? cleanBase64.slice(0, -1) : cleanBase64 + '='.repeat(4 - pad);
    const binaryString = window.atob(cleanBase64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) bytes[i] = binaryString.charCodeAt(i);
    return bytes.buffer;
  };

  useEffect(() => {
    if (!agentWanted || !agentConnectArmed) return; // agentWanted = started, or no_enter_gate's early connect (see useEarlyConnect)
    let connectUrl = WS_URL;
    const params = new URLSearchParams();
    // Always forward a token we hold. On an ungated tenant none is ever minted, and the agent
    // ignores the parameter when REQUIRE_AUTH is false — so there is nothing to gate this on, and
    // gating it on the hostname is what silently broke staging.
    if (localStorage.getItem('versai_token')) {
      params.set('token', localStorage.getItem('versai_token'));
    }
    // Demo identity tier: forward ?customer=<key> from the page URL to the backend so it can run
    // the authenticated ("welcome back, <name>") flow. Absent → anonymous flow.
    const customer = new URLSearchParams(window.location.search).get('customer');
    if (customer) params.set('customer', customer);
    // The film was skipped for this returning guest, so nothing is playing on screen. The backend
    // needs this at CONNECT (not at intro_finished): its on-connect turn otherwise tells the model
    // a film is playing and to stay completely silent, and that instruction then suppresses the
    // welcome that arrives moments later.
    if (introSkippedForReturningRef.current) params.set('returning', '1');
    // BUG FIX (2026-09-07): tell the backend a film is playing on THIS connection. A reloaded
    // page reuses its session id (SPEC-39), so the backend sees a restored session and used to
    // skip the silent grid pre-load — the grid then arrived after the film instead of under it.
    if (showIntroRef.current) params.set('intro_playing', '1');
    // SPEC-11: stable session id so reconnects resume the same conversation.
    // SPEC-39 OQ3: both tiers now live in localStorage — see state/sessionIdentity.js for why
    // sessionStorage was abandoned and what it costs (tabs share a bag).
    if (!sessionIdRef.current) {
      sessionIdRef.current = resolveSessionId(customer);
    }
    params.set('session_id', sessionIdRef.current);
    // SPEC-67 D-1/D-9: which page this socket belongs to, and — only on this page's reconnect on
    // the same sid — that it asks to resume. A refresh mints a new page id, so it never resumes.
    params.set('page_id', getPageId());
    if (wantsResumeParam(sessionResume.classify(sessionIdRef.current))) params.set('resume', '1');
    // SPEC-74: a logo soft reset announces itself on its NEW sid until that sid has opened once.
    logoSoftReset.addConnectParams(params, sessionResume.classify(sessionIdRef.current));
    const qs = params.toString();
    if (qs) connectUrl += `?${qs}`;
    ws.current = new WebSocket(connectUrl);
    ws.current.binaryType = "arraybuffer";
    ws.current.onopen = () => {
      setIsConnected(true);
      setShowReconnectBanner(false);
      reconnectAttemptsRef.current = 0;
      // 🛑 Manual stop: a fresh session has no abandoned tail — whatever turn was stopped died with
      // the socket that was carrying it. The gate must not survive into it (see below, and
      // useManualStop `resetForNewSession`). Belt to onclose's braces: this also covers a first
      // connect that somehow inherited a gate, and it is the last word before any frame arrives.
      manualStop.resetForNewSession();
      // 🎬 Same argument for the film-tail gate: the turn it was holding back died with the old
      // socket, so nothing is left to swallow and a stale gate would only mute the new session.
      filmTailGateRef.current.reset();
      // SPEC-67 D-15: the held stage queues die with the socket on EVERY path (SPEC-59 AC-26).
      stageMachine.clearPending();
      // SPEC-67 RC-2 bug fix: a same-page reconnect keeps the screen until the backend answers
      // `session_resume`; the hook re-runs this handler with the guard released on a fresh answer.
      if (sessionResume.deferOpen(sessionIdRef.current)) return;
      // SPEC-59 D-15: and the same argument for the stage machine's held commands — a stage
      // command queued under a film on the previous socket describes a session that is gone, and
      // the backend now sends no stage on a new connection, so nothing else would ever reset it.
      stageMachine.resetPending();
      // SPEC-35 (SPEC-31 G2): offer the paint-cache state (ids only) for reconciliation.
      // The server answers with the authoritative selection_state envelope even when it
      // holds NOTHING — which is exactly what erases stale localStorage rows after the
      // snapshot TTL. Slightly delayed so the connect-time hydration lands first.
      setTimeout(() => {
        try {
          const offer = buildSelectionOffer({
            wishlistItems: wishlistItemsRef.current || [],
            cartItems: cartItemsRef.current || [],
          });
          if (offer && ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify(offer));
          }
        } catch { /* an offer is best-effort by contract */ }
      }, 700);
      // SPEC-C2: flush the current on-screen grid so an early deictic reference ("the third one")
      // can resolve even before the guest's first search changes the grid.
      // BUG FIX (2026-09-08, GRID ONLY — "connection green, agent never speaks again"): these
      // used to be one else-if chain, and the pending-delivery arm sat last behind
      // `showIntroRef.current` being TRUE. Its own comment claimed it covered "the grid is
      // populated so the branch above did not run", but in an else-if chain a populated grid is
      // caught by the FIRST arm and nothing after it runs — and `introFinishedPendingRef` is only
      // ever set by `sendIntroFinished` against a closed socket, which happens from `finishIntro`,
      // which has already set showIntro false. So the arm was unreachable and the pending
      // `intro_finished` was dropped.
      //
      // Reachable on the grid and only on the grid: since `?intro_playing=1` the backend pre-loads
      // the default selection UNDER the film, so `products` is non-empty by the time the film ends.
      // If the socket happens to be down at that moment (SPEC-11 backoff, a Live-API session
      // refresh closing with 1000, force_reconnect) the film's `intro_finished` is banked as
      // pending, the reconnect takes the grid-context arm, and the backend never hears that the
      // film ended — so its on-connect instruction ("a film is playing, stay COMPLETELY SILENT")
      // is never released and the concierge stays mute for the whole session while everything
      // looks connected. Reel tenants have `enable_product_grid: false`, so `products` is always
      // empty there and the relaunch arm always ran.
      //
      // The three concerns are now independent: send the grid context when there is one, and
      // deliver `intro_finished` whenever the film is over and either the grid is empty (the
      // original relaunch case) or a trigger is banked. `sendIntroFinished` dedupes on
      // `introFinishedSentRef`, so this cannot double-welcome.
      const gridHasProducts = !!(productsRef.current && productsRef.current.length);
      // SPEC-C2: flush the current on-screen grid so an early deictic reference ("the third one")
      // can resolve even before the guest's first search changes the grid.
      if (gridHasProducts) sendGridContext(productsRef.current);
      if (!showIntroRef.current) {
        // Returning session: intro is already done but grid is empty (relaunch / reconnect with
        // cold in-memory state). Reset the guard so the backend re-triggers the welcome grid +
        // greeting for this session. NOT done when the grid is populated — that would re-welcome
        // a guest mid-browse.
        if (!gridHasProducts) introFinishedSentRef.current = false;
        if (!gridHasProducts || introFinishedPendingRef.current) {
          setTimeout(() => {
            if (!showIntroRef.current) sendIntroFinished();
          }, 500); // brief delay so backend can finish selection_state hydration first
        }
      }
    };
    ws.current.onmessage = (event) => {
      if (event.data instanceof ArrayBuffer) {
        // 🛑 Manual stop: agent content on the wire means the MODEL is generating, which is a
        // different fact from `isAgentSpeaking` (true for the whole length of the locally queued
        // audio, long after the turn that produced it closed). The stop's gate length depends on
        // this one — see useManualStop's LIFETIME note.
        manualStop.noteAgentActivity();
        handleAudioPlayback(event.data);
        return;
      }
      try {
        const msg = JSON.parse(event.data);

        // 🤔 Liveness tick for useThinkingWatchdog. ANY typed message means the turn is still being
        // worked on — a tool call streaming `ui_update` grid results, a `reel_command`, a
        // `scene_command`, as well as agent transcript. Ticking only on transcript would make the
        // watchdog a flat timer on 'thinking' rather than a silence detector, and it would expire
        // during exactly the pre-first-token window where all the tool latency lives.
        // Binary audio deliberately does not tick: it returns above, and it sets agentState to
        // 'speaking' anyway, which disarms the watchdog.
        // Render-free by requirement (2026-09-01 mobile jank pass): this was a useState counter
        // bump, which re-rendered the whole App tree per typed message — several commits a second
        // during agent speech — and on phones that main-thread churn delayed the reel's rVFC
        // boundary callbacks (the welcome-idle wrap fired late and bled 360-section frames). The
        // poke re-arms the watchdog's timer behind a ref; nothing re-renders.
        notifyBackendActivity();

        if (msg.type === 'transcript') {
          // 🛑 Manual stop: an agent transcript is agent content, so it marks the turn in flight —
          // BEFORE the discard check, because a caption arriving during a stop is the abandoned
          // turn proving it is still streaming, which is exactly a turn in flight.
          if (msg.sender === 'agent') manualStop.noteAgentActivity();
          // 🛑 Manual stop: the same gate that discards orphan audio must discard orphan CAPTIONS.
          // On the native-audio model the agent's words arrive here, not as content parts, so the
          // backend's suppression never sees them — without this the voice cuts out and the caption
          // bar keeps printing the rest of the answer. Agent only: the guest's own captions stay.
          if (msg.sender === 'agent' && manualStop.shouldDiscardAgentOutput()) return;
          transcriptStore.push({ sender: msg.sender, text: msg.text, isPartial: msg.isPartial });

          if (msg.sender === 'user' && !msg.isPartial && msg.text?.trim()) {
            if (!hasUserSpoken) setHasUserSpoken(true);
          }
          if (msg.sender === 'agent') {
            // These two strings feed ONLY the PRODUCT-view scanners (the Vesper lore effect and
            // the attribute highlighter — both no-op on every other view), and nothing renders
            // them; reel captions read transcriptStore. Unconditional, they re-rendered the App
            // tree per partial through the reel too — the other half of the 2026-09-01 mobile
            // jank alongside the watchdog tick above. viewRef, not view: this closure is bound
            // once per socket. PRODUCT-view behaviour is byte-identical (SPEC-26 §7).
            if (viewRef.current === 'PRODUCT') {
              setLiveAgentText(msg.text); // immediate read for the scanners

              // 🚀 Throttle the background scanners to run every 300ms instead of every 10ms
              if (!throttleTimerRef.current) {
                throttleTimerRef.current = setTimeout(() => {
                  setThrottledAgentText(msg.text);
                  throttleTimerRef.current = null;
                }, 300);
              }
            }
          }
          return;
        }

        const content = msg.content || (msg.serverContent && msg.serverContent.modelTurn);
        if (content && Array.isArray(content.parts)) {
          // 🛑 Manual stop: the half-cascade shape of the same signal as the binary branch above.
          // An event that carries BOTH parts and the boundary marks here and is cleared by
          // `noteTurnComplete` below — clearing last is what makes the net result correct.
          manualStop.noteAgentActivity();
          content.parts.forEach(part => {
            const inlineData = part.inlineData || part.inline_data;
            if (inlineData && inlineData.mimeType?.startsWith('audio/') && inlineData.data) {
              handleAudioPlayback(base64ToArrayBuffer(inlineData.data));
            }
          });
        }

        // 🛑 Manual stop: the ONLY thing that lifts the discard gate (bar its 30s ceiling). The
        // gate exists to swallow the tail of the turn the guest abandoned, and `turnComplete` is
        // that turn saying it has no more tail — anything after it is a new turn the guest caused
        // and must be heard. Placed AFTER the audio parts above and not before: native audio can
        // hang a trailing audio part off the very event carrying the boundary, and that part still
        // belongs to the abandoned turn. Untyped event, so it cannot live in the `msg.type` ladder
        // — see useManualStop for the ADK/by_alias wire shape this reads.
        manualStop.noteTurnComplete(msg);
        // 🎬 Same placement, same reason, for the film-tail gate: this turn is over, so whatever
        // the agent says next is a NEW turn and must be heard even if this one was spoken under the
        // film. `interrupted` counts too — it is what the intro_finished injection provokes.
        if (isTurnBoundary(msg)) {
          filmTailGateRef.current.noteTurnBoundary();
          implicitRelease.noteTurnBoundary(); // SPEC-82 Rev 3: a held pre-tap request waits for the welcome's boundary
        }

        handleSignaling(msg);
      } catch (e) { }
    };

    ws.current.onclose = (event) => {
      setIsConnected(false);
      // 🛑 Manual stop: drop the discard gate with the transport. `discardRef` lives on the App
      // instance, not on the socket, and SPEC-11 reconnects on a 1→2→4→8→15s backoff — every step
      // of it well inside DISCARD_CEILING_MS. Left engaged, a stop pressed just before a blip
      // resumes into the NEW session and discards its welcome audio and captions while the mic
      // stays dead, with nothing on screen to say why. The turn the gate was protecting against
      // cannot outlive the socket that was streaming it, so there is nothing left to protect.
      manualStop.resetForNewSession();
      sessionResume.onClose(event.code); // SPEC-67 D-9: a waiting open dies with it; 1008 → fresh visit
      console.warn("WebSocket Closed. Code:", event.code);
      // SPEC-8: freeze 3D scene on disconnect so no orphaned RAF loops run
      if (viewRef.current === 'SCENE_3D') {
        sceneRef.current?.pauseAnimations?.();
      }

      if (event.code === 1008) {
        // Auth expired: clear token + force re-login. No reconnect.
        localStorage.removeItem('versai_token');
        localStorage.removeItem('versai_token_timestamp');
        localStorage.removeItem('versai_token_expires_at');
        setIsAuthenticated(false);
        setIsExperienceStarted(false);
        setAuthStep(1);
        setErrorMessage("Your secure session has expired. Please log in again.");
        return;
      }

      // SPEC-11: transport loss (1006, 1011, etc.) — auto-reconnect with exponential backoff.
      // Uses the stable sessionIdRef so the resumed backend session rehydrates from Redis.
      const MAX_ATTEMPTS = 5;
      const attempt = reconnectAttemptsRef.current;
      if (attempt < MAX_ATTEMPTS) {
        const delay = Math.min(1000 * Math.pow(2, attempt), 15000); // 1→2→4→8→15s
        setShowReconnectBanner(true);
        reconnectTimerRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          // Re-trigger the WS-connect effect below via the nonce (see its declaration for why
          // this isn't a agentConnectArmed false->true toggle).
          setReconnectNonce((n) => n + 1);
        }, delay);
      } else {
        setShowReconnectBanner(true);
        setErrorMessage(`Connection to ${agentName} was lost. Please refresh.`);
      }
    };

    return () => {
      // BUG FIX (2026-09-08, "connection green, pill listening, visualizer flat, agent deaf"):
      // this cleanup used to call stopMicrophone(). It runs on EVERY re-run of this effect, and
      // SPEC-11 re-runs it for every reconnect (reconnectNonce) — a backend rollout, the Live
      // API's 1000 "operation cancelled" refresh, a force_reconnect, any transport blip. Nothing
      // rebuilt the graph afterwards: the mic lifecycle effect below keys on
      // [isExperienceStarted, isMicOn], and a reconnect changes neither. So the socket came back
      // green while micAnalyser/processorRef stayed null — RMS 0, no PCM, isMicOn still true.
      // (The pre-2026-06-22 build restarted the mic on isConnected; the welcome-gesture build
      // dropped that dependency and with it the only restart.) The capture graph does not
      // belong to the socket — handleMicFrame re-reads ws.current on every frame — so it is
      // left alone here and stopped where the experience actually ends (lifecycle effect) or
      // consent is withdrawn (VER-40 effect). Tested in App.Reconnect.test.js (T-S6/T-S7).
      if (reconnectTimerRef.current) { clearTimeout(reconnectTimerRef.current); reconnectTimerRef.current = null; }
      ws.current?.close();
    };
  }, [agentWanted, agentConnectArmed, reconnectNonce]);

  // 🎬 Keep a live mirror of showIntro so the mic processor / VAD callbacks (long-lived closures)
  // can see the current value. While the intro film plays UNMUTED, its audio leaks into the mic;
  // gating on this ref stops that echo from reaching the agent (false barge-in / phantom turn).
  useEffect(() => { showIntroRef.current = showIntro || introX.holding || earlyHeld; }, [showIntro, introX.holding, earlyHeld]); // SPEC-82 D-5 (+ early connect)
  useEffect(() => { productsRef.current = products; }, [products]);

  useEffect(() => {
    // 🎙️ Mic lifecycle. The graph is normally built up-front on the welcome screen (handleEnterBoutique,
    // before the intro film) so the permission prompt + heavy construction never land on the agent's
    // first words. This effect only mirrors the on/off toggle afterwards: startMicrophone() is idempotent
    // (no-op if already built), so it both covers the on-toggle and is a safe rebuild if the welcome-time
    // build was skipped. We intentionally do NOT gate on showIntro/isConnected here — capturing during
    // the intro is harmless because handleMicFrame drops frames while showIntroRef is set or the WS is
    // closed, and the VAD ignores the mic during the intro.
    // BUG FIX (2026-09-08): the experience ending (a 1008 auth close sends the guest back to the
    // login) is now the ONE place the mic is torn down with the session — the WebSocket effect's
    // cleanup no longer does it, because that cleanup also runs on every reconnect (see it).
    // At mount this is a no-op: nothing is built yet, stopMicrophone() only bumps the generation.
    if (!isExperienceStarted) { stopMicrophone(); return; }
    if (isMicOn) startMicrophone();
    else stopMicrophone();
  }, [isExperienceStarted, isMicOn]);

  // 🔐 VER-40: authoritative "consent withdrawn → stop capturing" rule. Whatever path
  // clears audio consent mid-session (preferences panel, policy-version bump, a storage
  // change), the mic is torn down immediately. This backstops the explicit stop in the
  // preferences handler and keeps the keystone invariant true from every direction.
  useEffect(() => {
    if (isExperienceStarted && !hasAudioConsent) {
      hasAudioConsentRef.current = false;
      if (isMicOn) setIsMicOn(false);
      stopMicrophone();
    }
  }, [hasAudioConsent, isExperienceStarted]); // eslint-disable-line react-hooks/exhaustive-deps

  // 🎙️ Shared mic-frame handler — identical gating for both the AudioWorklet and the ScriptProcessor
  // fallback. Receives one 2048-sample Float32 frame and forwards it to the backend as 16-bit PCM.
  const handleMicFrame = (float32) => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    // 🎬 Never stream the mic while the intro film is playing — its unmuted audio would otherwise be
    // captured and the server VAD would treat it as the guest talking (false barge-in + the agent
    // "responding" to the narration). The film owns this moment; the agent stays silent.
    if (showIntroRef.current) return;
    // 🛑 CROSS-REPO CONTRACT (manual stop). While the discard gate is engaged the guest has asked
    // for silence, so nothing the room produces is meant for the agent — and, far more importantly,
    // the BACKEND's suppression is written against exactly this. `services/manual_interrupt.py`
    // counts an inbound BINARY FRAME as condition B of its release ("the guest re-engaged"), on the
    // assumption that a frame arriving means the client decided the guest is speaking. In its own
    // stated common case — the stopped turn had already finished, so `turn_open` is False — that
    // release lifts on the spot; only a turn still generating defers it to `note_turn_complete`.
    // Mic frames land every ~128ms, so a client that kept streaming here would lift the server-side
    // stop within one frame period of setting it and the whole backend half would be inert. Gating
    // here is what makes that assumption true. Do not remove one side of this without the other.
    // (`shouldDiscardAgentOutput` self-expires — at DISCARD_TAIL_MS when nothing was in flight, at
    // DISCARD_CEILING_MS otherwise — so neither a lost boundary nor a boundary that was never
    // coming can mute the microphone for good.)
    if (manualStop.shouldDiscardAgentOutput()) return;
    // Gate the mic while the agent is speaking UNLESS the user has intentionally barged in
    // (userHasSpokenRef set true on the VAD rising edge, and only ever with the keyboard SHUT —
    // see onSpeechStart). Stops the agent's own audio/echo from looping back and self-interrupting
    // the turn (the "silent turn" amplifier).
    if (isAgentSpeakingRef.current && !userHasSpokenRef.current) return;
    ws.current.send(floatTo16BitPCM(float32));
  };

  const startMicrophone = async () => {
    // 🔐 VER-40 keystone: getUserMedia must NEVER run without explicit audio-recording
    // consent, whatever isMicOn or the lifecycle effect say. This is the single hard gate.
    if (!hasAudioConsentRef.current) { setIsMicOn(false); return; }
    // Idempotent: the graph may already have been built on the welcome screen (handleEnterBoutique).
    // The lifecycle effect also calls this, so bail if a live capture node already exists.
    if (processorRef.current) return;
    // 🎙️ Generation token: if the mic is turned off (or restarted) while we're awaiting getUserMedia
    // or the worklet module, this start has been superseded — bail and clean up instead of leaving a
    // live, dangling mic graph behind. stopMicrophone() bumps the same counter to invalidate us.
    const session = ++micSessionRef.current;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: { sampleRate: MIC_SAMPLE_RATE, echoCancellation: true, noiseSuppression: true, autoGainControl: true, channelCount: 1 } });
      if (session !== micSessionRef.current) { stream.getTracks().forEach(t => t.stop()); return; }
      streamRef.current = stream;
      // BUG FIX (2026-09-07, sporadic "pill on, visualizer flat"): the capture source can go
      // silent underneath a healthy graph (OS default-device switch, Bluetooth, another app
      // taking the mic). Chrome reports it on the track; nothing here listened. Log the device
      // and rebuild the graph when the track ends or stays muted (state/micTrackHealth.js).
      // Observability must never be able to break capture: a stream without getAudioTracks (jsdom,
      // an unusual polyfill) or a throwing attach leaves the graph exactly as it was.
      try {
        micTrackDetachRef.current?.();
        const track = typeof stream.getAudioTracks === 'function' ? stream.getAudioTracks()[0] : null;
        micTrackDetachRef.current = attachMicTrackHealth(track, { restart: restartMicrophone });
      } catch (healthErr) {
        micTrackDetachRef.current = null;
        console.warn('[mic] track health monitor not attached:', healthErr && (healthErr.name || healthErr));
      }

      const micCtx = new (window.AudioContext || window.webkitAudioContext)({ sampleRate: MIC_SAMPLE_RATE });
      micContextRef.current = micCtx;
      // A mic context created outside a fresh user gesture can come up suspended; without this the
      // capture node never runs and the agent hears nothing.
      if (micCtx.state === 'suspended') { try { await micCtx.resume(); } catch (e) { } }
      // BUG FIX (2026-09-08): that await had no generation check after it, and the analyser is
      // assigned below. A start superseded while parked on `resume()` (a track-health restart, a
      // fast mic off/on) would settle late and overwrite `micAnalyser.current` — which the newer
      // start had already pointed at its own live graph — with an analyser on a context the newer
      // start's `stopMicrophone()` had closed. PCM keeps flowing from the newer worklet, so the
      // agent still hears the guest, but the VAD reads that dead analyser as permanent silence:
      // flat visualizer, no speech_start, no barge-in, for the rest of the session. Same bail
      // shape as the two worklet checks below.
      if (session !== micSessionRef.current) { stream.getTracks().forEach(t => t.stop()); try { await micCtx.close(); } catch (e) { } return; }
      // BUG FIX (2026-09-07, watches demo — sporadic "mic on but visualizer flat"): the resume
      // above ran ONCE and its failure was swallowed, and Chrome/Windows can also suspend a
      // running context later (device change, focus loss, AEC re-init while the intro film
      // plays). A suspended context feeds the analyser 128s → RMS 0 → flat visualizer and no
      // frames, while isMicOn still says on. Re-resume on every state change and on the next
      // gesture, so the graph self-heals instead of staying silent for the session.
      micCtx.onstatechange = () => {
        if (micCtx.state === 'suspended' && micContextRef.current === micCtx) {
          micCtx.resume().catch(() => { });
        }
      };
      if (micCtx.state !== 'running') {
        const resumeOnGesture = () => { micCtx.resume().catch(() => { }); };
        window.addEventListener('pointerdown', resumeOnGesture, { once: true });
        window.addEventListener('keydown', resumeOnGesture, { once: true });
      }

      const source = micCtx.createMediaStreamSource(stream);
      micAnalyser.current = micCtx.createAnalyser();
      micAnalyser.current.fftSize = 256;
      source.connect(micAnalyser.current);

      // Prefer the AudioWorklet (capture on the audio thread → no main-thread glitches). If the module
      // can't load for any reason, fall back to the proven ScriptProcessor so the agent can always hear.
      let node;
      try {
        await micCtx.audioWorklet.addModule(`${process.env.PUBLIC_URL || ''}/mic-processor.js`);
        if (session !== micSessionRef.current) { stream.getTracks().forEach(t => t.stop()); try { await micCtx.close(); } catch (e) { } return; }
        node = new AudioWorkletNode(micCtx, 'mic-processor');
        node.port.onmessage = (e) => handleMicFrame(e.data);
      } catch (workletErr) {
        console.warn('AudioWorklet unavailable — falling back to ScriptProcessor:', workletErr);
        if (session !== micSessionRef.current) { stream.getTracks().forEach(t => t.stop()); try { await micCtx.close(); } catch (e) { } return; }
        node = micCtx.createScriptProcessor(2048, 1, 1);
        node.onaudioprocess = (e) => handleMicFrame(e.inputBuffer.getChannelData(0));
      }

      // Same topology as before (source -> node -> muted gain -> destination) so the node is pulled by
      // the graph; the gain is 0 so nothing is ever heard.
      source.connect(node);
      const gainNode = micCtx.createGain();
      gainNode.gain.value = 0;
      node.connect(gainNode);
      gainNode.connect(micCtx.destination);
      processorRef.current = node;
      micBlocked.clear();
      micBlocked.setLive(true);
    } catch (err) {
      // BUG FIX (2026-09-07): a getUserMedia / worklet failure used to flip the mic off with no
      // trace at all, which is indistinguishable from "the guest never spoke" in a demo. Name it.
      console.error('[mic] capture graph failed — mic turned off:', err && (err.name || err));
      setIsMicOn(false);
      micBlocked.fail(err); // the guest is told (VoiceOffPrompt) instead of sitting in a silent boutique
    }
  };

  const stopMicrophone = () => {
    // Invalidate any in-flight startMicrophone() so it tears itself down instead of completing.
    micSessionRef.current++;
    // A deliberate stop cancels a restart deferred by the film guard above — otherwise a guest who
    // muted during the film would find the mic live again the moment it ended.
    micRestartPendingRef.current = null;
    // Detach the track-health listeners BEFORE stopping, so a deliberate stop never reads as a
    // failure and triggers a restart.
    micTrackDetachRef.current?.();
    micTrackDetachRef.current = null;
    micBlocked.setLive(false);
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    const node = processorRef.current;
    if (node) {
      try { if (node.port) node.port.onmessage = null; } catch (e) { }  // AudioWorkletNode
      try { node.onaudioprocess = null; } catch (e) { }                 // ScriptProcessorNode fallback
      try { node.disconnect(); } catch (e) { }
      processorRef.current = null;
    }
    // 🎙️ Null the analyser BEFORE closing the context — the VAD loop reads micAnalyser.current every
    // frame, and calculateRMS() already treats null as silence (returns 0). This avoids ever calling
    // into an analyser whose context has been closed.
    micAnalyser.current = null;
    if (micContextRef.current) {
      const ctx = micContextRef.current;
      micContextRef.current = null;
      try { ctx.close(); } catch (e) { }
    }
  };

  // BUG FIX (2026-09-07): rebuild the capture graph when its source died (see micTrackHealth.js).
  // Deliberate mutes never reach here — stopMicrophone detaches the health listeners first — and
  // startMicrophone re-checks consent, so this can never start a mic the guest did not allow.
  const restartMicrophone = (reason) => {
    // BUG FIX (2026-09-08, "the film has no sound, then I hear a second of it midway"): never
    // reopen the capture device while the intro film is playing. Windows ducks every other stream
    // by 80% for as long as a COMMUNICATIONS capture is open (echoCancellation puts us in that
    // class), so closing and reopening the mic makes the film audibly surface for about a second
    // and then drop away again — right in the middle of the one cinematic moment of the demo. The
    // restart buys nothing there either: `handleMicFrame` drops every frame and the VAD ignores the
    // mic while `showIntroRef` is set. It also burns the restart budget below, which can leave the
    // guest with a mic that is already off by the time the film ends. Deferred to `finishIntro`,
    // which is the guest's own recorded rule for this laptop: the ducking drop lands on the film,
    // and the mic is never reopened mid-film.
    if (showIntroRef.current) {
      micRestartPendingRef.current = reason;
      console.warn(`[mic] capture needs a restart (${reason}) — deferred until the intro film ends`);
      return;
    }
    // BUG FIX (2026-09-08): budget the restarts. A source that comes back muted every time (mic
    // muted at the OS level, a device that never delivers) would otherwise loop getUserMedia +
    // AudioContext every MUTE_GRACE_MS for the whole session — invisible, and on Windows a fast
    // re-open can itself fail. After RESTART_BUDGET_MAX in a window the mic is turned OFF so the
    // pill shows a mic that is not capturing (a tap starts a fresh attempt) instead of a live one.
    if (!micRestartBudgetRef.current.allow()) {
      console.error(`[mic] capture keeps dying (${reason}) — restart budget exhausted, mic turned off; tap the mic to try again`);
      setIsMicOn(false);
      return;
    }
    console.warn(`[mic] restarting capture (${reason})`);
    stopMicrophone();
    startMicrophone();
  };

  /*useEffect(() => {
    let animationFrameId;
    let noiseEnergy = 0; // 🌟 THE LEAKY INTEGRATOR: Replaces consecutiveNoiseFrames

    const updateVolume = () => {
      const dataArray = new Uint8Array(128);
      let agentVol = 0;
      let micVol = 0;
      
      // 1. Calculate Agent Volume
      if (agentAnalyser.current) { 
        agentAnalyser.current.getByteFrequencyData(dataArray); 
        agentVol = dataArray.reduce((a, b) => a + b) / dataArray.length; 
      }
      
      // 2. Calculate Mic Volume
      if (micAnalyser.current && isMicOn && !isKeyboardOpen) { 
        micAnalyser.current.getByteFrequencyData(dataArray); 
        micVol = dataArray.reduce((a, b) => a + b) / dataArray.length; 
      }
      
      currentVolume.current = Math.min(Math.max(agentVol, micVol) / 128, 1.0);
      const normalizedMicVol = Math.min(micVol / 128, 1.0);
      
      // Dynamic Threshold: Harder to trigger while Yris is speaking to prevent echo-loops
      const noiseThreshold = isAgentSpeakingRef.current ? 0.35 : (agentState === 'thinking' ? 0.25 : 0.15);

      if (isMicOn && !isKeyboardOpen) 
      {
        if (normalizedMicVol > noiseThreshold) {
          noiseEnergy += 25; // 🚀 Build energy FAST on noise (4 frames = trigger)
        } else {
          noiseEnergy -= 4;  // 🪂 Drain energy SLOWLY on silence (bridges syllables)
        }

        // Clamp energy between 0 and 100
        noiseEnergy = Math.max(0, Math.min(100, noiseEnergy));

        // 🌟 If energy fills up, it is undeniably human speech!
        if (noiseEnergy >= 100) 
        { 
          if (isAgentSpeakingRef.current) {
             activeAudioSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) {} });
             activeAudioSourcesRef.current = [];
             isAgentSpeakingRef.current = false;
             setIsAgentSpeaking(false);
          }

          setAgentState('listening');
          userHasSpokenRef.current = true;
          if (silenceTimerRef.current) {
            clearTimeout(silenceTimerRef.current);
            silenceTimerRef.current = null;
          }
        }
        
        // 🌟 When energy fully drains to 0, speech has ended. Transition to Thinking.
        if (noiseEnergy === 0 && userHasSpokenRef.current && !silenceTimerRef.current && agentState === 'listening') 
        {
          silenceTimerRef.current = setTimeout(() =>
          {
            setAgentState('thinking');
            userHasSpokenRef.current = false;
            silenceTimerRef.current = null;
          }, 600); // 600ms trailing silence to ensure they are done talking
        }
      }
      animationFrameId = requestAnimationFrame(updateVolume);
    };
    updateVolume();
    return () => 
    { 
      cancelAnimationFrame(animationFrameId); 
      if (silenceTimerRef.current) clearTimeout(silenceTimerRef.current); 
    };
  }, [isMicOn, agentState, isKeyboardOpen]);*/
  // ==============================================================================
  // 🛑 MANUAL STOP — the typing guest's only way to interrupt
  // ==============================================================================
  // One flag governs three separate things, so it is read once and shared: the STOP control itself,
  // whether the mic keeps listening with the keyboard open (`micLiveWithKeyboard` below), and which
  // condition clears the stranded-`isUserSpeaking` flag. Tenants without it are byte-identical.
  const manualInterruptEnabled = Boolean(brandConfig.feature_flags?.manual_interrupt);
  // Declared ABOVE the VAD block on purpose: the footer/audio/transcript consumers further down all
  // come after. `handleMicFrame` and `ws.onmessage` are declared EARLIER and still reference it —
  // safe because neither runs during render, and every member they touch is a `useCallback([])`
  // reading a ref, so a stale closure still reads the live gate.
  // Everything it needs is passed in; the hook owns the policy and holds no App.js knowledge.
  const manualStop = useManualStop({
    enabled: manualInterruptEnabled,
    isAgentSpeaking,
    agentState,
    wsRef: ws,
    audioContext,
    activeAudioSourcesRef,
    nextStartTime,
    speakingTimeoutRef,
    isAgentSpeakingRef,
    setIsAgentSpeaking,
    setIsUserSpeaking,
    setAgentState,
    // The rest of the end-of-speech teardown the flush's cancelled `speakingTimeoutRef` would have
    // run. Without these the abandoned turn's subtitle, spec highlights and lore image sit on
    // screen over a silent agent.
    setLiveAgentText,
    setHighlightedFeatures,
    setActiveLoreImage,
    highlightedThisTurnRef: highlightedThisTurn,
  });

  // SPEC-43 R2: an intro-film REPLAY re-uses the first-entry film gate (`showIntro`) and tells the
  // backend via `video_state`. Pure pass-through of refs — the policy lives in the hook.
  const introReplay = useIntroReplayBridge({
    ws,
    setShowIntro,
    showIntroRef,
    audio: {
      audioContext, activeAudioSourcesRef, nextStartTime, speakingTimeoutRef, resetSpeechClock,
      isAgentSpeakingRef, setIsAgentSpeaking, setAgentState,
    },
  });

  // SPEC-74: the wordmark is a soft reset (new sid, carousel, no film, a short fresh greeting; the bag is
  // kept). Inert while a film, the waitlist form or the checkout overlay owns the screen.
  const logoSoftReset = useLogoSoftReset({
    enabled: Boolean(brandConfig.feature_flags?.logo_soft_reset),
    blocked: showIntro || waitlist.open || checkout.open,
    experienceStarted: isExperienceStarted,
    deps: {
      ws, sessionIdRef, reconnectAttemptsRef, reconnectTimerRef, setReconnectNonce, sessionResume, stageMachine,
      introFinishedSentRef, introFinishedPendingRef, introSkippedForReturningRef,
      forgetSessionId: () => clearStoredSessionId(new URLSearchParams(window.location.search).get('customer')),
      clearScreen: () => { closeVideoOverlay(); reelSpecSheetRef.current = null; setReelSpecSheet(null); setReelSubject(null); },
      audio: {
        audioContext, activeAudioSourcesRef, nextStartTime, speakingTimeoutRef, isAgentSpeakingRef,
        setIsAgentSpeaking, setIsUserSpeaking, setAgentState, setLiveAgentText, setHighlightedFeatures,
        setActiveLoreImage, highlightedThisTurnRef: highlightedThisTurn,
      },
    },
  });

  // 🎙️🛑 The VAD's two activity markers, behind the manual-stop gate.
  //
  // These are ENERGY-triggered, and the backend's release is a fail-OPEN denylist
  // (`manual_interrupt.releases_on`): `speech_start` / `speech_end` are not in its telemetry set,
  // so either one lifts the server-side stop exactly as a mic frame would. `handleMicFrame` is
  // gated for that reason, and leaving these two ungated left the same hole open a crack — pressing
  // stop clears `isAgentSpeakingRef`, which un-suppresses the keyboard-mode rising-edge branch, so
  // ~117ms of room noise was enough to re-open the backend gate the guest had just closed.
  //
  // Nothing is lost by withholding them: `MANUAL_ACTIVITY_DETECTION` is "false" in every shipped
  // environment (see services/agent_factory.py's tested warning against flipping it), so the
  // backend cannot act on a manual activity marker at all — it only reads them as "the guest
  // re-engaged", which during a stop is precisely the claim that must not be made on the room's
  // behalf. Recreated per render; the VAD reads its callbacks out of a ref, so it always has this one.
  const sendVadMarker = (type) => {
    if (manualStop.shouldDiscardAgentOutput()) return;
    if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify({ type }));
  };

  // ==============================================================================
  // 🎙️ IMPLEMENTING THE VAD SUBSYSTEM
  // ==============================================================================
  useVoiceActivityDetection({
    micAnalyser,
    agentAnalyser,
    isMicOn,
    isKeyboardOpen,
    micLiveWithKeyboard: manualInterruptEnabled,
    isAgentSpeakingRef,
    currentVolumeRef: currentVolume,
    speakingThreshold: vadSpeakingThreshold,
    listeningThreshold: vadListeningThreshold,
    echoRejectRatio: vadEchoRejectRatio,
    isIntroPlayingRef: showIntroRef,
    onInterrupt: (levels) => {
      // 🔇 Print what beat the echo gate. If the agent is still cutting itself off, this line
      // is the tuning instruction: raise "Echo reject" above mic/agent. See state/echoGate.js.
      if (levels) {
        console.warn(
          `[vad] barge-in accepted — mic ${levels.micVol.toFixed(3)} vs agent ${levels.agentEcho.toFixed(3)} `
          + `(ratio ${levels.ratio}; needed > ${(levels.agentEcho * levels.ratio).toFixed(3)})`,
        );
      }
      // 🎬 Ignore VAD entirely while the intro film plays — the film's own audio leaking into the mic
      // must not skip the film or fire a phantom turn. Use the Skip Intro button to bypass the film.
      if (showIntroRef.current) return;
      // 🛑 The discard gate is deliberately NOT lifted here. It lifts when the stopped turn reports
      // itself complete (`manualStop.noteTurnComplete`), never on guest input: this callback is
      // energy-triggered, so a companion talking or a door closing would otherwise re-open it and
      // let the tail the guest asked to be rid of resume. See useManualStop's lifetime note.
      // 🛑 1. Instant local playback kill
      activeAudioSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) { } });
      activeAudioSourcesRef.current = [];

      if (audioContext.current) {
        nextStartTime.current = audioContext.current.currentTime;
      }
      resetSpeechClock(); // 💬 the rest of that answer will never be heard — stop pacing against it

      isAgentSpeakingRef.current = false;
      setIsAgentSpeaking(false);

      // 2. State transition
      setAgentState('listening');
      userHasSpokenRef.current = true;
      setIsUserSpeaking(true); // 🎙️ guest is now talking → visualizer plays 'speaking' (shared with the agent)
      setShowIntro(false); // 🎬 barging in skips the intro film straight to the grid
      closeVideoOverlay(); // 🎬 barge-in during a movement film ALWAYS closes it; the agent then does whatever was asked
      // 🎙️ Manual activity detection: mark the start of the (barge-in) utterance for the backend.
      sendVadMarker('speech_start');
      console.log("🎤 VAD: Interruption Triggered (Rising Edge)");
    },
    onSpeechStart: () => {
      // 🎬 Ignore VAD entirely while the intro film plays (see onInterrupt) — its audio leaks into the mic.
      if (showIntroRef.current) return;
      // 🛑 The discard gate is NOT lifted here either — same reason as onInterrupt: this is an
      // energy trigger, and room noise must not be able to un-stop the agent.
      setAgentState('listening');
      // 🛑 The barge-in exemption is granted ONLY with the keyboard shut, and that is a structural
      // guarantee, not a nicety. `handleMicFrame` reads this flag to keep streaming the guest's
      // voice while the agent is speaking; in keyboard mode there is no barge-in to exempt, because
      // the rising-edge branch in useVoiceActivityDetection refuses to interrupt from there
      // precisely to keep the agent's own loopback out of its own ear. Setting it here regardless
      // is how it got STRANDED: set while the agent was silent (a companion, a saturating room, a
      // laptop mic hearing the keys), never cleared — `onSpeechEnd` is the only other clear and it
      // is easy to miss — and then the agent's next turn streams its own voice back to Gemini,
      // whose server-side VAD self-interrupts it. `echoCancellation: true` is not a substitute for
      // this and must not be relied on as one.
      if (!isKeyboardOpen) userHasSpokenRef.current = true;
      setIsUserSpeaking(true); // 🎙️ guest is now talking → visualizer plays 'speaking' (shared with the agent)
      setShowIntro(false); // 🎬 user speaking dismisses the intro film
      closeVideoOverlay(); // 🎬 the guest is speaking over a film -> close it and act on their request
      // (manual activity detection start sent below)
      // 🎙️ Manual activity detection: mark the start of the user's utterance for the backend.
      sendVadMarker('speech_start');
      console.log("🎤 VAD: Speech Started (Rising Edge)");
    },
    onSpeechEnd: () => {
      if (showIntroRef.current) return; // 🎬 intro film: VAD is suppressed (see onSpeechStart)
      setAgentState('thinking');
      userHasSpokenRef.current = false;
      setIsUserSpeaking(false); // 🎙️ user stopped talking → hand off to thinking/agent
      // 🎙️ Manual activity detection: mark the end of the user's utterance for the backend.
      sendVadMarker('speech_end');
      console.log("🎤 VAD: Speech Ended (Falling Edge)");
    }
  });

  // 🎙️ Cutting the mic mid-word strands `isUserSpeaking` true: the VAD's falling-edge trigger lives
  // inside `if (micActive)`, so the energy stops draining, the 700ms timer never arms and
  // `onSpeechEnd` never runs — the cluster then shows the guest talking over silence until they
  // complete a whole new utterance. This effect is the only thing that clears it in that case.
  //
  // The two lines below deliberately have DIFFERENT conditions.
  //
  // `isUserSpeaking` clears on keyboard-open ONLY for tenants where the keyboard still kills
  // `micActive` — i.e. everyone except `manual_interrupt`. That is the original condition,
  // unchanged, for the original reason: their loop really does freeze, so nothing else can clear
  // the flag. On the reel it must NOT clear, because there the mic keeps running with the field up
  // and blanking the visualizer the instant a talking guest reaches for the keyboard is exactly the
  // "mic reads as muted" bug this work exists to fix.
  //
  // Dropping the clause on the reel is only safe because the strand it guarded is now closed at
  // source. Two things had to be true: the loop keeps draining energy with the keyboard open (so a
  // real falling edge still arrives), AND the loop no longer tears down on every render — its
  // callbacks and tunables moved into refs, so `isSpeechActive` survives the re-render that
  // opening the keyboard causes. Before that second fix the falling edge was routinely lost to
  // typing, which is what made this clause look load-bearing on the reel too.
  // `isIntroPlayingRef` is the remaining `micActive` input but it is a ref, so it cannot be
  // observed here — it is also not a route a guest can take mid-utterance.
  useEffect(() => {
    if (!isMicOn || (isKeyboardOpen && !manualInterruptEnabled)) setIsUserSpeaking(false);
    // 🛑 `userHasSpokenRef` gets the keyboard clause too, but it is a DIFFERENT flag with a
    // different and much worse failure mode. It is the barge-in exemption `handleMicFrame` checks:
    // while it is true the mic streams even though the agent is speaking, so a stranded true means
    // the agent's own voice reaches Gemini and its server-side VAD self-interrupts the turn.
    // `onSpeechStart` now refuses to set it in keyboard mode at all, so this is the second of two
    // belts: entering keyboard mode revokes any exemption granted before the switch, and nothing
    // can grant a new one until the guest leaves it.
    //
    // TENANT-GATED, and that is the point of the extra term rather than an oversight. This clearing
    // did not exist at all before manual stop — the effect only ever cleared `isUserSpeaking` — so
    // running it everywhere would silently change Baume/Huntsman, where nothing else in this work
    // does. The strand it closes is reachable only where the mic keeps running with the field up,
    // which is exactly what `manual_interrupt` buys (`micLiveWithKeyboard`); on the other tenants
    // `micActive` still goes false with the keyboard, so the exemption behaves as it always has.
    // Pinned from both sides by the tenant-parity tests in App.ReelOverlays.test.js.
    if (manualInterruptEnabled && (!isMicOn || isKeyboardOpen)) userHasSpokenRef.current = false;
  }, [isMicOn, isKeyboardOpen, manualInterruptEnabled]);

  // 🤔 Nothing in the transport moves `agentState` off 'thinking' when the agent simply never
  // answers, and an agent that stays silent is often doing exactly what it was told. See
  // useThinkingWatchdog for why this rests instead of erroring. The hook's returned poke is the
  // liveness signal: ws.onmessage calls it on any typed message — render-free and identity-stable,
  // so the once-per-socket closure can capture it — and only true silence trips the window.
  const handleThinkingSilence = useMemo(
    () => makeThinkingSilenceHandler({ triggerReaction, setAgentState }),
    // Deps MUST stay []: useThinkingWatchdog re-arms its timer on identity change, so a
    // per-render identity would restart the 4s window every render. Both captures are
    // mount-stable (triggerReaction closes over a ref + setState; setAgentState is a
    // setState) — see thinkingSilenceBeat.js for the beat's contract and its test.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  const notifyBackendActivity = useThinkingWatchdog({
    agentState,
    onSilence: handleThinkingSilence,
  });

  const handleSignaling = (msg) => {
    // [SPEC-8 DEBUG] Log every typed WS message so we can trace what kills the 3D scene
    if (msg.type && msg.type !== 'audio' && !(msg.type === 'control' && msg.action === 'stop_audio')) {
      console.log('[SPEC-8 WS]', msg.type, msg.action || '', msg.view || '', '| viewRef:', viewRef.current);
    }

    // SPEC-11: backend snapshot loss → reconnect fresh (clear stored session id so a new one is minted)
    if (msg.type === 'error' && msg.action === 'force_reconnect') {
      clearStoredSessionId(new URLSearchParams(window.location.search).get('customer'));
      sessionIdRef.current = null;
      sessionResume.onFreshSession(); // SPEC-67 D-9: a new sid is a fresh visit, never a resume
      reconnectAttemptsRef.current = 0;
      ws.current?.close(1000);
      return;
    }

    // SPEC-67 D-4/D-9: the backend's answer to this page's reconnect.
    if (msg.type === 'session_resume') {
      sessionResume.onAnswer(msg);
      return;
    }
    // SPEC-67 D-9: a queued connect answers late — restart the wait (the frame is handled as today).
    if (isWaitingRoomFrame(msg)) sessionResume.onWaiting();

    // SPEC-8: route 3D scene commands to the mounted scene component (or buffer pre-mount)
    if (msg.type === 'scene_command') {
      console.log('[SPEC-8] scene_command received → dispatching to scene:', msg.action);
      if (sceneRef.current) {
        sceneRef.current.handleSceneCommand(msg);
      } else {
        console.log('[SPEC-8] scene not mounted yet — buffering command');
        pendingSceneCommandsRef.current.push(msg);
      }
      return;
    }

    // Reel pilot: route chapter commands to the Projectionist.
    // Any video overlay is dismissed — the Projectionist owns the screen in reel mode.
    // SPEC-53: stage moves — enter a static collection, open/close a piece card, return to the reel.
    if (msg.type === 'stage_command') {
      console.log('[SPEC-53] stage_command received →', msg.action, msg.transition);
      stageMachine.handleStageCommand(msg);
      return;
    }

    if (msg.type === 'reel_command') {
      console.log('[Reel] reel_command received → chapter:', msg.chapter_id);
      closeVideoOverlay(); // always dismiss — videoOverlay state is stale in this closure
      // SPEC-53 (review R5): off the reel a command is queued only while a return is in flight and
      // replayed at the swap; any other off-stage reel command is dropped with a warning.
      if (stageMachine.handleReelCommand(msg)) reelRef.current?.handleReelCommand(msg);
      return;
    }

    // SPEC-26 D5/Phase 2: the featured-piece envelope, dispatched once per connection at the
    // intro_finished seam. Feeds the Projectionist's point-tag layer — never open_product_details.
    if (msg.type === 'reel_product') {
      console.log('[Reel] reel_product received:', msg.product?.title || msg.product?.id);
      setReelProduct({
        product: msg.product,
        tagEligibility: msg.tag_eligibility,
        // SPEC-27 P13 — {regions, attributeRegion}: where each component sits per chapter, and
        // which component an attribute belongs to. Without it only the nine attributes a designer
        // hand-placed could ever surface a tag.
        tagRegions: msg.tag_regions,
        // Collection-chapter chrome (Figma 45:1143): {title, items:[{id, headline, subline}]} in
        // the film's left-to-right order — drives CollectionOverlay's pill + variant rail.
        rail: msg.rail,
      });
      return;
    }

    // SPEC-34 D7: the spec sheet for a showcased variant — rides beside the same turn's
    // reel_command play_chapter(variant_slot_N). Overlay mounting is chapter- and
    // hold-gated inside the Projectionist; here it is just state.
    if (msg.type === 'reel_spec_sheet') {
      console.log('[Reel] spec sheet received:', msg.variant_id, msg.chapter_id);
      reelSpecSheetRef.current = msg;
      setReelSpecSheet(msg);
      return;
    }

    // SPEC-27 D18/AC-26: the agent has moved to a subject the CURRENT chapter does not show.
    // Point-tags mount on HOLDING/FROZEN and would otherwise survive that change — leaving this
    // chapter's editorial copy on screen underneath an answer about something else. A null
    // subject_key means "nothing here illustrates what I am saying": clear them.
    if (msg.type === 'reel_subject') {
      setReelSubject({ subjectKey: msg.subject_key ?? null, outcome: msg.outcome });
      return;
    }

    if (msg.type === 'control' && msg.action === 'stop_audio') {
      // FOLLOW-UP: the next four lines are now duplicated in `state/agentAudioFlush.js`, which the
      // manual STOP button uses and which was extracted from exactly here. Both this and the VAD
      // barge-in copy (~:1818) should fold onto it — as their own change, not bundled with a feature.
      activeAudioSourcesRef.current.forEach(source => { try { source.stop(); } catch (e) { } });
      activeAudioSourcesRef.current = [];
      if (audioContext.current) nextStartTime.current = audioContext.current.currentTime;
      resetSpeechClock(); // 💬 nothing more will be spoken — release the caption to its full line
      isAgentSpeakingRef.current = false;
      setIsAgentSpeaking(false);
      setIsUserSpeaking(false); // 🎙️ hard reset so the 'speaking' animation can't stick on
      setAgentState('listening');
      setHighlightedFeatures([]);
      highlightedThisTurn.current.clear();
      // 📸 NOTE: do NOT unlock the gallery here. Once the guest asks to see a specific image,
      // that choice must persist for the rest of the deep dive — the lock is only cleared when a
      // fresh product opens (handleCardClick / PRODUCT ui_update) or we navigate away.
      setActiveLoreImage(null);
      if (speakingTimeoutRef.current) clearTimeout(speakingTimeoutRef.current);
      return;
    }

    if (msg.action === 'switch_gallery_image' && typeof msg.index === 'number') {
      // SPEC-53 (review R8): with a static-collection card open the index is the card's, not the grid's.
      if (stageMachine.stateRef.current.stage === Stage.PIECE) { stageMachine.setGalleryIndex(msg.index); return; }
      isGalleryLockedRef.current = true;
      setCurrentImageIndex(msg.index);
      return;
    }

    // 📸 Relative photo navigation ("next photo" / "previous photo") — backend doesn't track the
    // index, the UI does. Wrap around at both ends so the guest can keep stepping through.
    if (msg.action === 'gallery_navigate') {
      if (stageMachine.stateRef.current.stage === Stage.PIECE) { stageMachine.stepGallery(msg.direction); return; } // SPEC-53
      isGalleryLockedRef.current = true;
      const step = msg.direction === 'previous' ? -1 : 1;
      setCurrentImageIndex((prev) => {
        const count = productImagesRef.current?.length || 1;
        return (prev + step + count) % count;
      });
      return;
    }

    // 🎬 Agent-triggered movement video overlay
    if (msg.action === 'open_video' && msg.data?.url) {
      clearTimeout(videoCloseTimerRef.current);
      videoNarrationStartedRef.current = false;
      videoManuallyHeldRef.current = false;
      setIsVideoPaused(false);
      // 🎬 Device-aware source: the backend may ship both a 16:9 (url) and a portrait (url_mobile)
      // encode (e.g. the Huntsman cut film). The browser owns the device truth, so pick here.
      // Uses the resolved composition rather than a raw `innerWidth < 768`, so the encode always
      // matches the chrome drawn around it. The two disagree on a desktop window dragged or zoomed
      // under 768px: that is not a phone, it keeps the desktop composition, and shipping it the
      // portrait cut would letterbox a 9:16 film inside a landscape window.
      const videoUrl = (msg.data.url_mobile && isMobileCompositionRef.current) ? msg.data.url_mobile : msg.data.url;
      setVideoOverlay({ url: videoUrl, title: msg.data.title || '', kind: msg.data.kind || '' });
      // 🎬 Tell the backend a film is on screen so the watchdog suspends stalled-turn recovery
      // (otherwise a slow turn_complete mid-narration makes the agent repeat itself and the film vanishes).
      if (ws.current?.readyState === WebSocket.OPEN) ws.current.send(JSON.stringify({ type: 'video_state', active: true }));
      return;
    }
    if (msg.action === 'video_control' && msg.data?.command) {
      applyVideoControl(msg.data.command);
      return;
    }
    // 🆚 Agent-driven: open the side-by-side comparison of the pinned selection.
    if (msg.action === 'open_compare') {
      console.log('[SPEC-8] open_compare → setView COMPARE | viewRef:', viewRef.current);
      setView('COMPARE'); setOverlay(null); setDiscovery(null);
      return;
    }

    // 🧠 Live DIM read sidecar — update the ambient "show the brain" widget.
    if (msg.action === 'dim_read' && msg.data) {
      setDimRead(msg.data);
      return;
    }

    // 🧠 DIM signal: capture the agent's intent/mood read (sent on grid/product/enrichment payloads).
    if (msg.intent || msg.mood) {
      setAgentRead({ intent: msg.intent || null, mood: msg.mood || null });
    }
    // semantic_enrichment is a sidecar (clusters / comparison axes / continuity) — capture the
    // full payload so the "why these" panel can render the agent's reasoning, then stop.
    if (msg.action === 'semantic_enrichment') {
      if (msg.semantic_intent) setSemanticIntent(msg.semantic_intent);
      return;
    }

    // SPEC-14/SPEC-35: universal selection hydration — backend pushes this on (re)connect
    // (and in answer to a selection_offer) so any frontend repopulates every REGISTERED
    // domain from the server (source of truth). Server wins wholesale; the module warns
    // loudly on manifest domains this build has no handler for (AC-6).
    if (msg.type === 'ui_update' && msg.action === 'selection_state' && msg.data) {
      applyHydration(msg.data, {
        wishlist: setWishlistItems,
        cart: setCartItems,
        pinned: setPinnedItems,
        // SPEC-34 E16 upgraded (AC-5): the focused piece + its sheet survive a
        // reconnect — the overlay re-mounts the moment the playhead reports its chapter.
        focused_variant: (value) => {
          const sheet = value?.sheet ?? null;
          reelSpecSheetRef.current = sheet;
          setReelSpecSheet(sheet);
        },
        // SPEC-53 (review R3 / §4.9): the stage is a session-UI domain. Null lands the reel with
        // no fade (server wins wholesale); a stage this build is not already showing asks the
        // backend to re-dispatch it — `stage_restore` → enter_collection (+ open_piece), "none".
        stage: (value) => {
          if (stageMachine.hydrate(value) === 'restore') sendInteraction('stage_restore', {});
        },
        waitlist: waitlist.hydrate, // SPEC-52 D-7/D-8
      });
      // Mark as returning guest if they have any saved items so the welcome screen adapts.
      const hasItems = (msg.data.wishlist?.length || 0) + (msg.data.cart?.length || 0) + (msg.data.pinned?.length || 0) > 0;
      if (hasItems) setIsReturningGuest(true);
      return;
    }

    startTransition(() => {
      if (msg.type === 'ui_update' && !msg.bulk_result) {
        const incomingView = msg.view ? msg.view.toUpperCase() : '';

        // SPEC-8: explicit navigation exit from 3D — only for views that represent intentional
        // navigation away (PRODUCT, OUTFIT, etc.). GRID/PRODUCT_GRID are background product loads
        // while staying in the 3D scene; they must NOT exit 3D.
        const SCENE_3D_PASSTHROUGH = ['GRID', 'PRODUCT_GRID', ''];
        if (incomingView && !SCENE_3D_PASSTHROUGH.includes(incomingView) && incomingView !== 'SCENE_3D' && viewRef.current === 'SCENE_3D') {
          console.log('[SPEC-8] handoff guard — exiting 3D for incomingView:', incomingView);
          handleSceneExit();
        }

        // 📸 Any new view that ISN'T the styled-look itself dismisses the composite overlay + spinner,
        // so the guest can move on by voice ("show me something else") without the image stuck on top.
        if (incomingView && incomingView !== 'STYLED_LOOK' && incomingView !== 'STYLED_VIDEO') {
          setStyledLook(null);
          setIsAnalysing(false);
          setStyledVideo(null);
          setIsAnimating(false);
        }

        if (incomingView === 'GRID' || incomingView === 'PRODUCT_GRID') {
          // 📸 Leaving the deep dive (incl. agent "close panels"): clear the gallery lock so the
          // next product opens with auto-scroll active.
          isGalleryLockedRef.current = false;
          const incomingProducts = msg.data
            ? (Array.isArray(msg.data?.results || msg.data) ? (msg.data?.results || msg.data) : [])
            : null;
          if (incomingProducts) { setProducts(incomingProducts); sendGridContext(incomingProducts); }
          // SPEC-8: in 3D mode, load products in background but don't leave the scene.
          // The handoff guard above handles intentional exits (agent explicitly sends a non-3D view).
          if (viewRef.current === 'OUTFIT' && incomingProducts && incomingProducts.length) {
            // Stay in OUTFIT layout — search results appear on right, anchor stays pinned left.
            setOutfitSearchOverride(incomingProducts);
          } else if (viewRef.current !== 'SCENE_3D') {
            setView('GRID');
            // Only clear the overlay if it's NOT a panel the user explicitly opened.
            // A background GRID search while cart/wishlist/privacy is open should not close it.
            setOverlay(prev => (prev === 'CART' || prev === 'WISHLIST' || prev === 'PRIVACY') ? prev : null);
            setDiscovery(null);
          }
          // else: SCENE_3D suppresses setView — stay in 3D scene
        } else if (incomingView === 'CART') {
          setOverlay('CART');
        } else if (incomingView === 'WISHLIST') {
          setOverlay('WISHLIST');
        } else if (incomingView === 'PRIVACY') {
          setOverlay('PRIVACY');
        } else if (incomingView === 'PRODUCT') {
          // 📸 Fresh deep dive: unlock the gallery so the storytelling auto-switcher can run again.
          console.log('[SPEC-8] PRODUCT ui_update → setView PRODUCT | viewRef:', viewRef.current);
          isGalleryLockedRef.current = false;
          lastAutoSwapRef.current = 0;
          setSelectedProduct(msg.data); setSelectedVariant(null); setCurrentImageIndex(0); setView('PRODUCT'); setOverlay(null); setDiscovery(null);
        } else if (incomingView === 'OUTFIT') {
          // 👔 Agent-assembled "Complete the Look" (from suggest_pairings) — render-only, voice-driven.
          console.log('[SPEC-8] OUTFIT ui_update → setView OUTFIT | viewRef:', viewRef.current);
          setOutfit({ anchor: msg.data?.anchor || null, pieces: msg.data?.pieces || [], occasion: msg.data?.occasion || '' });
          setOutfitSearchOverride(null);
          setView('OUTFIT'); setOverlay(null); setDiscovery(null);
        } else if (incomingView === 'BRAND_DISCOVERY') {
          // 🎬 Cinematic brand storytelling (from search_brand_knowledge)
          setDiscovery({ kind: 'brand', title: msg.data?.brand_name || brandName, text: msg.data?.knowledge || '', images: msg.data?.images || [] });
        } else if (incomingView === 'COLLECTION_SHOWCASE') {
          // 🎬 Immersive collection showcase (from explore_collection)
          setDiscovery({ kind: 'collection', title: msg.data?.collection_name || '', text: msg.data?.philosophy || '', images: msg.data?.images || [] });
        } else if (incomingView === 'STYLED_LOOK') {
          // 📸 Generated styled-look composite (from render_outfit_visual) — full-screen overlay.
          setStyledLook({ image: msg.data?.image || '', pieces: msg.data?.pieces || [], kind: msg.data?.kind || 'garment', occasion: msg.data?.occasion || '' });
          setIsAnalysing(false);
        } else if (incomingView === 'STYLED_VIDEO') {
          // 🎬 Generated Veo try-on film (from animate_current_look) — plays in the overlay.
          setStyledVideo({ video: msg.data?.video || '', kind: msg.data?.kind || 'look', occasion: msg.data?.occasion || '' });
          setIsAnimating(false);
        }

        // 📸 Styling spinner toggle (image generation is a long, silent tool call).
        // NOTE: we deliberately keep any existing composite ON SCREEN while a new one generates
        // (e.g. "show me another pose") — the guest keeps the current view and gets a subtle
        // "creating another view…" badge instead of a takeover spinner. The full-screen spinner
        // only shows for the FIRST generation (when no composite is up yet).
        if (msg.action === 'styling_look') {
          setIsAnalysing(!!msg.data?.active);
        } else if (msg.action === 'animating_look') {
          // 🎬 Veo render in flight (slow) — show a badge over the existing composite.
          setIsAnimating(!!msg.data?.active);
        } else if (msg.action === 'dismiss_styled_look') {
          // 🗣️ Voice-driven close ("close that", "take it away").
          setStyledLook(null);
          setIsAnalysing(false);
          setStyledVideo(null);
          setIsAnimating(false);
        } else if (msg.action === 'prompt_upload') {
          // 📸 Agent invited the guest to share their own piece → highlight the camera control.
          setUploadHint(true);
        } else if (msg.action === 'waitlist_form') {
          waitlist.handleServerMessage(msg.data); // SPEC-52: open/close/confirm, voice or manual
        } else if (msg.action === 'offer_checkout') {
          checkout.handleServerMessage(msg.data); // SPEC-46: real Stripe session, voice or the button
        }

        // 🗣️ Agent advisories (clarify / substitute) — subtle on-screen hint; the agent also speaks them.
        if (msg.action === 'CLARIFICATION_ADVISORY') {
          const dims = msg.data?.candidate_dimensions || [];
          setAdvisory(dims.length ? { kind: 'clarify', text: `Refine by: ${dims.slice(0, 4).join(' · ')}` } : null);
        } else if (msg.action === 'SUBSTITUTION_ADVISORY') {
          setAdvisory({ kind: 'substitute', text: 'Showing the closest alternatives' });
        }

        // 🔦 Catch the array of features (With LLM Hallucination Safety)
        if (msg.action === 'highlight') {
          let parsedFeatures = [];
          if (Array.isArray(msg.features)) {
            // If the LLM lazily sends ["Dial, Hands"] instead of ["Dial", "Hands"]
            msg.features.forEach(f => {
              if (typeof f === 'string') parsedFeatures.push(...f.split(',').map(s => s.trim().toLowerCase()));
            });
          } else if (typeof msg.features === 'string') {
            parsedFeatures = msg.features.split(',').map(s => s.trim().toLowerCase());
          }
          setHighlightedFeatures(parsedFeatures);
        }

        // Prefer the dispatched payload: the backend sends fully normalized cart/wishlist
        // envelopes (incl. line `quantity`); the catalog object only fills gaps. The old
        // `|| selectedProduct` fallback substituted whichever piece was last TAPPED whenever the
        // id wasn't in `products` — and reel mode never populates `products`, so one tap on any
        // panel tile made every later add resolve to the wrong piece while the agent said "added".
        const payloadItem = msg.data ? { ...(products.find(p => p.id === msg.data.id) || {}), ...msg.data } : null;
        if (msg.action === 'pin_item' && payloadItem) setPinnedItems(prev => prev.find(p => p.id === payloadItem.id) ? prev : [...prev, payloadItem]);
        if (msg.action === 'unpin_item') setPinnedItems(prev => msg.data?.id ? prev.filter(p => p.id !== msg.data.id) : []);
        if (msg.action === 'add_to_cart' && payloadItem) {
          // Upsert by id: the envelope may carry a new line `quantity`; the old keep-the-stale-
          // object dedupe silently discarded it (duplicate rows were never correct either way).
          setCartItems(prev => prev.some(p => p.id === payloadItem.id) ? prev.map(p => (p.id === payloadItem.id ? payloadItem : p)) : [...prev, payloadItem]);
          triggerReaction('cart');
        }
        if (msg.action === 'add_to_wishlist' && payloadItem) { setWishlistItems(prev => prev.find(p => p.id === payloadItem.id) ? prev : [...prev, payloadItem]); triggerReaction('wishlist'); }
        if (msg.action === 'remove_from_wishlist') setWishlistItems(prev => msg.data?.id ? prev.filter(p => p.id !== msg.data.id) : prev);
        if (msg.action === 'remove_from_cart') setCartItems(prev => msg.data?.id ? prev.filter(p => p.id !== msg.data.id) : []);
        // A line's count changed without the row set changing (backend `update_cart_quantity`:
        // "add two more of those" / "take one of them out" on a multi-quantity line).
        if (msg.action === 'update_cart_quantity' && msg.data?.id) {
          setCartItems(prev => prev.map(p => (p.id === msg.data.id ? { ...p, quantity: msg.data.quantity } : p)));
        }
        // The agent scrolling an already-open panel (IconRail/MobileChrome), distinct from opening
        // it — `view: 'CART'|'WISHLIST'` owns that. `data.panel`/`data.direction` come straight from
        // the backend's scroll_panel tool.
        if (msg.action === 'scroll_panel' && msg.data?.panel) {
          setScrollCommand({ panel: msg.data.panel, direction: msg.data.direction === 'up' ? 'up' : 'down', nonce: scrollCommandNonceRef.current++ });
        }
        // Agent-driven close, targeted by panel — distinct from `view: null`, which would be
        // ambiguous about which open panel it meant to clear (privacy_panel tool, backend).
        if (msg.action === 'close_panel' && msg.data?.panel) {
          setOverlay(prev => (prev === msg.data.panel ? null : prev));
        }

      } else if (msg.type === 'ui_update' && msg.bulk_result) {
        const { cart, wishlist, pinned } = msg.bulk_result;
        let cartHasItems = -1, wishlistHasItems = -1;
        // Bulk adds UPSERT by id (they may carry a merged line quantity — e.g. a wishlist→bag move
        // of a piece already in the bag); the old filter kept the stale object and dropped it.
        const upsertById = (prevArr, removeList = [], addList = []) => {
          const kept = prevArr.filter(item => !removeList.some(r => r.id === item.id));
          const byId = new Map(addList.map(a => [a.id, a]));
          return [
            ...kept.map(item => (byId.has(item.id) ? byId.get(item.id) : item)),
            ...addList.filter(a => !kept.some(item => item.id === a.id)),
          ];
        };
        if (cart && (cart.add?.length > 0 || cart.remove?.length > 0)) {
          setCartItems(prev => upsertById(prev, cart.remove, cart.add));
          cartHasItems = cart.add?.length > 0 ? 1 : cart.remove?.length > 0 ? 0 : -1;
          if (cart.add?.length > 0) triggerReaction('cart');
        }
        if (wishlist && (wishlist.add?.length > 0 || wishlist.remove?.length > 0)) {
          setWishlistItems(prev => upsertById(prev, wishlist.remove, wishlist.add));
          wishlistHasItems = wishlist.add?.length > 0 ? 1 : wishlist.remove?.length > 0 ? 0 : -1;
          if (wishlist.add?.length > 0) triggerReaction('wishlist');
        }
        if (pinned && (pinned.add?.length > 0 || pinned.remove?.length > 0)) {
          setPinnedItems(prev => [...prev.filter(item => !pinned.remove.some(r => r.id === item.id)), ...pinned.add.filter(item => !prev.some(n => n.id === item.id))]);
        }
        if (cartHasItems >= wishlistHasItems && cartHasItems > -1) { setOverlay('CART'); }
        else if (wishlistHasItems > -1) { setOverlay('WISHLIST'); }
        else setOverlay(null);
      }
    });
  };

  // SPEC-C1: a compact, ORDERED snapshot of what the guest is currently looking at, so the backend
  // can resolve deictic references ("the third one", "this colour", "that blue blazer"). Order is
  // preserved exactly as rendered (products.map). Kept tiny (id + title + position) — no payloads.
  const buildVisibleGrid = (list) =>
    (Array.isArray(list) ? list : [])
      .filter(Boolean)
      .map((p, idx) => ({ id: p.id, title: p.title, grid_index: idx }));

  const sendGridContext = (list) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: 'grid_context', visible_grid: buildVisibleGrid(list) }));
    }
  };

  const sendInteraction = (action, product, variant = null, extra = null, bypassRelease = false) => {
    // no_enter_gate returning landing: an action that needs the agent starts the conversation and is sent right behind the welcome
    // trigger (owner 2026-10-08) - a no-op (false) once entered and on every flag-off tenant.
    if (!bypassRelease && introX.landing.isWaiting()) {
      if (isScreenMove(action)) {
        if (introX.landing.intercept(() => sendInteraction(action, product, variant, extra, true))) return;
      } else {
        // A bag / wishlist edit on the landing never starts the agent (owner rule): it rides the early socket, if any, SILENTLY
        // (agent_speaking: the backend's own "move the screen, no spoken beat" contract) and is otherwise dropped, as before.
        extra = { ...(extra || {}), agent_speaking: true }; // eslint-disable-line no-param-reassign
        bypassRelease = true; // eslint-disable-line no-param-reassign
      }
    }
    // SPEC-82 Rev 3 (bug 2, owner 2026-10-06): pre-tap, this request IS the tap — the welcome is released; a screen move is sent at
    // once, silently (`silent` = agent_speaking: true, the backend's own contract), anything else is re-sent through here once the
    // welcome is over. False whenever nothing is held (every flag-off tenant).
    if (!bypassRelease && implicitRelease.intercept(action, (silent) => sendInteraction(
      action, product, variant, silent ? { ...(extra || {}), agent_speaking: true } : extra, !!silent,
    ))) return;
    if (ws.current?.readyState === WebSocket.OPEN) {
      // 🔇 Owner report 2026-09-15: a variant or chapter chosen BY HAND while the agent is
      // mid-sentence must move the screen SILENTLY — the backend drops the spoken beat instead of
      // speaking it the moment the audio already queued here runs out. Only WE can answer that:
      // the backend gates on `turn_idle`, which tracks Gemini's GENERATION and flickers idle in
      // the middle of one answer (native audio emits a turn_complete boundary ~1s before its
      // first audio chunk — measured 1.55s vs 2.51s). `isAgentSpeakingRef` is true from the first
      // PCM chunk until the playout queue drains (handleAudioPlayback), and the cursor below is
      // that queue's ground truth for the sliver before the drain timer fires. Silent is only the
      // VOICE: every other field still rides along, so the agent knows what the guest is looking
      // at and "tell me about this one" still resolves. Agent idle at tap time -> it still speaks
      // up, exactly as the 2D deep-dive always has.
      //
      // 🔁 REGRESSION FIX (owner, 2026-09-15, same day): the first cut of this answered
      // `isAgentSpeakingRef.current || queuedSpeechS > 0.05`, and BOTH of those can stick on
      // "speaking" — after which the backend dropped the beat for every tap of the session, agent
      // silent or not, which is precisely what the owner hit on mobile. The flag has no timer left
      // to lower it if anything throws between the `clearTimeout` and the re-arm in
      // `handleAudioPlayback`; the queue cursor is on the AudioContext clock, which freezes with the
      // context. Neither self-heals, and OR meant either one alone could hold the gate shut.
      // So: AND, against a WALL-clock deadline that is over the moment it passes
      // (state/agentPlayoutDeadline.js). Every existing teardown path already lowers the flag, and
      // the deadline now bounds it when none of them runs. In a healthy turn nothing changes — the
      // drain timer lowers the flag ~200ms after the queue ends, the same grace the deadline uses.
      const msg = {
        type: 'interaction', action: action, product_name: product.title, product_id: product.id,
        // SPEC-C1: ride the current grid + focus pointer along so "this / the third one" resolves.
        visible_grid: buildVisibleGrid(products),
        focused_product_id: selectedProduct?.id ?? null,
        agent_speaking: isAgentSpeakingRef.current === true
          && isWithinPlayout(speechEndsAtRef.current, Date.now()),
      };
      if (variant) msg.variant = variant;
      if (extra) Object.assign(msg, extra); // SPEC-57: chapter_id / collection_key, D-2
      ws.current.send(JSON.stringify(msg));
    }
  };

  // CollectionPieceCard's explicit close (SPEC-53). The card's own comment records an "owner
  // ruling" that closing it back to the carousel was voice-only — deliberately overridden here
  // (client request) with an explicit X, but the destination is exactly the `close_piece`
  // transition the backend already drives by voice (stageReducer.js), not a new state: PIECE ->
  // COLLECTION, never all the way back to the reel (that is `onBackToReel`'s job, a different
  // button). Also sends the one-line notice the `overlay` effect above already sends for the
  // cart/wishlist/privacy panels — the agent has no other way to learn the guest dismissed this
  // deep dive by hand rather than by voice.
  const handleCloseCollectionPiece = () => {
    stageMachine.handleStageCommand({
      action: 'close_piece', collection_key: stageMachine.state.collectionKey,
    });
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(JSON.stringify({ type: 'interaction', action: 'closed product panel' }));
    }
  };

  // Keeps the backend's per-panel "is it open" awareness accurate regardless of who last changed
  // it — a manual tap on a rail icon, the generic drawer's close button, or the agent's own
  // view_cart/view_wishlist/privacy_panel/close_* tool calls. Fires once per panel's enter/exit
  // transition, not per render, for all three panels the single `overlay` slot can hold.
  const prevOverlayRef = useRef(overlay);
  useEffect(() => {
    const prev = prevOverlayRef.current;
    if (prev !== overlay && ws.current?.readyState === WebSocket.OPEN) {
      const panelLabel = { CART: 'cart', WISHLIST: 'wishlist', PRIVACY: 'privacy' };
      if (prev && panelLabel[prev]) {
        ws.current.send(JSON.stringify({ type: 'interaction', action: `closed ${panelLabel[prev]} panel` }));
      }
      if (overlay && panelLabel[overlay]) {
        ws.current.send(JSON.stringify({ type: 'interaction', action: `opened ${panelLabel[overlay]} panel` }));
      }
    }
    prevOverlayRef.current = overlay;
  }, [overlay]);

  const handleTextSubmit = (e) => {
    e.preventDefault();
    // no_enter_gate returning landing: sending a message starts the conversation; the text goes right behind the welcome trigger.
    const heldText = textInput.trim();
    if (heldText && introX.landing.intercept(() => ws.current?.send(JSON.stringify({ type: 'text', text: heldText })))) {
      transcriptStore.push({ sender: 'user', text: heldText });
      setTextInput('');
      return;
    }
    if (!textInput.trim() || !ws.current || ws.current.readyState !== WebSocket.OPEN)
      return;

    // 🛑 Sending a message auto-stops a speaking agent — the guest has plainly moved on, and making
    // them press stop first is a step nobody would think to take. It does NOT engage the discard
    // gate (the reply is due in a second or two and would be indistinguishable from the tail), and
    // it does not lift an engaged one either — that ends on the stopped turn's own `turnComplete`
    // (the backend serialises turns, so it lands ahead of this message's reply) or, when the stop
    // caught a turn that had already finished, on the short DISCARD_TAIL_MS deadline.
    manualStop.interruptForGuestTurn();

    ws.current.send(JSON.stringify({ type: 'text', text: textInput }));
    transcriptStore.push({ sender: 'user', text: textInput });
    setTextInput('');
    //setIsKeyboardOpen(false); 
    setAgentState('thinking');
  };

  // 📸 SPEC-5/6: upload a garment/watch photo. Read as base64 JPEG, send over the existing WS as a
  // one-shot message; the backend feeds it into the live session (Yris sees it) and builds a look.
  const handleImageUpload = (e, kind = 'garment') => {
    const file = e.target?.files?.[0];
    if (e.target) e.target.value = ''; // allow re-uploading the same file
    if (!file || !ws.current || ws.current.readyState !== WebSocket.OPEN) return;

    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result || '';
      const base64 = String(result).split(',')[1] || ''; // strip the data: URI prefix
      if (!base64) return;
      ws.current.send(JSON.stringify({ type: 'image_upload', mime_type: 'image/jpeg', data: base64, kind }));
      // The backend brings the guest's photo into focus as the look anchor (no corner widget).
      setUploadHint(false);
      transcriptStore.push({ sender: 'user', text: `(shared a ${kind} photo)` });
      setAgentState('thinking');
    };
    reader.readAsDataURL(file);
  };

  const handleCardClick = (item) => {
    // 📸 Fresh deep dive: unlock the gallery so the auto-scroll storytelling cycle runs again.
    isGalleryLockedRef.current = false;
    lastAutoSwapRef.current = 0;
    setSelectedProduct(item); setSelectedVariant(null); setCurrentImageIndex(0); setView('PRODUCT'); setOverlay(null);
    if (!isAgentSpeakingRef.current) sendInteraction('viewed', item);
  };

  const handleToggleWishlist = (item) => {
    const isWished = wishlistItems.find(p => p.id === item.id);
    if (isWished) {
      setWishlistItems(prev => prev.filter(p => p.id !== item.id));
      sendInteraction('removed from wishlist', item);
    } else {
      setWishlistItems(prev => [...prev, item]);
      sendInteraction('added to wishlist', item);
    }
  };

  const handleAddToCart = (item, variant = null) => {
    // SPEC-52 R1: a Vesper never enters the cart, manual UI included — the same guard as the four
    // backend cart paths, at this frontend's own single funnel (surfaces A-D all call this).
    if (item?.waitlist) { waitlist.openFromAdd(item); return; }
    const entry = variant ? { ...item, selectedSize: variant } : item;
    setCartItems(prev => prev.find(p => p.id === item.id && p.selectedSize === entry.selectedSize) ? prev : [...prev, entry]);
    sendInteraction('added to cart', item, variant);
  };

  const handleRemoveFromCart = (item) => {
    setCartItems(prev => prev.filter(p => p.id !== item.id));
    sendInteraction('removed from cart', item);
  };

  // Reel cart ✕ button: optimistic local removal + silent server sync. `remove_cart_line` is a
  // dedicated interaction action the backend mirrors into the session WITHOUT a model turn — the
  // guest just watched the row go; the agent narrating it would be the double-response class.
  const handleRemoveCartLine = (item) => {
    setCartItems(prev => prev.filter(p => p.id !== item.id));
    sendInteraction('remove_cart_line', item);
  };

  // Reel cart − / + buttons. Same silent contract as the ✕ above: the guest just watched the count
  // change, so the backend mirrors it into the session WITHOUT a model turn, and the agent only
  // learns about it the next time it reads the bag. Clamped to [1, MAX_LINE_QUANTITY] on this side
  // too — `selection_ops` clamps identically, and a row showing a count the server refused would
  // make "what's in my bag?" contradict the panel.
  const handleIncrementCartLine = (item) => {
    setCartItems(prev => prev.map(p => (
      p.id === item.id
        ? { ...p, quantity: Math.min(itemQuantity(p) + 1, MAX_LINE_QUANTITY) }
        : p
    )));
    sendInteraction('increment_cart_line', item);
  };

  // Never removes the line: the ✕ owns that, and the button is disabled at 1 (see RowActions).
  const handleDecrementCartLine = (item) => {
    setCartItems(prev => prev.map(p => (
      p.id === item.id
        ? { ...p, quantity: Math.max(itemQuantity(p) - 1, 1) }
        : p
    )));
    sendInteraction('decrement_cart_line', item);
  };

  const handleManualUnpin = (item) => {
    setPinnedItems(prev => prev.filter(p => p.id !== item.id));
    sendInteraction('unpinned', item);
  };

  const handleAudioPlayback = (arrayBuffer) => {
    // 🎬 Hard guarantee: the agent is NEVER heard over the intro film. The model is told to stay
    // silent during its (silent) grid-fetch turn, but native audio occasionally ignores that and
    // emits a stray welcome. While the film is on screen we simply drop any agent audio — the real
    // welcome is driven later by `intro_finished` (sent when the film ends), so nothing legit is lost.
    if (showIntroRef.current) { filmTailGateRef.current.noteDroppedUnderFilm(); return; }
    // BUG FIX (2026-09-08, "the welcome cuts off mid-sentence and then resumes"): the guard above is
    // per CHUNK, so it stopped protecting the moment the film ended — and a stray turn still
    // generating at that moment had its whole remaining tail played, starting mid-sentence, before
    // the intro_finished injection cut it off in favour of the real welcome. Once a chunk has been
    // dropped for the film, the REST OF THAT TURN goes with it (state/filmAudioGate.js).
    if (filmTailGateRef.current.shouldDropTail()) return;

    // 🛑 Manual-stop guard. Must be ABOVE the flags below: setting them is the first thing this
    // function does, so a chunk still in flight when the guest pressed stop would re-arm
    // `isAgentSpeakingRef` and snap the cluster back to 'speaking' the instant after the flush.
    if (manualStop.shouldDiscardAgentOutput()) return;

    isAgentSpeakingRef.current = true;
    setIsAgentSpeaking(true);
    setAgentState('speaking');
    // Agent is now speaking → isAgentSpeaking alone drives the 'speaking' animation.
    // Drop any stale user-speaking flag so mic bleed from the agent's own voice can't
    // latch the square on after the user stopped (closes the VAD echo-loop window).
    setIsUserSpeaking(false);

    if (speakingTimeoutRef.current)
      clearTimeout(speakingTimeoutRef.current);

    const ctx = audioContext.current;
    if (ctx.state === 'suspended')
      ctx.resume();

    const int16Data = new Int16Array(arrayBuffer);
    const float32Data = new Float32Array(int16Data.length);
    for (let i = 0; i < int16Data.length; i++)
      float32Data[i] = int16Data[i] / 32768.0;

    const buffer = ctx.createBuffer(1, float32Data.length, AUDIO_SAMPLE_RATE);
    buffer.getChannelData(0).set(float32Data);

    const source = ctx.createBufferSource();
    source.buffer = buffer;
    source.connect(agentAnalyser.current);
    activeAudioSourcesRef.current.push(source);
    source.onended = () => {
      activeAudioSourcesRef.current = activeAudioSourcesRef.current.filter(s => s !== source);
    };

    const currentTime = ctx.currentTime;
    // When the queue has drained (fresh utterance), start a hair in the future so a brief main-thread
    // stall can't underrun the first chunk. Mid-sentence chunks keep scheduling off nextStartTime, so
    // this adds no cumulative latency.
    const queueWasDry = nextStartTime.current < currentTime;
    const startTime = queueWasDry ? currentTime + PLAYBACK_LOOKAHEAD : nextStartTime.current;
    source.start(startTime);
    nextStartTime.current = startTime + buffer.duration;
    // 🕰️ Shadow the line above in wall clock. `nextStartTime` rides the AudioContext clock, which
    // stops dead while the context is suspended (mobile backgrounding / screen lock / an iOS audio
    // interruption) and is only ever resumed by the next chunk to reach this function — so on its
    // own it cannot tell "still speaking" from "frozen mid-answer". This one always runs out.
    // `queueWasDry` is the same branch the line above takes, so a cancelled answer (barge-in,
    // stop_audio — both rewind `nextStartTime` to now) starts this deadline over too, instead of
    // stacking the next utterance on a tail nobody will ever hear.
    speechEndsAtRef.current = extendPlayoutDeadline(
      queueWasDry ? 0 : speechEndsAtRef.current, Date.now(), buffer.duration, PLAYBACK_LOOKAHEAD,
    );
    // 💬 The CC pair reveals the agent's transcript against this queue rather than against the
    // socket, which delivers the whole answer's text long before the voice reaches the end of it.
    scheduleSpeech(ctx, startTime, buffer.duration);

    speakingTimeoutRef.current = setTimeout(() => {
      isAgentSpeakingRef.current = false;
      resetSpeechClock(); // 💬 utterance over — the next one starts its own reveal from zero
      setIsAgentSpeaking(false);
      setIsUserSpeaking(false); // 🎙️ agent done → return to listening (circle), not stuck on speaking
      setAgentState('listening');
      setHighlightedFeatures([]);
      setLiveAgentText('');
      highlightedThisTurn.current.clear();
      // 📸 Keep the gallery lock intact when the agent stops speaking — see stop_audio note above.
      setActiveLoreImage(null);
    }, ((nextStartTime.current - ctx.currentTime) * 1000) + 200);
  };

  const floatTo16BitPCM = (float32Array) => {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
      let s = Math.max(-1, Math.min(1, float32Array[i]));
      view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    return buffer;
  };

  // Quantity-aware bag arithmetic (utils/cartMath mirrors backend selection_ops semantics).
  const cartTotal = cartTotalAmount(cartItems);
  const cartUnits = cartUnitCount(cartItems);
  // A row's price column: untouched pretty display for a single unit; computed line total (unit ×
  // quantity, same format as the subtotal) once the line holds multiples.
  const linePriceDisplay = (item) => (
    itemQuantity(item) > 1 ? formatPrice(lineTotalAmount(item), brandConfig.currency) : priceDisplay(item)
  );

  const productImages = selectedProduct?.images?.length > 0
    ? selectedProduct.images
    : [selectedProduct?.image_url || selectedProduct?.image_uri].filter(Boolean);
  // Mirror into a ref so the WebSocket signal handler (which closes over an older render) can read
  // the current photo count for relative `gallery_navigate` ("next"/"previous") without going stale.
  productImagesRef.current = productImages;

  const handleNextImage = (e) => {
    isGalleryLockedRef.current = true;
    e.stopPropagation(); setCurrentImageIndex((prev) => (prev + 1) % productImages.length);
  };
  const handlePrevImage = (e) => {
    isGalleryLockedRef.current = true;
    e.stopPropagation(); setCurrentImageIndex((prev) => (prev - 1 + productImages.length) % productImages.length);
  };

  if (isCheckingAuth) {
    // Deliberately NOT the branded <Loader> here: reel_mode isn't known until /api/config
    // resolves, so the OrientationGate (SPEC-26 Phase 5) cannot arm yet — and its contract is
    // that no unlocked frame ever paints on a sideways handset (App.OrientationLock.test.js
    // "boot loader beat is already locked"). A role="status" loader in this pre-config window
    // would be exactly such a frame. This blank beat is one config round-trip; the pathological
    // blank-FOREVER case is closed by the AbortSignal timeout on the config fetch above.
    return <div className="fixed inset-0 w-full h-[100dvh]" style={backgroundStyle} />;
  }

  if (!isAuthenticated) {
    return (
      <div className="fixed inset-0 w-full h-[100dvh] text-neutral-900 flex flex-col items-center justify-center font-sans selection:bg-black/10" style={backgroundStyle}>
        <div className="relative z-10 flex flex-col items-center bg-white/80 backdrop-blur-2xl p-10 rounded-[2rem] border border-black/10 shadow-2xl w-[90%] max-w-[400px]">

          {authStep === 2 && (
            <button
              onClick={() => { setAuthStep(1); setAuthOtp(''); setErrorMessage(''); }}
              className="absolute top-8 left-8 p-2 text-black/40 hover:text-black transition-colors"
            >
              <ChevronLeft size={20} />
            </button>
          )}

          <img src="/versailogo.png" alt="Versai Logo" className="h-8 mb-8 object-contain opacity-80" />
          <h2 className="text-sm font-semibold tracking-[0.2em] uppercase mb-8 text-black/80 text-center">Secure Access</h2>

          {authStep === 1 ? (
            <>
              <input
                type="email"
                placeholder="Enter your corporate email"
                className={`w-full text-sm font-light border-b ${errorMessage ? 'border-red-400' : 'border-black/20'} bg-transparent px-2 py-3 mb-4 focus:outline-none focus:border-black transition-colors text-center placeholder:text-black/30`}
                value={authEmail}
                onChange={(e) => setAuthEmail(e.target.value)}
              />

              <div className="h-6 mb-2 flex items-center justify-center">
                {errorMessage && <p className="text-[10px] text-red-500 uppercase tracking-tighter font-semibold text-center">{errorMessage}</p>}
              </div>

              <button onClick={handleSendEmail} disabled={!authEmail || isResending} className="w-full py-4 bg-black text-white rounded-full text-[10px] font-medium tracking-widest uppercase hover:bg-neutral-800 transition-colors disabled:opacity-50 shadow-md">
                {isResending ? 'Validating...' : 'Send Login Code'}
              </button>
            </>
          ) : (
            <>
              {/* Neutral by necessity: the backend will not say whether this address is
                  authorised, so claiming "Code sent" would be an assertion the UI cannot make.
                  Naming the address and the spam folder is what actually helps the guest who
                  mistyped, which is the likely case for a code that never arrives. */}
              <p className="text-xs text-neutral-500 mb-6 text-center font-light px-4">If <strong>{authEmail}</strong> is authorised, a code is on its way. Check your spam folder, or go back to correct the address.</p>

              <div className="h-4 mb-2 flex items-center justify-center">
                {errorMessage && <p className="text-[10px] text-red-500 uppercase tracking-tighter font-semibold text-center">{errorMessage}</p>}
              </div>

              <input
                type="text"
                placeholder="------"
                className={`w-full text-2xl tracking-[0.5em] font-light border-b ${errorMessage ? 'border-red-400' : 'border-black/20'} bg-transparent px-2 py-3 mb-8 focus:outline-none focus:border-black transition-colors text-center placeholder:text-black/20`}
                value={authOtp}
                onChange={(e) => setAuthOtp(e.target.value)}
                maxLength={6}
              />
              <button onClick={handleVerifyOtp} disabled={authOtp.length < 6} className="w-full py-4 bg-black text-white rounded-full text-[10px] font-medium tracking-widest uppercase hover:bg-neutral-800 transition-colors mb-6 disabled:opacity-50 shadow-md">
                Verify & Access
              </button>

              <button
                onClick={handleSendEmail}
                disabled={isResending}
                className="text-[10px] font-semibold uppercase tracking-widest text-black/40 hover:text-black transition-colors disabled:opacity-30"
              >
                {isResending ? 'Sending...' : "Didn't receive it? Resend"}
              </button>
            </>
          )}
        </div>
      </div>
    );
  }

  // SPEC-26 Phase 5 — orientation lock (client requirement, 2026-08-13). A phone is held to
  // portrait and a tablet to landscape, so each sees the composition authored for its shape.
  // Mounted in every reel-mode branch, the boot beats included: a device held the wrong way at load
  // has to be asked to turn round before it is shown a composition that was never drawn for that
  // shape. It renders null unless it is actually blocking, so flag-off tenants and every desktop
  // client keep the markup they had (AC-9). Declared here rather than repeated inline because there
  // are three exits below.
  const orientationLock = <OrientationGate enabled={!!brandConfig.feature_flags?.reel_mode} />;

  // SPEC-26 Phase 4: the Ophion boot beat (Figma 45:1314). Placed after the auth gate so a tenant
  // that does require OTP never has its login form hidden behind the loader.
  if (brandConfig.feature_flags?.reel_mode && showBootLoader) {
    return (
      <>
        <Loader brandName={brandName} />
        {orientationLock}
      </>
    );
  }

  if (!inBoutique) {
    // SPEC-26 Phase 4: reel-mode tenants get the Figma Enter Boutique gate (48:5551) — a
    // consent/gesture screen that unlocks audio autoplay, NOT authentication (the email/OTP
    // Login frame 45:1316 is explicitly out of scope). ConsentGate + PrivacyPolicy render here
    // too: requestEnter() opens the consent gate when a decision is still pending, so dropping
    // them would leave the button silently doing nothing. Every other tenant falls through to
    // the generic welcome screen below, unchanged (AC-9).
    if (brandConfig.feature_flags?.reel_mode) {
      return (
        <>
          <EnterBoutiqueGate
            onEnter={requestEnter}
            brandName={brandName}
            // SPEC-76: always visible, first visit or return (owner 2026-09-29); per-tenant data, absent elsewhere.
            primer={tutorialCopy && gateHint.primerVisible ? <TutorialPrimer copy={tutorialCopy} extraReserve={gateHint.extraReserve} id={gateHint.primerId} /> : null}
            afterButton={gateHint.afterButton} // SPEC-82
          />
          <ConsentGate reelChrome open={consentGateOpen} onSave={handleConsentSave} onOpenPrivacy={() => setPrivacyOpen(true)} />
          <PrivacyPolicy reelChrome open={privacyOpen} onClose={() => setPrivacyOpen(false)} brandName={brandName} brandContact={brandConfig.privacy_contact || ''} />
          {orientationLock}
        </>
      );
    }
    return (
      <div className="fixed inset-0 w-full h-[100dvh] text-neutral-900 flex flex-col items-center justify-center font-sans selection:bg-black/10" style={backgroundStyle}>
        <div className="relative z-10 flex flex-col items-center">
          <img src="/versailogo.png" alt="Versai Logo" className="h-12 md:h-16 mb-8 object-contain drop-shadow-sm" />
          <p className="text-black/80 tracking-widest text-base font-semibold mb-12 uppercase text-center px-4 drop-shadow-sm">{brandName} Private Virtual Salon</p>
          <button onClick={requestEnter} className="px-10 py-4 border border-black/20 rounded-full hover:bg-black hover:text-white transition-all duration-500 tracking-widest uppercase text-sm font-medium bg-white/50 backdrop-blur-sm">
            Enter Boutique
          </button>
        </div>
        <ConsentGate open={consentGateOpen} onSave={handleConsentSave} onOpenPrivacy={() => setPrivacyOpen(true)} />
        <PrivacyPolicy open={privacyOpen} onClose={() => setPrivacyOpen(false)} brandName={brandName} brandContact={brandConfig.privacy_contact || ''} />
      </div>
    )
  }

  const inWishlist = selectedProduct ? wishlistItems.some(i => i.id === selectedProduct.id) : false;

  // SPEC-26 Phase 4: reel-mode chrome palette, derived once. The alternative — a ternary inline on
  // each of ~30 classNames — would be unreviewable and easy to get subtly wrong. Every `false` arm
  // below is the exact string that shipped before this phase, so flag-off tenants (Baume/Huntsman)
  // render byte-identical markup (AC-9).
  const isReelChrome = !!brandConfig.feature_flags?.reel_mode;
  const ui = {
    drawer: isReelChrome
      ? 'bg-reel-chrome-panel backdrop-blur-reel-panelDense'
      : 'bg-white/95 backdrop-blur-3xl',
    drawerEdge: isReelChrome ? 'border-white/10' : 'border-black/10',
    drawerHeadEdge: isReelChrome ? 'border-white/10' : 'border-black/5',
    drawerTitle: isReelChrome ? 'text-reel-chrome-textPrimary' : 'text-neutral-900',
    closeBtn: isReelChrome
      ? 'bg-white/10 hover:bg-white/20 border border-white/20'
      : 'bg-black/5 hover:bg-black/10 border border-black/10',
    closeIcon: isReelChrome ? 'text-reel-chrome-textPrimary' : 'text-neutral-800',
    emptyText: isReelChrome ? 'text-reel-chrome-textDim' : 'text-neutral-400',
    itemCard: isReelChrome
      ? 'bg-white/5 border border-white/10'
      : 'bg-white border border-black/5 shadow-sm',
    itemThumb: isReelChrome ? 'bg-white/5' : 'bg-neutral-50',
    itemTitle: isReelChrome ? 'text-reel-chrome-textPrimary' : 'text-neutral-900',
    itemPrice: isReelChrome ? 'text-reel-chrome-textDim' : 'text-neutral-600',
    // Separate from itemPrice: the size value shipped a shade darker than the price line
    // (neutral-700 vs -600) and AC-9 requires the flag-off arm to stay byte-identical.
    sizeValue: isReelChrome ? 'text-reel-chrome-textPrimary' : 'text-neutral-700',
    itemPriceAlt: isReelChrome ? 'text-reel-chrome-textDim' : 'text-neutral-500',
    totalLabel: isReelChrome ? 'text-reel-chrome-textDim' : 'text-neutral-500',
    totalValue: isReelChrome ? 'text-reel-chrome-textPrimary' : 'text-neutral-900',
    footerBand: isReelChrome ? 'border-white/10 bg-white/5' : 'border-black/10 bg-white',
    checkoutBtn: isReelChrome
      ? 'bg-reel-cta-primaryBg text-black hover:bg-white'
      : 'bg-black text-white hover:bg-neutral-800',
    addBtn: isReelChrome
      ? 'bg-white/10 hover:bg-white/20 text-reel-chrome-textPrimary'
      : 'bg-black/5 hover:bg-black/10 text-neutral-900',
    removeBtn: isReelChrome ? 'text-reel-chrome-textDim hover:text-white' : 'text-neutral-400 hover:text-neutral-600',
    // Footer control pill
    pill: isReelChrome
      ? 'bg-reel-chrome-panel backdrop-blur-reel-chrome border border-white/10'
      : 'bg-white/90 backdrop-blur-xl border border-black/10',
    pillInput: isReelChrome
      ? 'text-reel-chrome-textPrimary placeholder:text-white/40'
      : 'text-neutral-900 placeholder:text-neutral-400',
    pillIconBtn: isReelChrome ? 'text-reel-chrome-textDim hover:bg-white/10' : 'text-neutral-600 hover:bg-black/5',
    micOn: isReelChrome ? 'bg-reel-cta-primaryBg text-black shadow-md' : 'bg-black text-white shadow-md',
    micOff: isReelChrome ? 'bg-white/10 text-white/60 hover:bg-white/20' : 'bg-black/5 text-black/60 hover:bg-black/10',
    ccOn: isReelChrome ? 'text-reel-chrome-textPrimary bg-white/10' : 'text-neutral-900 bg-black/5',
    ccOff: isReelChrome ? 'text-white/40 hover:bg-white/10' : 'text-neutral-400 hover:bg-black/5',
    pillDivider: isReelChrome ? 'border-white/10' : 'border-black/10',
    pillStatus: isReelChrome ? 'text-white/50' : 'text-neutral-500',
    pillStatusStrong: isReelChrome ? 'text-reel-chrome-textPrimary' : 'text-neutral-800',
    pillSendBtn: isReelChrome ? 'bg-reel-cta-primaryBg text-black' : 'bg-black text-white',
    pillCloseBtn: isReelChrome
      ? 'bg-white/10 hover:bg-white/20 text-white/80'
      : 'bg-black/5 hover:bg-black/10 text-neutral-700',
    // ParticlePill renders to canvas, so its dot colour is a value, not a class.
    pillDotColor: isReelChrome ? '#f3f0e9' : '#111111',
  };

  // Sizing the Cavalry canvas to the authored 140-unit cluster slot renders the art far too small,
  // because the dot field does not fill its comp: the scene is 1920x1080 (read back from
  // player.getSceneResolution) and the field is centred with a wide transparent margin either side.
  // On a 140-unit-wide 16:9 canvas that leaves only ~79 units of height, so the art reads at well
  // under half of Figma's 139.69 (node 45:1147). The canvas therefore has to overhang its slot.
  //
  // The multiplier is measured, not derived: the group transforms above the "Circle 4" distribution
  // (circleDistribution#90 in V011, #70 before it renumbered) mean its authored radius does not
  // predict the on-screen extent, so it was calibrated against a screenshot (at overscan 3 the
  // field measured ~195 units wide; 3 * 140/195 ~= 2.15), then taken down a further 0.5 on JC's eye
  // at 2.15. Re-measure if the art team re-exports with a different comp size or field scale — the
  // value is tuned, not computed. The extra width is transparent comp background and `screen` makes
  // transparent a no-op, so the overhang paints nothing over the caption columns either side.
  //
  // The 2026-08-28 V011 re-export did NOT need a re-measure: comp resolution, every distribution
  // radius (70 / 308 / 437) and every grid (7x3 at 38x38) came across byte-identical, so the
  // on-screen extent is unchanged. V011 only adds shapes (Error / Share / Idle - Waiting).
  //
  // Taken down again 1.65 -> 1.0 on JC's eye (2026-08-25: the cluster read as too large in the
  // bar). 1.0 is the natural landmark — the canvas now exactly fills its authored 140-unit slot
  // with no overhang at all, ~39% narrower than before. This is the single knob for BOTH
  // compositions (ReelFooter and MobileFooter share `footerVisualizer`): nudge it down toward 0.8
  // for smaller, back up toward 1.65 for the previous size. Below ~0.7 the dot field starts to
  // lose legibility at mobile widths.
  const CAVALRY_COMP_OVERSCAN = 1.45;

  // Reel footer's compact voice indicator: cavalry (Cavalry avatar) / pill (particle dots) /
  // anything else falls back to the raw voice-volume Visualizer. Shared by MobileFooter and
  // ReelFooter so both compositions stay in sync with a single visualizer_mode switch.
  const visualizerMode = brandConfig.visualizer_mode || DEFAULT_VISUALIZER_MODE;

  // The voice state every visualizer reads. Single source so the reel footer's cluster and the
  // legacy full-width overlay below can never disagree about what the concierge is doing. The
  // precedence rules and the mute rule live in voiceVisualizerState.js, with their tests.
  const visualizerState = resolveVisualizerState({
    reactionState, agentState, isAgentSpeaking, isUserSpeaking, isMicOn,
  });

  const footerVisualizer = visualizerMode === 'cavalry'
      ? (
        // Deliberately NOT gated on `isMicOn`. The cluster is the concierge's face, not a mic-level
        // meter: pulling it off screen on mute (the behaviour until 2026-08-28) read as the whole
        // agent having been switched off, and it left a hole in the footer bar that resized the
        // caption columns either side. It now stays mounted and holds the idle shape — see
        // `visualizerState` above. Keeping it mounted also avoids tearing down and rebuilding
        // CavalryViz's WebGL surface on every mic toggle, which is the exact remount churn
        // releaseSurface() exists to survive.
        //
        // Voice_Animation.cv (V011, 2026-08-28) still draws WHITE ink on a transparent comp
        // (`colorMaterial#492` -> `basicShape#31 "Base Circle"`, rgba 255/255/255 — the circle the
        // one visible duplicator draws), matching Figma node 45:1147. Re-verified against the new
        // export, since `screen` depends on it: the old `invert(1)` was for the pre-2026-08-20
        // dark-ink rig and against white ink it turns the dots black again — exactly the "still
        // black" symptom. Screen keeps white light on the dark bar and drops the transparent comp
        // background, so no chip is needed.
        <CavalryViz
          state={visualizerState}
          maxWidth={u(CLUSTER_W * CAVALRY_COMP_OVERSCAN)}
          blendMode="screen"
          amplitudeRef={currentVolume}
        />
      )
    // The other two modes ARE literal level meters — a muted mic gives them nothing to draw, so
    // they keep the original hide-on-mute behaviour rather than sitting there flatlined.
    : !isMicOn ? null
    : visualizerMode === 'pill'
      ? <ParticlePill state={visualizerState} size={36} dotColor={ui.pillDotColor} />
    : <Visualizer volumeRef={currentVolume} />;

  // Ophion demo request (2026-09-14): mobile skips the hamburger and shows the three chips
  // permanently at top-right, cart/wishlist/privacy in that order — same string-match convention
  // as `isHuntsman` above, so no backend contract change and no touch to any other tenant. The
  // hamburger logic in MobileChrome itself is untouched, gated behind `alwaysExpanded`, so it is
  // still there to reuse for a future tenant that wants it back.
  const isOphionMobileIconRail = (brandConfig.brand_name || '').toLowerCase().includes('ophion');

  // The rail/chrome content itself (cart/wishlist/privacy accordion, wordmark, connection dot) — one
  // definition, reused by both branches of the `showIntro` split below, so fixing the panel-vs-tag
  // layering bug didn't mean maintaining two copies of this prop list.
  const reelRailChildren = (
    <>
      {/* SPEC-26 Phase 4b: two owned compositions, not one scaled (OQ-2). The desktop rail sits
          top-LEFT as three chips; mobile puts one hamburger top-RIGHT that reveals the same
          three sections. Both are driven by the identical props — same `overlay` slot, same
          handlers — so the mutual exclusion and the agent-driven path (R.7) hold on both
          without a second code path for either. Ophion's mobile chrome opts out of the hamburger
          step via `alwaysExpanded` (see isOphionMobileIconRail above). */}
      {isMobileComposition ? (
        <MobileChrome
          alwaysExpanded={isOphionMobileIconRail}
          expanded={voiceFirst.railExpanded} // SPEC-82 Rev 3: `overlay`, minus PRIVACY when the menu hosts it
          onToggle={(kind) => setOverlay(prev => (prev === kind ? null : kind))}
          cartItems={cartItems}
          wishlistItems={wishlistItems}
          priceDisplay={priceDisplay}
          linePriceDisplay={linePriceDisplay}
          cartTotalDisplay={formatPrice(cartTotal, brandConfig.currency)}
          cartUnitCount={cartUnits}
          onRemoveCartLine={handleRemoveCartLine}
          onIncrementCartLine={handleIncrementCartLine}
          onDecrementCartLine={handleDecrementCartLine}
          scrollCommand={scrollCommand}
          onScrollStateChange={reportPanelScroll}
          onCheckout={checkout.requestCheckout}
          onSelectItem={handleCardClick}
          onToggleSaved={handleToggleWishlist}
          privacyLabel={DISCLOSURE.launcherLabel}
          consent={consent}
          onSaveConsent={handlePrefsSave}
          onOpenPrivacyPolicy={() => setPrivacyOpen(true)}
          recording={micRecording}
          showIntro={showIntro}
          wordmarkSrc={DEFAULT_WORDMARK_SRC}
          brandName={brandName}
          onLogoReset={logoSoftReset.available ? logoSoftReset.reset : undefined}
          hidePrivacyChip={voiceFirst.topChrome.railHidesPrivacy} // SPEC-82
        />
      ) : (
        <>
          <IconRail
            expanded={voiceFirst.railExpanded} // SPEC-82 Rev 3: `overlay`, minus PRIVACY when the menu hosts it
            onToggle={(kind) => setOverlay(prev => (prev === kind ? null : kind))}
            cartItems={cartItems}
            wishlistItems={wishlistItems}
            priceDisplay={priceDisplay}
            linePriceDisplay={linePriceDisplay}
            cartTotalDisplay={formatPrice(cartTotal, brandConfig.currency)}
            cartUnitCount={cartUnits}
            onRemoveCartLine={handleRemoveCartLine}
            onIncrementCartLine={handleIncrementCartLine}
            onDecrementCartLine={handleDecrementCartLine}
            scrollCommand={scrollCommand}
            onScrollStateChange={reportPanelScroll}
            onCheckout={checkout.requestCheckout}
            onSelectItem={handleCardClick}
            onToggleSaved={handleToggleWishlist}
            privacyLabel={DISCLOSURE.launcherLabel}
            consent={consent}
            onSaveConsent={handlePrefsSave}
            onOpenPrivacyPolicy={() => setPrivacyOpen(true)}
            recording={micRecording}
            showIntro={showIntro}
            side={voiceFirst.topChrome.rail} // SPEC-82 bag_top_right / collection_menu ('left' = today)
            hidePrivacyChip={voiceFirst.topChrome.railHidesPrivacy}
          />
          <LogoResetButton
            src={DEFAULT_WORDMARK_SRC}
            alt={brandName}
            className="absolute left-1/2 -translate-x-1/2 object-contain pointer-events-none"
            style={{ top: topOffset(WORDMARK.topPct), width: `${WORDMARK.widthPct}%` }}
            onReset={logoSoftReset.available ? logoSoftReset.reset : undefined}
          />
        </>
      )}
      <CollectionMenu composition={composition} menu={voiceFirst.menu} recording={micRecording} privacy={voiceFirst.menuPrivacy} />{/* SPEC-82: null unless collection_menu */}
      {/* The pre-Figma WS connection indicator (dot + Online/Connecting) had no home in the
          reel redesign and quietly stopped rendering — it still exists below in the legacy
          header, just unreachable in reel mode. Restored here, mirrored to the right of the
          rail's left inset for symmetry, and gated on !showIntro: it's meaningless chrome to
          show over the intro film and only matters once the guest has landed on the reel.
          On mobile it used to take the top-left corner (the desktop mirror was the chip owning
          the right corner), which every mobile frame left empty — until SPEC-57's "Views" pill
          (viewToggleGeometry.js MOBILE: leftInset 37, top 112) took that same corner and the two
          overlapped (2026-09-15). Mobile now centres it below the wordmark instead — desktop is
          unchanged. */}
      {/* no_enter_gate: nothing connects until the landing tap, so a red "Connecting" there would be a lie — hidden until then. */}
      {!showIntro && !(introX.landing.engaged && !isExperienceStarted) && (
        <div
          className={`absolute flex items-center gap-2 pointer-events-none ui-reveal-fade${panelIndicator.className}`}
          aria-hidden={panelIndicator.ariaHidden}
          style={isMobileComposition
            ? {
                '--u': MOBILE_UNIT,
                top: safeTop(MOBILE_WORDMARK.top + MOBILE_WORDMARK.h + CONNECTION_INDICATOR_GAP),
                left: '50%',
                transform: 'translateX(-50%)',
              }
            : (voiceFirst.desktopIndicatorStyle || { '--u': RAIL_UNIT, top: topOffset(RAIL_TOP_PCT), right: `${RAIL_LEFT_PCT}%` })}
        >
          {/* Sized in the composition's own unit with the old Tailwind sizes as floors (see
              railGeometry's INDICATOR): pixel-identical everywhere it rendered before, and it now
              grows with the chrome on a 2560px+ display instead of staying a 14px island. */}
          <div
            className={`rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`}
            style={{
              width: (isMobileComposition ? muMin : uMin)(INDICATOR.dot, INDICATOR.minDot),
              height: (isMobileComposition ? muMin : uMin)(INDICATOR.dot, INDICATOR.minDot),
            }}
          />
          <span
            className="text-reel-chrome-textDim tracking-widest uppercase font-medium"
            style={{
              fontSize: isMobileComposition
                ? muMin(INDICATOR.font, INDICATOR.minFontMobile)
                : uMin(INDICATOR.font, INDICATOR.minFont),
            }}
          >
            {isConnected ? 'Online' : 'Connecting'}
          </span>
        </div>
      )}
    </>
  );

  return (
    <div className="fixed inset-0 w-full h-[100dvh] flex flex-col overflow-hidden font-sans text-neutral-900 selection:bg-black/10" style={backgroundStyle}>

      {/* 🔐 VER-40: always-available privacy control (withdraw anytime). The panel itself is the
          'PRIVACY' overlay slot now (rail accordion in reel mode, generic drawer otherwise — see
          the drawer block below and IconRail/MobileChrome) — dropped behind the full-bleed reel
          intro (see PrivacyLauncher) rather than hidden outright, so it's never on top of the film
          but stays reachable once the intro ends. */}
      {/* SPEC-26 Phase 4: in reel mode this pill's job (and its VER-40 recording indicator) moves
          into the Figma icon rail above — rendering both would put two privacy controls on screen. */}
      {/* `privacy_launcher: false` (tenant feature flag) hides the pill for a demo deployment;
          absent keeps VER-40's always-on default. The consent gate, the privacy drawer and the
          mic-button route into it are unaffected — only this launcher pill goes. */}
      {!brandConfig.feature_flags?.reel_mode && brandConfig.feature_flags?.privacy_launcher !== false && (
        <PrivacyLauncher onOpen={() => setOverlay('PRIVACY')} recording={micRecording} behindOverlay={showIntro} />
      )}
      {/* 🔐 VER-40: when the guest is inside without audio consent, make the muted concierge obvious + one tap from on */}
      <VoiceOffPrompt {...voiceOffPrompt} placement={isReelChrome ? `reel-${composition}` : 'generic'} />
      {/* no_enter_gate: the boutique is up before the entry tap, so the blocking consent gate that tap may open lives here too —
          portaled above the footer's own portal (z 65), which would otherwise paint the dock over the gate's buttons. */}
      {landingGateOpen && (
        <OverlayPortal zIndex={200}>
          <ConsentGate reelChrome open onSave={handleConsentSave} onOpenPrivacy={() => setPrivacyOpen(true)} />
          <PrivacyPolicy reelChrome open={privacyOpen} onClose={() => setPrivacyOpen(false)} brandName={brandName} brandContact={brandConfig.privacy_contact || ''} />
        </OverlayPortal>
      )}
      <PrivacyPolicy reelChrome={isReelChrome} open={privacyOpen && !landingGateOpen} onClose={() => setPrivacyOpen(false)} />
      <WaitlistModal {...waitlist} /> {/* SPEC-52: reachable wherever an Add-to-Bag glyph is (A-D) */}
      <CheckoutOverlay {...checkout} /> {/* SPEC-46 Rev 7: payment happens HERE, never in a second tab */}

      {/* 🌟 1. PERFECTLY ALIGNED HEADER */}
      {/* SPEC-26 Phase 4 Revision: reel-mode tenants get the real Figma chrome — the top-left
          concertina rail (node 45:4838) plus a centred brand wordmark (45:1219). The three rail
          items are accordion headers that expand IN PLACE, displacing those below them; Phase 4
          built them as icon buttons opening a right-hand drawer, which is not the design (R.3
          defect 9). Every handler is still the pre-existing one.

          The rail reads App.js's existing `overlay` state, which buys two behaviours for free:
          only one panel can be open at a time (the client's art direction: opening the cart must
          auto-close the wishlist), and the agent can already drive it — "show me my cart" sets
          `overlay` in handleSignaling below. */}
      {brandConfig.feature_flags?.reel_mode ? (
        showIntro ? (
          // Unchanged from before this fix: inline, z-0, below the full-bleed intro film. Nothing
          // about intro-time behaviour (incl. the VER-40 privacy chip's reachability during the
          // film) should move while this fix is only about the POST-intro panel-vs-tag layering.
          <header className="flex-none w-full relative z-0 pointer-events-none">
            {reelRailChildren}
          </header>
        ) : (
          // Portaled (not just a higher z-index) once the intro has ended: the rail/panel and
          // OverlayPortal's point-tags/product-card/collection/spec-sheet layer are BOTH already
          // portaled to `document.body` at that point (the tag layer always; the footer/mic bar via
          // the OverlayPortal calls below) — true DOM siblings there, not nested inside this app's
          // `position: fixed` root. A `position: fixed` element is its own stacking context, so a
          // z-index set on anything INSIDE it (the first attempt at this fix, 2026-08-28) can only
          // ever outrank OTHER children of that same root — never a true sibling at the body level.
          // Raising the ROOT's own z-index instead (the second attempt) fixed the panel but dragged
          // the ENTIRE app — including the opaque reel video — in front of that same body-level
          // layer, blacking out the footer, mic pill and every tag along with it. Portaling just the
          // rail, the same way the tag layer and footer already are, is what actually sits it above
          // them without taking anything else with it. Verified in isolation with headless Chrome
          // (position:fixed root vs. this portal): identical child layout in both cases, so this
          // costs nothing structurally — only the stacking outcome changes.
          <OverlayPortal zIndex={80}>
            <header className="w-full relative pointer-events-none">
              {reelRailChildren}
            </header>
          </OverlayPortal>
        )
      ) : (
      <header className="flex-none w-full px-4 md:px-8 pt-4 md:pt-6 flex justify-between items-start z-30 pointer-events-none">

        {/* Left Side: Wishlist — hidden during intro film */}
        {!showIntro && <div className="pointer-events-auto mt-1 ui-reveal-fade">
          <button aria-label="Open wishlist" onClick={() => setOverlay('WISHLIST')} className="relative flex items-center justify-center p-3 bg-white/80 backdrop-blur border border-black/5 hover:bg-black/5 rounded-full transition-colors shadow-sm">
            <Heart size={20} className="text-neutral-800" />
            {wishlistItems.length > 0 && <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{wishlistItems.length}</span>}
          </button>
        </div>}

        {/* Right Side: Cart & Logo perfectly stacked */}
        <div className="flex flex-col items-end gap-3 pointer-events-none">
          {/* Cart button — hidden during intro film */}
          {!showIntro && <div className="pointer-events-auto mt-1 ui-reveal-fade">
            <button aria-label="Open cart" onClick={() => setOverlay('CART')} className="relative flex items-center justify-center p-3 bg-white/80 backdrop-blur border border-black/5 hover:bg-black/5 rounded-full transition-colors shadow-sm">
              <ShoppingCart size={20} className="text-neutral-800" />
              {cartItems.length > 0 && <span className="absolute -top-1 -right-1 bg-black text-white text-[10px] font-bold w-5 h-5 rounded-full flex items-center justify-center">{cartItems.length}</span>}
            </button>
          </div>}
          <div className="flex flex-col items-end pointer-events-auto pr-1">
            <img src="/versailogo.png" alt="Versai Logo" className="h-4 md:h-5 object-contain mb-1" />
            <div className="flex items-center gap-1.5">
              <div className={`w-1.5 h-1.5 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'} animate-pulse`} />
              <span className="text-[9px] md:text-[10px] text-black/40 tracking-widest uppercase font-medium">{isConnected ? 'Online' : 'Connecting'}</span>
            </div>
          </div>
        </div>
      </header>
      )}

      {/* 🌟 2. MAIN CONTENT ZONE (Shrunk to 60vh/65vh, positioned higher) */}
      <main className="absolute top-20 md:top-24 left-0 w-full z-10 px-4 md:px-12 flex justify-center">
        {/* 🛠️ DEV TOOLS: VAD TUNING PANEL — SPEC-26 R.3 defect 14. This was hardcoded `{true &&`,
            so it rendered its slider panel over the reel in production builds. Opt-in only. */}
        {process.env.REACT_APP_VAD_TUNER === 'true' && (
          <div className="absolute -top-16 z-50 flex gap-6 bg-white/90 backdrop-blur border border-black/10 shadow-sm px-6 py-2 rounded-full pointer-events-auto">
            <div className="flex flex-col items-center">
              <label className="text-[9px] font-bold uppercase tracking-widest text-neutral-500 mb-1">
                Speaking (Interrupt) : {vadSpeakingThreshold.toFixed(2)}
              </label>
              <input
                type="range" min="0" max="0.5" step="0.01"
                value={vadSpeakingThreshold}
                onChange={(e) => setVadSpeakingThreshold(parseFloat(e.target.value))}
                className="w-24 accent-black"
              />
            </div>
            <div className="flex flex-col items-center border-l border-black/10 pl-6">
              <label className="text-[9px] font-bold uppercase tracking-widest text-neutral-500 mb-1">
                Listening (Start) : {vadListeningThreshold.toFixed(2)}
              </label>
              <input
                type="range" min="0" max="0.2" step="0.01"
                value={vadListeningThreshold}
                onChange={(e) => setVadListeningThreshold(parseFloat(e.target.value))}
                className="w-24 accent-black"
              />
            </div>
            <div className="flex flex-col items-center border-l border-black/10 pl-6">
              {/* 🔇 How loud the room must be relative to the agent's own voice before it may
                  interrupt it. Higher = the agent is harder to cut off, including by its own
                  loopback through the speakers. See the echo gate in useVoiceActivityDetection. */}
              <label className="text-[9px] font-bold uppercase tracking-widest text-neutral-500 mb-1">
                Echo reject : {vadEchoRejectRatio.toFixed(2)}
              </label>
              <input
                type="range" min="0" max="2" step="0.05"
                value={vadEchoRejectRatio}
                onChange={(e) => setVadEchoRejectRatio(parseFloat(e.target.value))}
                className="w-24 accent-black"
              />
            </div>
          </div>
        )}
        <AnimatePresence mode='wait'>

          {/* GRID VIEW */}
          {view === 'GRID' && viewRef.current !== 'SCENE_3D' && (
            <motion.div key="grid" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.05 }} className="pointer-events-auto bg-white/70 backdrop-blur-3xl border border-black/5 rounded-2xl md:rounded-3xl p-4 md:p-8 w-full max-w-6xl h-[calc(100dvh-10.5rem)] md:h-[calc(100dvh-14rem)] shadow-[0_8px_30px_rgb(0,0,0,0.04)] flex flex-col overflow-hidden">
              {products.length === 0 && pinnedItems.length === 0 ? (
                <div className="flex-1 flex flex-col items-center justify-center text-center space-y-4 px-4">
                  <h2 className="text-2xl md:text-3xl font-light tracking-widest text-neutral-900">
                    {isReturningGuest ? `Welcome back` : `Welcome to ${brandName}`}
                  </h2>
                  {isReturningGuest && (wishlistItems.length > 0 || cartItems.length > 0) && (
                    <div className="flex flex-wrap justify-center gap-3 mt-1">
                      {wishlistItems.length > 0 && (
                        <button
                          onClick={() => setOverlay('WISHLIST')}
                          className="text-[10px] uppercase tracking-widest text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 hover:bg-black/5 transition-colors"
                        >
                          {wishlistItems.length} saved {wishlistItems.length === 1 ? 'piece' : 'pieces'}
                        </button>
                      )}
                      {cartItems.length > 0 && (
                        <button
                          onClick={() => setOverlay('CART')}
                          className="text-[10px] uppercase tracking-widest text-neutral-500 border border-black/10 rounded-full px-3 py-1.5 hover:bg-black/5 transition-colors"
                        >
                          {cartItems.length} in cart
                        </button>
                      )}
                    </div>
                  )}
                  {!hasWelcomed ? (
                    <div className="flex flex-col items-center gap-3 mt-4">
                      <Loader2 size={40} className="animate-spin text-neutral-400" />
                      <p className="text-neutral-500 max-w-md text-xs md:text-sm font-light leading-relaxed animate-pulse">
                        {isReturningGuest ? `${agentName} is preparing your boutique…` : `${agentName} is preparing the boutique for you...`}
                      </p>
                    </div>
                  ) : (
                    <p className="text-neutral-600 max-w-md text-xs md:text-sm font-light leading-relaxed opacity-70">
                      {isReturningGuest ? `What would you like to explore today?` : `Tell me what you are looking for.`}
                    </p>
                  )}
                </div>
              ) : (
                <>
                  {/* 🧠 AMBIENT DIM CHIP — hidden per product request; state still maintained */}
                  {/* agentRead.intent && ... (DIM intent/mood chip — UI off) */}
                  {/* 🧠 "WHY THESE" — hidden per product request; semantic_intent state still maintained */}
                  {/* semanticIntent && ... (Why These panel — UI off) */}
                  {/* 🧺 MY SELECTION — when ≥2 pieces are pinned, offer a side-by-side comparison */}
                  {brandConfig.feature_flags?.compare !== false && pinnedItems.length >= 2 && (
                    <div className="flex-none mb-3 md:mb-4 flex items-center justify-between bg-black/[0.03] border border-black/5 rounded-xl px-3 md:px-4 py-2">
                      <span className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-600 font-semibold">
                        My Selection · {pinnedItems.length}
                      </span>
                      <button
                        onClick={() => setView('COMPARE')}
                        className="px-4 py-2 bg-black text-white rounded-full text-[10px] md:text-xs uppercase tracking-widest font-medium hover:bg-neutral-800 transition-colors"
                      >
                        Compare
                      </button>
                    </div>
                  )}
                  <div className="flex-1 overflow-y-auto custom-scrollbar content-start pr-1 md:pr-2">
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-6">
                      {pinnedItems.map((pinned) => (
                        <div key={`pin-${pinned.id}`} className="relative border border-black/10 rounded-xl overflow-hidden shadow-[0_4px_20px_rgba(0,0,0,0.05)] p-1 bg-white">
                          <div className="absolute top-2 left-2 z-20 bg-black text-white text-[9px] md:text-[10px] uppercase font-bold tracking-widest px-2 py-1 rounded-full flex items-center gap-1">
                            <Pin size={10} /> Pinned
                          </div>
                          <button onClick={() => handleManualUnpin(pinned)} className="absolute top-2 right-2 z-30 bg-white/80 backdrop-blur p-1.5 rounded-full hover:bg-neutral-200 transition-colors shadow-sm">
                            <X size={12} className="text-neutral-900" />
                          </button>
                          <ProductCard item={pinned} onClick={() => handleCardClick(pinned)} />
                        </div>
                      ))}
                      {products.filter(p => !pinnedItems.find(pinned => pinned.id === p.id)).map((item) => (
                        <ProductCard key={item.id} item={item} onClick={() => handleCardClick(item)} />
                      ))}
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}

          {/* OUTFIT VIEW — agent-assembled "Complete the Look" (anchor + complementary pieces). */}
          {view === 'OUTFIT' && outfit && (
            <motion.div key="outfit" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.03 }} className="pointer-events-auto bg-white/90 backdrop-blur-3xl border border-black/10 rounded-2xl md:rounded-3xl p-4 md:p-6 w-full max-w-6xl h-[calc(100dvh-10.5rem)] md:h-[calc(100dvh-14rem)] shadow-[0_8px_40px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden">
              <div className="flex-none flex items-center justify-between mb-3">
                <div>
                  <h2 className="text-lg md:text-2xl font-light tracking-widest text-neutral-900">Complete the Look</h2>
                  {outfit.occasion && <p className="text-[10px] md:text-xs uppercase tracking-widest text-neutral-400 mt-0.5">for {outfit.occasion}</p>}
                </div>
                <button onClick={() => setView('GRID')} className="text-xs uppercase tracking-widest text-neutral-500 hover:text-black flex items-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
              <div className="flex-1 overflow-auto custom-scrollbar flex flex-col md:flex-row gap-4 md:gap-6 items-stretch">
                {/* Anchor piece — prominent */}
                {outfit.anchor && (
                  <div onClick={() => handleCardClick(outfit.anchor)} className="md:w-2/5 shrink-0 cursor-pointer bg-white rounded-2xl border border-black/10 p-4 flex flex-col">
                    <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-400 mb-2">The piece</div>
                    <div className="flex-1 flex items-center justify-center bg-neutral-50/50 rounded-xl p-3 min-h-[160px]">
                      <img src={outfit.anchor.image_url || outfit.anchor.image_uri} alt={outfit.anchor.title} className="max-h-[34vh] object-contain mix-blend-multiply" />
                    </div>
                    <h3 className="text-sm md:text-base font-medium text-neutral-900 mt-3 leading-tight">{outfit.anchor.title}</h3>
                    <p className="text-xs text-neutral-500 mt-0.5">{priceDisplay(outfit.anchor)}</p>
                  </div>
                )}
                {/* Complementary pieces — or search results when agent finds alternatives */}
                <div className="flex-1 flex flex-col gap-2">
                  {outfitSearchOverride && (
                    <div className="text-[9px] uppercase tracking-[0.25em] text-neutral-400">Options</div>
                  )}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 content-start">
                    {(outfitSearchOverride
                      ? [...(outfit.pieces || []).filter(p => garmentSlot(p) !== garmentSlot(outfitSearchOverride[0])), ...outfitSearchOverride]
                      : outfit.pieces
                    ).map((p, idx) => (
                      <div key={p.id || idx} onClick={() => handleCardClick(p)} className="cursor-pointer bg-white rounded-xl border border-black/5 shadow-sm p-3 flex flex-col">
                        <div className="flex-1 flex items-center justify-center bg-neutral-50/50 rounded-lg p-2 min-h-[110px]">
                          <img src={p.image_url || p.image_uri} alt={p.title} className="max-h-[18vh] object-contain mix-blend-multiply" />
                        </div>
                        <h4 className="text-[11px] md:text-xs font-medium text-neutral-900 mt-2 leading-tight line-clamp-2">{p.title}</h4>
                        <p className="text-[10px] text-neutral-500 mt-0.5">{priceDisplay(p)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* COMPARE VIEW — side-by-side of the pinned selection */}
          {view === 'COMPARE' && (
            <motion.div key="compare" initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 1.03 }} className="pointer-events-auto bg-white/90 backdrop-blur-3xl border border-black/10 rounded-2xl md:rounded-3xl p-4 md:p-6 w-full max-w-6xl h-[calc(100dvh-10.5rem)] md:h-[calc(100dvh-14rem)] shadow-[0_8px_40px_rgba(0,0,0,0.08)] flex flex-col overflow-hidden">
              <div className="flex-none flex items-center justify-between mb-3">
                <h2 className="text-lg md:text-2xl font-light tracking-widest text-neutral-900">Comparison</h2>
                <button onClick={() => setView('GRID')} className="text-xs uppercase tracking-widest text-neutral-500 hover:text-black flex items-center gap-1">
                  <ChevronLeft size={14} /> Back
                </button>
              </div>
              {pinnedItems.length < 2 ? (
                <div className="flex-1 flex items-center justify-center text-neutral-400 text-sm">Pin at least two pieces to compare.</div>
              ) : (
                <div className="flex-1 overflow-auto custom-scrollbar">
                  {/* header: one column per selected piece */}
                  <div className="grid sticky top-0 bg-white/95 backdrop-blur z-10 border-b border-black/10" style={{ gridTemplateColumns: `minmax(110px, 1fr) repeat(${pinnedItems.length}, minmax(120px, 1fr))` }}>
                    <div />
                    {pinnedItems.map((it) => (
                      <div key={it.id} className="p-2 text-center">
                        <img src={it.image_url || it.image_uri} alt={it.title} className="h-16 md:h-20 mx-auto object-contain mix-blend-multiply" />
                        <div className="text-[11px] md:text-xs font-medium text-neutral-900 mt-1 leading-tight line-clamp-2">{it.title}</div>
                        <div className="text-[10px] md:text-xs text-neutral-500">{priceDisplay(it)}</div>
                      </div>
                    ))}
                  </div>
                  {/* attribute rows, grouped into sections (Cloth & Pattern, Cut & Construction, ...) */}
                  {buildComparison(pinnedItems).groups.map((group) => (
                    <div key={group.label}>
                      <div className="px-2 pt-4 pb-1 text-[10px] uppercase tracking-[0.2em] text-neutral-900 font-semibold border-b border-black/10">
                        {group.label}
                      </div>
                      {group.rows.map((row) => (
                        <div key={row.label} className="grid border-b border-black/5" style={{ gridTemplateColumns: `minmax(110px, 1fr) repeat(${pinnedItems.length}, minmax(120px, 1fr))` }}>
                          <div className="p-2 text-[10px] uppercase tracking-widest text-neutral-400 font-medium self-center">{row.label}</div>
                          {row.values.map((v, i) => (
                            <div key={i} className={`p-2 text-xs md:text-sm font-light self-center ${row.diff ? 'text-neutral-900 bg-amber-50/70 rounded' : 'text-neutral-600'}`}>{v}</div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          )}

          {/* PRODUCT VIEW */}
          {view === 'PRODUCT' && selectedProduct && (
            <motion.div
              key="product"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="pointer-events-auto bg-white/90 backdrop-blur-3xl border border-black/10 rounded-2xl md:rounded-3xl w-full max-w-5xl h-[calc(100dvh-10.5rem)] md:h-[calc(100dvh-14rem)] flex flex-col md:flex-row shadow-[0_8px_40px_rgba(0,0,0,0.08)] overflow-hidden relative"
            >
              <button onClick={() => { setView('GRID'); setOverlay(null); }} className="absolute top-4 left-4 z-20 w-8 h-8 md:w-10 md:h-10 bg-white/80 backdrop-blur rounded-full flex items-center justify-center border border-black/5 hover:bg-neutral-100 transition-colors shadow-sm">
                <ChevronLeft size={18} className="text-black" />
              </button>

              <div className="w-full md:w-1/2 h-[45%] md:h-full bg-neutral-50/50 p-4 md:p-8 flex flex-col relative border-b md:border-b-0 md:border-r border-black/5 shrink-0 min-h-0">

                <div
                  ref={imgContainerRef}
                  onMouseEnter={() => setShowLens(true)}
                  onMouseLeave={() => setShowLens(false)}
                  onMouseMove={handleLensMove}
                  className="w-full flex-1 rounded-xl overflow-hidden flex items-center justify-center relative group cursor-crosshair min-h-0"
                  onClick={() => setIsLightboxOpen(true)}
                >

                  {/* ✨ THE MAGIC LENS (Now z-40) ✨ */}
                  {showLens && (
                    <div
                      className="absolute pointer-events-none rounded-full border border-black/10 shadow-[0_20px_50px_rgba(0,0,0,0.3)] z-40 bg-white hidden md:block"
                      style={{
                        width: '220px',
                        height: '220px',
                        left: `${lensPos.x - 110}px`,
                        top: `${lensPos.y - 110}px`,
                        backgroundImage: `url("${productImages[currentImageIndex]}")`, // ⬅️ Added double quotes
                        backgroundPosition: `${lensPos.bgX}% ${lensPos.bgY}%`,
                        backgroundSize: '250%',
                        backgroundRepeat: 'no-repeat'
                      }}
                    />
                  )}

                  {/* ⬅️ RIGHT / LEFT ARROWS (Now z-50) ➡️ */}
                  {productImages.length > 1 && (
                    <>
                      <button onClick={handlePrevImage} className="absolute left-2 md:left-4 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-50 hover:bg-white shadow-sm"><ChevronLeft size={20} /></button>
                      <button onClick={handleNextImage} className="absolute right-2 md:right-4 p-2 bg-white/80 backdrop-blur-sm rounded-full opacity-0 group-hover:opacity-100 transition-opacity z-50 hover:bg-white shadow-sm"><ChevronRight size={20} /></button>
                    </>
                  )}

                  <img src={productImages[currentImageIndex]} alt={`${selectedProduct.title} view`} className="max-w-full max-h-full object-contain mix-blend-multiply transition-transform duration-300 group-hover:scale-[1.02]" />
                </div>
                {productImages.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto custom-scrollbar pt-4 shrink-0">
                    {productImages.map((img, idx) => (
                      <button key={idx} onClick={() => setCurrentImageIndex(idx)} className={`w-14 h-14 md:w-20 md:h-20 shrink-0 rounded-lg border-2 overflow-hidden bg-white p-1 transition-all ${currentImageIndex === idx ? 'border-neutral-900 scale-100' : 'border-transparent opacity-60 hover:opacity-100 scale-95'}`}>
                        <img src={img} alt="thumbnail" className="w-full h-full object-contain mix-blend-multiply" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="w-full md:w-1/2 flex-1 md:h-full flex flex-col relative bg-white min-h-0">
                <div className="absolute top-4 right-4 md:top-6 md:right-6 flex gap-3 z-30">
                  <button onClick={() => handleToggleWishlist(selectedProduct)} className="p-2 bg-neutral-100 rounded-full border border-black/5 hover:bg-neutral-200 transition-colors shadow-sm">
                    <Heart size={16} className={`${inWishlist ? 'text-red-500 fill-red-500' : 'text-neutral-400'}`} />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar p-6 md:p-10">
                  <h2 className="text-3xl md:text-4xl font-extralight text-neutral-900 mb-2 leading-tight pr-10">{selectedProduct.title}</h2>
                  <div className="w-12 h-px bg-black/10 mb-6" />

                  {buildDetailGroups(selectedProduct).map((group) => (
                    <div key={group.label} className="mb-8">
                      {group.label && (
                        <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold mb-3">{group.label}</div>
                      )}
                      <div className="grid grid-cols-2 gap-y-4 gap-x-8">
                        {group.rows.map((row) => {
                          const isHighlighted = isRowHighlighted(highlightedFeatures, row.k);
                          return (
                            <div key={row.k} className={`border-b border-black/5 pb-2 p-1 rounded transition-colors duration-700 ${isHighlighted ? 'bg-yellow-100 shadow-[0_0_15px_rgba(254,240,138,0.5)]' : ''}`}>
                              <dt className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold mb-1">{row.k}</dt>
                              <dd className="text-sm font-light text-neutral-800">{row.v}</dd>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                  <p className="text-neutral-700 font-light leading-relaxed text-sm md:text-base pb-4">{selectedProduct.description}</p>

                  {/* 📏 SIZE SELECTOR — only for size-based products (suits). In-stock = solid,
                      made-to-order = dashed outline, truly unavailable = disabled. */}
                  {selectedProduct.variants?.options?.length > 0 && (
                    <div className="mb-2">
                      <div className="text-[10px] uppercase tracking-[0.2em] text-neutral-400 font-semibold mb-3">
                        {selectedProduct.variants.type === 'size' ? 'Select Size' : 'Select Option'}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {selectedProduct.variants.options.map((opt) => {
                          const isSel = selectedVariant === opt.label;
                          const disabled = !opt.inStock && !opt.mto;
                          return (
                            <button
                              key={opt.label}
                              disabled={disabled}
                              onClick={() => setSelectedVariant(opt.label)}
                              title={opt.inStock ? 'In stock' : opt.mto ? 'Made to order' : 'Unavailable'}
                              className={`min-w-[44px] px-3 py-2 rounded-md text-xs font-medium transition-colors border ${isSel ? 'bg-black text-white border-black'
                                : opt.inStock ? 'bg-white text-neutral-800 border-black/20 hover:border-black'
                                  : opt.mto ? 'bg-white text-neutral-500 border-dashed border-black/40 hover:border-black'
                                    : 'bg-neutral-100 text-neutral-300 border-transparent cursor-not-allowed line-through'
                                }`}
                            >
                              {opt.label}
                            </button>
                          );
                        })}
                      </div>
                      {(() => {
                        const opt = selectedProduct.variants.options.find(o => o.label === selectedVariant);
                        if (!opt) return null;
                        if (opt.inStock) return <p className="text-[11px] text-green-700 mt-2 tracking-wide">In stock — ready to ship.</p>;
                        if (opt.mto) return <p className="text-[11px] text-neutral-500 mt-2 tracking-wide">Made to order — tailored to your measurements.</p>;
                        return null;
                      })()}
                    </div>
                  )}
                </div>

                <div className="flex-none p-4 md:p-6 bg-white border-t border-black/5 flex items-center justify-between z-20">
                  <div>
                    <div className="text-[9px] md:text-xs text-neutral-400 mb-1 tracking-widest uppercase font-semibold">Price</div>
                    <div className="text-xl md:text-3xl font-light text-neutral-900">{priceDisplay(selectedProduct)}</div>
                  </div>
                  {(() => {
                    const hasSizes = selectedProduct.variants?.options?.length > 0;
                    const needsSize = hasSizes && !selectedVariant;
                    return (
                      <button
                        onClick={() => handleAddToCart(selectedProduct, hasSizes ? selectedVariant : null)}
                        disabled={needsSize}
                        className={`px-6 md:px-8 h-10 md:h-12 rounded-full font-medium tracking-widest uppercase text-[10px] md:text-xs transition-colors shadow-md ${needsSize ? 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
                          : 'bg-black text-white hover:bg-neutral-800'
                          }`}
                      >
                        {needsSize ? 'Select a Size' : 'Add to Cart'}
                      </button>
                    );
                  })()}
                </div>
              </div>
              {/* 🏛️ VESPER INSPIRATION CARD */}
              <AnimatePresence>
                {activeLoreImage && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9, x: 20 }}
                    animate={{ opacity: 1, scale: 1, x: 0 }}
                    exit={{ opacity: 0, scale: 0.9, x: 20 }}
                    className="absolute top-24 right-8 z-50 w-56 md:w-72 bg-white/80 backdrop-blur-2xl p-3 rounded-2xl border border-black/5 shadow-[0_20px_50px_rgba(0,0,0,0.15)] pointer-events-none"
                  >
                    <div className="flex flex-col gap-2">
                      <span className="text-[8px] font-bold uppercase tracking-[0.2em] text-black/40 border-b border-black/5 pb-1 mb-1 text-center">
                        Inspiration: Architecture of Shadows
                      </span>
                      <img
                        src={activeLoreImage}
                        className="w-full h-auto rounded-lg shadow-sm"
                        alt="Architectural Lore"
                      />
                      <div className="flex justify-between items-end mt-1">
                        <p className="text-[9px] text-black/60 font-serif italic leading-tight">
                          E.L. Boullée <br /> Newton Cenotaph Project
                        </p>
                        <span className="text-[8px] text-black/30 font-medium">c. 1784</span>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </main>

      {/* Reel pilot: Projectionist — full-bleed video. Mounted only after user enters so the
          intro film never plays before the experience starts (avoids intro-already-done race).
          The fallback is a black cover (not null): Projectionist is a React.lazy chunk, and a
          null fallback leaves the storefront grid visible underneath for the chunk's load time —
          a visible flash of the grid before the film appears. This fallback matches
          Projectionist's own container styling so the transition into it is invisible. */}
      <Suspense
        fallback={
          brandConfig.feature_flags?.reel_mode && inBoutique
            ? <div className="fixed inset-0 z-20 bg-black" />
            : null
        }
      >
        {/* SPEC-27 D18: `offSubject` means "the voice is on something this chapter cannot show".
            A `play` is never that — the film moved BECAUSE the subject resolved to a chapter, so
            the picture is on-subject by construction even when no single attribute key came back.
            Suppressing tags on a play would blank the chapter that just earned them. */}
        {brandConfig.feature_flags?.reel_mode && inBoutique && (
          <Projectionist
            ref={reelRef}
            onReelState={sendReelState}
            onIntroFinished={sendReelIntroFinished}
            product={reelShown.product}
            tagEligibility={reelProduct?.tagEligibility}
            tagRegions={reelProduct?.tagRegions}
            rail={reelShown.rail}
            // SPEC-34 P4 (closes SPEC-28): a rail tap IS the voice ask — the backend routes it
            // through the same showcase path (film + sheet + focus + graph) and speaks a short
            // grounded beat when the model is idle (D8/AC-19a). One code path, tap or voice.
            // SPEC-92 D-4: `via` ('wheel' | 'touch') is set only by a swipe; a tap leaves the frame byte-identical.
            onVariantSelect={(item, via) => item?.id && sendInteraction('variant_selected', {
              id: item.id, title: item.headline || item.id,
            }, null, withGesture(null, via))}
            // "OPH 411 VESPER Waitlist" pill: opens the waitlist panel directly, client-side —
            // same guard/entry point as the manual Add-to-Bag waitlist funnel (handleAddToCart).
            onWaitlistClick={() => reelShown.product?.waitlist && waitlist.openFromAdd(reelShown.product)}
            // SPEC-34 P3: the PDP spec sheet + its heart/bag quick actions. The handlers
            // ARE the drawers' own (E26/D8b — one manual-sync path); the two booleans
            // only drive the glyph fill state.
            specSheet={reelSpecSheet}
            // Derived by `overlays/sheetSelection.js` rather than inline: the strict `p.id ===
            // variant_id` these two lines used to be missed a row the BACKEND put there (its
            // resolved envelope can name the piece under `variant_id`, in another case), so a
            // piece the agent saved showed an empty heart on the sheet describing it.
            sheetWishlisted={isSheetWishlisted(reelSpecSheet, wishlistItems)}
            sheetInBag={isSheetInBag(reelSpecSheet, cartItems)}
            sheetBagUnits={sheetBagUnits(reelSpecSheet, cartItems)}
            onSheetToggleWishlist={handleToggleWishlist}
            onSheetAddToBag={handleAddToCart}
            offSubject={reelSubject?.subjectKey === null && reelSubject?.outcome !== 'play'}
            returningGuest={hasSeenIntroBefore}
            skipToHome={introX.skipToHome} // SPEC-82 skip_intro_film: boot on the hero idle, film kept for replay_intro
            filmSoundCue={introX.filmFirst && showIntro && !isExperienceStarted} // first_visit_film: muted film, a tap turns the sound on
            stageOverlaysHidden={!!voiceFirst.menuPrivacy?.open} // SPEC-82 Rev 3: the consent panel never sits over the PDP or tags
            // SPEC-43 R2: `replay_intro` brackets — film gate up/down + video_state frames.
            onIntroReplayStart={introReplay.onStart}
            onIntroReplayEnd={introReplay.onEnd}
            // SPEC-53: paused + hidden under a static collection; instance and history survive.
            offStage={reelOffStage}
            // SPEC-57 fix 2: the view toggle's own disable-while-pending signal (R2 — the SAME
            // pendingChapterId useVideoPlayer already computes, never a local click flag).
            onPendingChapterChange={viewToggle.observePendingChapterId}
            // SPEC-57 fix 3: the view toggle's own disable-until-settled signal (R2 — the SAME
            // playerState FSM useVideoPlayer already computes).
            onPlayerStateChange={viewToggle.observePlayerState}
            // SPEC-92 D-1: armed at entry — never on the pre-entry landing, whose hold would turn a gesture into an entry + ghost step.
            swipeNav={reelSwipeNavProps(brandConfig.feature_flags, viewToggle.buttons, showIntro, ux, introX.landing.engaged && !isExperienceStarted)} // SPEC-86
          />
        )}
      </Suspense>

      {/* SPEC-53: the static collection stage over the paused reel, and the black fade between the
          two. The fade sits above the reel and its overlays and BELOW the rail, panels, CC bar,
          mic and keyboard pills, which never leave the screen (owner A1). A slot tap is the same
          `variant_selected` interaction the Vesper rail sends; the backend routes it by stage. */}
      {brandConfig.feature_flags?.reel_mode && inBoutique && (
        <>
          <StageHost
            state={stageMachine.state}
            composition={composition}
            onSelectSlot={(slot) => slot?.id && sendInteraction('variant_selected', {
              id: slot.id, title: slot.title || slot.headline || slot.id,
            })}
            wishlistItems={wishlistItems}
            cartItems={cartItems}
            onToggleWishlist={handleToggleWishlist}
            onAddToBag={handleAddToCart}
            onBackToReel={() => sendInteraction('return_to_reel', {})}
            onClosePiece={handleCloseCollectionPiece}
            onGalleryIndexChange={stageMachine.setGalleryIndex}
            scrollCommand={scrollCommand}
            onScrollStateChange={reportPanelScroll}
            backLabel={`Back to ${stageMachine.state?.collection?.reel_title
              || reelShortName(reelProduct?.rail?.title) || 'the film'}`}
          />
          <StageFade phase={stageFade.phase} />
        </>
      )}

      {/* SPEC-8: 3D BOUTIQUE SCENE — fixed full-screen, above main content, below nav overlays */}
      <Suspense fallback={null}>
        {view === 'SCENE_3D' && (brandConfig.feature_flags?.scene_control || process.env.REACT_APP_SCENE_CONTROL === 'true') && !brandConfig.feature_flags?.reel_mode && brandConfig.model_url && (
          <div className="fixed inset-0 z-20">
            <Scene3DBoutique
              ref={sceneRef}
              onExit={handleSceneExit}
              onViewportStateSync={handleViewportStateSync}
              brandConfig={brandConfig}
              className="scene-3d-boutique"
            />
            {showReconnectBanner && (
              <div className="absolute inset-x-0 top-4 flex justify-center pointer-events-none z-30">
                <div className="bg-black/70 text-white text-xs px-4 py-2 rounded-full backdrop-blur-sm">
                  Connection lost — reconnecting…
                </div>
              </div>
            )}
          </div>
        )}
      </Suspense>

      {/* OVERLAYS (Fixed directly to screen, absolutely no flex-bouncing) */}
      {/* 🎬 BRAND INTRO FILM — plays WITH its own audio (UNMUTED); the agent stays silent over it and
          only speaks after the film ends + the grid lands. Barge-in / Skip / end dismisses it.
          In reel_mode the Projectionist owns the intro chapter so this overlay must NOT render —
          two copies of intro.mp4 playing simultaneously would cause the film to reappear after
          the old overlay hid but before the Projectionist transitioned to idle. */}
      <AnimatePresence>
        {showIntro && !brandConfig.feature_flags?.reel_mode && (
          <motion.div key="intro-video" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.8 } }} className="fixed inset-0 z-[200] bg-black flex items-center justify-center pointer-events-auto">
            <video
              ref={introVideoRef}
              src={brandConfig.intro_video || BRAND_VIDEOS.intro}
              autoPlay
              playsInline
              preload="auto"
              onLoadedData={() => {
                // Enter Boutique is a user gesture (we resumed the AudioContext there), so unmuted
                // autoplay is normally permitted. If the browser still blocks sound, fall back to a
                // muted play so the film still runs and onEnded fires (the grid always loads).
                const v = introVideoRef.current;
                if (!v) return;
                v.muted = false;
                v.play().catch(() => { v.muted = true; v.play().catch(() => { }); });
              }}
              onPlaying={() => {
                // Intro is decoding/painting smoothly now — let it own the first ~600ms, then connect
                // the agent (warm-up only; it produces no welcome and stays silent during the film).
                clearTimeout(agentArmTimerRef.current);
                agentArmTimerRef.current = setTimeout(() => setAgentConnectArmed(true), 600);
              }}
              onEnded={() => finishIntro(true)}
              onError={() => finishIntro(true)}
              className="w-full h-full object-cover"
            />
            <button
              onClick={() => finishIntro(true)}
              className="absolute bottom-8 right-8 px-6 py-3 border border-white/30 text-white/80 hover:bg-white hover:text-black transition-all duration-500 tracking-widest uppercase text-xs font-medium rounded-full backdrop-blur-sm bg-black/30"
            >
              Skip Intro
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🧠 DIM brain widget — hidden per product request; functionality kept, UI off */}
      {/* <DimBrainWidget dim={dimRead} show={hasUserSpoken} /> */}

      {/* 🎬 BRAND / COLLECTION STORYTELLING OVERLAY (backend BRAND_DISCOVERY / COLLECTION_SHOWCASE) */}
      <AnimatePresence>
        {discovery && (
          <motion.div
            key="discovery"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.6 } }}
            className="fixed inset-0 z-[150] bg-black/90 backdrop-blur-md flex items-center justify-center pointer-events-auto p-6 md:p-12"
            onClick={() => setDiscovery(null)}
          >
            <div className="max-w-3xl w-full text-center" onClick={(e) => e.stopPropagation()}>
              <div className="text-[10px] uppercase tracking-[0.35em] text-white/40 mb-3">
                {discovery.kind === 'collection' ? 'Collection' : 'Maison'}
              </div>
              <h2 className="text-3xl md:text-5xl font-extralight text-white tracking-wide mb-6">{discovery.title}</h2>
              {discovery.images?.length > 0 && (
                <div className="flex gap-3 justify-center mb-8 flex-wrap">
                  {discovery.images.slice(0, 4).map((src, i) => (
                    <img key={i} src={src} alt="" className="h-28 md:h-40 object-cover rounded-lg opacity-90" />
                  ))}
                </div>
              )}
              <p className="text-white/80 font-light leading-relaxed text-sm md:text-lg max-h-[40vh] overflow-y-auto custom-scrollbar">
                {discovery.text}
              </p>
              <button
                onClick={() => setDiscovery(null)}
                className="mt-8 px-6 py-3 border border-white/30 text-white/80 hover:bg-white hover:text-black transition-all duration-500 tracking-widest uppercase text-xs rounded-full"
              >
                Continue
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🗣️ AGENT ADVISORY CHIP (clarify / substitute) — subtle, auto-present */}
      <AnimatePresence>
        {advisory && (
          <motion.div
            key="advisory"
            initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}
            className="fixed top-6 left-1/2 -translate-x-1/2 z-[140] pointer-events-auto"
          >
            <div className="flex items-center gap-2 bg-white/90 backdrop-blur border border-black/10 rounded-full px-4 py-2 shadow-md">
              <span className="text-[10px] uppercase tracking-widest text-neutral-500">{advisory.text}</span>
              <button onClick={() => setAdvisory(null)} className="text-neutral-400 hover:text-black"><X size={12} /></button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🎬 MOVEMENT VIDEO OVERLAY — agent-triggered, muted, with voice/manual play-pause-close */}
      <AnimatePresence>
        {videoOverlay && (
          <motion.div key="movement-video" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[110] bg-black/95 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-auto">
            <button onClick={() => applyVideoControl('close')} className="absolute top-6 right-6 md:top-10 md:right-10 text-white/50 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors z-10"><X size={32} /></button>
            {videoOverlay.title && (
              <p className="absolute top-8 left-8 text-white/70 tracking-widest uppercase text-xs md:text-sm font-medium">{videoOverlay.title}</p>
            )}
            <video
              ref={movementVideoRef}
              src={videoOverlay.url}
              autoPlay
              muted
              loop
              playsInline
              onError={() => applyVideoControl('close')}
              className="max-w-[92vw] max-h-[78vh] object-contain"
            />
            <div className="mt-6 flex items-center gap-4">
              <button
                onClick={() => applyVideoControl(isVideoPaused ? 'play' : 'pause')}
                className="px-6 py-3 border border-white/30 text-white/80 hover:bg-white hover:text-black transition-all duration-300 tracking-widest uppercase text-xs font-medium rounded-full"
              >
                {isVideoPaused ? 'Play' : 'Pause'}
              </button>
              <button
                onClick={() => applyVideoControl('close')}
                className="px-6 py-3 border border-white/30 text-white/80 hover:bg-white hover:text-black transition-all duration-300 tracking-widest uppercase text-xs font-medium rounded-full"
              >
                Close
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isLightboxOpen && selectedProduct && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/95 backdrop-blur-sm flex items-center justify-center pointer-events-auto" onClick={() => setIsLightboxOpen(false)}>
            <button onClick={() => setIsLightboxOpen(false)} className="absolute top-6 right-6 md:top-10 md:right-10 text-white/50 hover:text-white p-3 rounded-full hover:bg-white/10 transition-colors"><X size={32} /></button>
            {productImages.length > 1 && <button onClick={handlePrevImage} className="absolute left-4 md:left-12 p-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"><ChevronLeft size={32} /></button>}
            <img src={productImages[currentImageIndex]} alt="Expanded view" className="max-w-[90vw] max-h-[85vh] object-contain cursor-default" onClick={(e) => e.stopPropagation()} />
            {productImages.length > 1 && <button onClick={handleNextImage} className="absolute right-4 md:right-12 p-3 bg-white/10 text-white rounded-full hover:bg-white/20 transition-colors"><ChevronRight size={32} /></button>}
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {/* Reel mode shows every item in the concertina panel itself (IconRail/MobileChrome) — this
            drawer is only the cart/wishlist view for non-reel tenants. */}
        {overlay === 'CART' && !isReelChrome && (
          <motion.div key="cart-overlay" initial={{ opacity: 0, x: 50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 50 }} className={`pointer-events-auto fixed right-0 top-0 bottom-0 ${ui.drawer} border-l ${ui.drawerEdge} w-[85vw] md:w-96 shadow-2xl flex flex-col z-[70]`}>
            <div className={`flex-none flex justify-between items-center p-6 md:p-8 border-b ${ui.drawerHeadEdge}`}>
              <h2 className={`text-xl md:text-2xl font-light tracking-wide ${ui.drawerTitle}`}>Your Cart</h2>
              <button onClick={() => setOverlay(null)} className={`p-2 ${ui.closeBtn} rounded-full transition-colors`}><X size={18} className={ui.closeIcon} /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 md:space-y-4 custom-scrollbar p-6">
              {cartItems.length === 0 ? <p className={`${ui.emptyText} text-sm font-light mt-4 text-center`}>Your cart is empty.</p> : cartItems.map((item, idx) => (
                <div key={idx} className={`flex gap-3 md:gap-4 ${ui.itemCard} p-3 rounded-xl`}>
                  <div className={`w-16 h-20 md:w-20 md:h-24 rounded-lg ${ui.itemThumb} p-2 flex items-center justify-center shrink-0`}>
                    <img src={item.image_url || item.image_uri} className="max-w-full max-h-full object-contain" alt={item.title} />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className={`text-xs md:text-sm font-medium leading-tight ${ui.itemTitle}`}>
                        {item.title || 'Untitled'}{itemQuantity(item) > 1 ? ` ×${itemQuantity(item)}` : ''}
                      </h3>
                      {item.selectedSize && (
                        <p className={`text-[9px] md:text-[10px] uppercase tracking-wider ${ui.emptyText} mt-1`}>
                          Size <span className={`${ui.sizeValue} font-medium normal-case tracking-normal`}>{item.selectedSize}</span>
                        </p>
                      )}
                      {/* Line total, not unit price — the Total below is quantity-aware, so a ×3
                          row showing the unit price would leave the sum unexplained. */}
                      <p className={`text-[10px] md:text-xs ${ui.itemPrice} font-medium mt-1`}>{linePriceDisplay(item)}</p>
                    </div>
                    <button onClick={() => handleRemoveFromCart(item)} className="text-[10px] md:text-xs flex items-center gap-1 text-red-500 hover:text-red-600 w-fit font-medium"><Trash2 size={12} /> Remove</button>
                  </div>
                </div>
              ))}
            </div>
            {cartItems.length > 0 && (
              <div className={`flex-none p-6 border-t ${ui.footerBand}`}>
                <div className="flex justify-between items-end mb-5 px-1">
                  <span className={`text-[10px] md:text-xs ${ui.totalLabel} tracking-widest uppercase font-semibold`}>Total</span>
                  <span className={`text-lg md:text-xl font-medium ${ui.totalValue}`}>{formatPrice(cartTotal, brandConfig.currency)}</span>
                </div>
                {/* SPEC-46 Rev 7: this legacy (non-reel) drawer button had no onClick at all —
                    it has been dead for every tenant that does not get reel chrome. Wired to the
                    same request the reel CTAs use. JC AC-30/D-23: it always renders now, same as
                    the reel CTAs — `requestCheckout` is a silent no-op without a publishable
                    key, so a config gap is invisible to the guest instead of a hidden button. */}
                <button
                  onClick={checkout.requestCheckout}
                  className={`w-full py-3 md:py-4 ${ui.checkoutBtn} font-medium tracking-widest uppercase text-[10px] md:text-xs rounded-full transition-colors shadow-md`}
                >
                  Checkout
                </button>
              </div>
            )}
          </motion.div>
        )}

        {overlay === 'WISHLIST' && !isReelChrome && (
          <motion.div key="wishlist-overlay" initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className={`pointer-events-auto fixed left-0 top-0 bottom-0 ${ui.drawer} border-r ${ui.drawerEdge} w-[85vw] md:w-96 shadow-2xl flex flex-col z-[70]`}>
            <div className={`flex-none flex justify-between items-center p-6 md:p-8 border-b ${ui.drawerHeadEdge}`}>
              <h2 className={`text-xl md:text-2xl font-light tracking-wide ${ui.drawerTitle}`}>Saved for Later</h2>
              <button onClick={() => setOverlay(null)} className={`p-2 ${ui.closeBtn} rounded-full transition-colors`}><X size={18} className={ui.closeIcon} /></button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-3 md:space-y-4 custom-scrollbar p-6">
              {wishlistItems.length === 0 ? <p className={`${ui.emptyText} text-sm font-light mt-4 text-center`}>No items saved.</p> : wishlistItems.map((item, idx) => (
                <div key={idx} className={`flex gap-3 md:gap-4 ${ui.itemCard} p-3 rounded-xl`}>
                  <div className={`w-16 h-20 md:w-20 md:h-24 rounded-lg ${ui.itemThumb} p-2 flex items-center justify-center shrink-0`}>
                    <img src={item.image_url || item.image_uri} className="max-w-full max-h-full object-contain" alt={item.title} />
                  </div>
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h3 className={`text-xs md:text-sm font-medium leading-tight ${ui.itemTitle}`}>{item.title}</h3>
                      <p className={`text-[10px] md:text-xs ${ui.itemPriceAlt} mt-1`}>{priceDisplay(item)}</p>
                    </div>
                    <div className="flex justify-between items-center">
                      <button onClick={() => handleToggleWishlist(item)} className={`text-[10px] md:text-xs flex items-center gap-1 ${ui.removeBtn} w-fit`}>
                        <X size={12} /> Remove
                      </button>
                      <button onClick={() => handleAddToCart(item)} className={`text-[9px] md:text-[10px] uppercase tracking-widest ${ui.addBtn} px-3 py-1.5 rounded-full font-semibold`}>
                        Add
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* Non-reel tenants' privacy panel — same overlay slot, same drawer chrome, same
            auto-collapse-the-others behavior as CART/WISHLIST above; only the row styling
            (ConsentCategoryList's "generic" theme) differs from the reel-chrome rail panel. */}
        {overlay === 'PRIVACY' && !isReelChrome && (
          <motion.div key="privacy-overlay" initial={{ opacity: 0, x: -50 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -50 }} className={`pointer-events-auto fixed left-0 top-0 bottom-0 ${ui.drawer} border-r ${ui.drawerEdge} w-[85vw] md:w-96 shadow-2xl flex flex-col z-[70]`}>
            <div className={`flex-none flex justify-between items-center p-6 md:p-8 border-b ${ui.drawerHeadEdge}`}>
              <h2 className={`text-xl md:text-2xl font-light tracking-wide ${ui.drawerTitle}`}>{DISCLOSURE.manageTitle}</h2>
              <button onClick={() => setOverlay(null)} className={`p-2 ${ui.closeBtn} rounded-full transition-colors`}><X size={18} className={ui.closeIcon} /></button>
            </div>
            <div className="flex-1 overflow-y-auto custom-scrollbar p-6 space-y-4">
              <button
                type="button"
                onClick={() => setPrivacyOpen(true)}
                className={`inline-block text-xs tracking-widest uppercase underline underline-offset-4 ${ui.drawerTitle} hover:opacity-70 transition-opacity`}
              >
                {DISCLOSURE.privacyLinkLabel}
              </button>
              <ConsentCategoryList
                selected={privacySelected}
                onToggle={(id) => setPrivacySelected(prev => ({ ...prev, [id]: !prev[id] }))}
                theme="generic"
              />
            </div>
            <div className={`flex-none flex gap-3 p-6 border-t ${ui.drawerHeadEdge}`}>
              <button
                type="button"
                onClick={() => setOverlay(null)}
                className="flex-1 px-6 py-3 rounded-full border border-black/20 bg-white hover:bg-black/5 transition-colors tracking-widest uppercase text-xs font-medium"
              >
                {DISCLOSURE.cancelLabel}
              </button>
              <button
                type="button"
                onClick={() => { handlePrefsSave({ ...privacySelected, necessary: true }); setOverlay(null); }}
                className="flex-1 px-6 py-3 rounded-full border border-black bg-black text-white hover:bg-neutral-800 transition-colors tracking-widest uppercase text-xs font-medium"
              >
                {DISCLOSURE.saveLabel}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🌟 CAVALRY VOICE VISUALIZER OVERLAY — legacy (non-reel) chrome only. In reel mode the
          same CavalryViz instance renders inside ReelFooter/MobileFooter's visualizer slot
          instead (compact, in the bottom bar); mounting both at once would race two canvases
          against CavalryViz's single shared WebGL surface. */}
      {!isReelChrome && (brandConfig.visualizer_mode || DEFAULT_VISUALIZER_MODE) === 'cavalry' && isConnected && (
        <div className="fixed bottom-0 left-0 right-0 flex justify-center z-[55] pointer-events-none">
          {/* Same amplitude wiring as the reel footer above — this instance had none at all, so
              its speaking shape was a flat on/off blend over the authored loop while the reel one
              breathed with the voice. */}
          <CavalryViz state={visualizerState} maxWidth={560} amplitudeRef={currentVolume} />
        </div>
      )}

      {/* SPEC-26 Phase 4 Revision: reel mode replaces the legacy control pill with the authored
          footer composition — keyboard and mic flanking the CC bar from outside it (R.3 defects
          5-8). Portaled above the reel for the same reason the point-tag layer is (D6). */}
      {/* SPEC-26 Phase 4b: mobile stacks the keyboard and mic in a column to the RIGHT of the bar
          and stacks the two captions inside it, where desktop flanks the bar from both sides and
          sets the captions in outer columns. Same props, same handlers, same CaptionOverlay
          leaves — only the arrangement is composition-specific. */}
      {isReelChrome && inBoutique && !showIntro && isMobileComposition && (
        <OverlayPortal>
          <div className="ui-reveal-fade">
            <MobileFooter
              ccEnabled={ccEnabled}
              isKeyboardOpen={isKeyboardOpen}
              onOpenKeyboard={() => setIsKeyboardOpen(true)}
              onCloseKeyboard={() => setIsKeyboardOpen(false)}
              textInput={textInput}
              onTextInputChange={setTextInput}
              onSubmitText={handleTextSubmit}
              isMicOn={micShownOn}
              onToggleMic={onToggleMic}
              agentState={agentState}
              showCamera={Boolean(brandConfig.feature_flags?.garment_upload)}
              onCamera={() => { imageUploadRef.current?.click(); setUploadHint(false); }}
              showStop={manualStop.showStop}
              canStop={manualStop.canStop}
              onStopAgent={() => manualStop.stop()}
              visualizer={footerVisualizer}
              voiceFirst={voiceFirst.footer} // SPEC-82
            />
          </div>
        </OverlayPortal>
      )}

      {isReelChrome && inBoutique && !showIntro && !isMobileComposition && (
        <OverlayPortal>
          <div className="ui-reveal-fade">
            <ReelFooter
              ccEnabled={ccEnabled}
              onToggleCc={() => setCcEnabled(!ccEnabled)}
              isKeyboardOpen={isKeyboardOpen}
              onOpenKeyboard={() => setIsKeyboardOpen(true)}
              onCloseKeyboard={() => setIsKeyboardOpen(false)}
              textInput={textInput}
              onTextInputChange={setTextInput}
              onSubmitText={handleTextSubmit}
              isMicOn={micShownOn}
              onToggleMic={onToggleMic}
              agentState={agentState}
              showCamera={Boolean(brandConfig.feature_flags?.garment_upload)}
              onCamera={() => { imageUploadRef.current?.click(); setUploadHint(false); }}
              showStop={manualStop.showStop}
              canStop={manualStop.canStop}
              onStopAgent={() => manualStop.stop()}
              visualizer={footerVisualizer}
              voiceFirst={voiceFirst.footer} // SPEC-82
            />
          </div>
        </OverlayPortal>
      )}

      {/* SPEC-57: the manual reel-view toggle. Below the footer/mic portal (65) and the rail (80),
          above the SPEC-53 stage layers (D-7) so it survives the black fade like the CC bar and
          mic; hidden on the piece card (D-11, useReelViewToggle.visible). */}
      {isReelChrome && inBoutique && !showIntro && viewToggle.visible && !voiceFirst.hideViews && (
        <OverlayPortal zIndex={VIEW_TOGGLE_Z}>
          <ReelViewToggle
            composition={composition}
            open={viewToggle.open}
            onToggleOpen={viewToggle.toggle}
            buttons={viewToggle.buttons}
            hasTags={viewToggle.hasTags}
            topShift={voiceFirst.topChrome.viewsShift} // SPEC-82: 0 unless collection_menu shares the corner
          />
        </OverlayPortal>
      )}

      {/* 🌟 3. ABSOLUTE FOOTER ZONE (Subtitles & Pill Fixed to Viewport) — hidden during intro */}
      {!showIntro && !isReelChrome && ['original', 'pill'].includes(brandConfig.visualizer_mode || DEFAULT_VISUALIZER_MODE) && <div className="fixed bottom-6 md:bottom-8 left-0 right-0 w-full flex justify-center z-[60] pointer-events-none px-4">

        {/* 🌟 DRAGGABLE SUBTITLES */}
        <div className="absolute bottom-[100%] left-0 right-0 w-full flex justify-center mb-4">
          <AnimatePresence>

            {false && !hasInteracted && isConnected && hasWelcomed && agentState === 'listening' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -5 }}
                className="absolute bottom-[100%] mb-40 bg-black/70 backdrop-blur-md px-6 py-3 rounded-full shadow-xl border border-white/10 pointer-events-none overflow-hidden"
              >
                <div className="absolute inset-0 -translate-x-full animate-[shimmer_2.5s_infinite] bg-gradient-to-r from-transparent via-white/20 to-transparent" />
                <p className="text-sm text-white/90 font-light tracking-wide text-center flex items-center gap-2">
                  <span className="text-white/50">Try saying:</span>
                  <AnimatePresence mode="wait">
                    <motion.span
                      key={currentSuggestions[suggestionIndex]}
                      initial={{ opacity: 0, y: 5 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -5 }}
                      transition={{ duration: 0.3 }}
                      className="italic"
                    >
                      "{currentSuggestions[suggestionIndex]}"
                    </motion.span>
                  </AnimatePresence>
                </p>
              </motion.div>
            )}

            {/* SPEC-26 AC-7: the legacy combined box does not render in reel mode — the
                Projectionist's split CC pair (CaptionOverlay.User/Agent) replaces it there. */}
            {ccEnabled && activeSubtitle && !brandConfig.feature_flags?.reel_mode && (
              <motion.div
                key="subtitle-box"
                drag
                dragMomentum={false}
                whileDrag={{ scale: 1.02 }}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                className="bg-black/80 backdrop-blur-md px-6 py-3 rounded-2xl shadow-xl border border-white/10 pointer-events-auto w-fit max-w-[90vw] md:max-w-2xl cursor-grab active:cursor-grabbing"
              >
                <span className="text-[10px] uppercase tracking-widest text-white/50 mb-1 font-semibold block text-center">
                  {activeSubtitle.sender === 'user' ? 'You' : agentName}
                </span>
                <p className="text-sm md:text-base text-white/95 font-light text-center leading-relaxed">
                  {activeSubtitle.text}
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* 🌟 NON-BOUNCING PILL (Fixed height, PopLayout animation) */}
        <div className={`pointer-events-auto ${ui.pill} p-1.5 md:p-2 rounded-full shadow-2xl overflow-hidden relative flex items-center h-[52px] md:h-[64px] transition-all duration-300 ease-in-out ${isKeyboardOpen ? 'w-[95%] max-w-lg' : 'w-fit'}`}>
          <AnimatePresence mode="popLayout">
            {isKeyboardOpen ? (
              /* ⌨️ TEXT INPUT MODE */
              <motion.form
                key="keyboard-mode"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                onSubmit={handleTextSubmit}
                className="flex items-center w-full h-full"
              >
                <div className={`flex items-center gap-2 pl-3 pr-3 border-r ${ui.pillDivider} shrink-0`}>
                  <div className={`w-1.5 h-1.5 rounded-full ${isAgentSpeaking ? 'bg-blue-500 shadow-[0_0_8px_rgba(59,130,246,0.8)]' : isReelChrome ? 'bg-white/25' : 'bg-black/20'}`} />
                  <span className={`text-[9px] md:text-[10px] w-20 tracking-widest uppercase ${ui.pillStatus} font-medium ml-1 flex items-center`}>
                    {agentState === 'thinking' ? (
                      <span className="flex items-center gap-1.5 text-blue-500">
                        <Loader2 size={10} className="animate-spin" /> Thinking
                      </span>
                    ) : agentState === 'speaking' ? (
                      <span className={ui.pillStatusStrong}>Speaking</span>
                    ) : (
                      'Listening'
                    )}
                  </span>
                </div>

                <input
                  type="text"
                  value={textInput}
                  onChange={(e) => setTextInput(e.target.value)}
                  placeholder="Type a message..."
                  className={`flex-1 bg-transparent border-none focus:outline-none text-sm font-light ${ui.pillInput} px-3 md:px-4 min-w-0 h-full`}
                  disabled={agentState === 'thinking'}
                  autoFocus
                />
                <button type="submit" disabled={!textInput.trim() || agentState === 'thinking'} className={`w-8 h-8 md:w-10 md:h-10 rounded-full ${ui.pillSendBtn} flex items-center justify-center disabled:opacity-50 shrink-0`}>
                  <Send size={14} />
                </button>
                <button type="button" onClick={() => setIsKeyboardOpen(false)} className={`w-8 h-8 md:w-10 md:h-10 rounded-full ${ui.pillCloseBtn} flex items-center justify-center ml-2 shrink-0 transition-colors`}>
                  <X size={16} />
                </button>
              </motion.form>
            ) : (
              /* 🎙️ VOICE MODE */
              <motion.div
                key="voice-mode"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="flex items-center justify-between w-fit h-full px-1 gap-4"
              >
                <div className="flex items-center gap-3 h-full">
                  <button
                    onClick={() => (hasAudioConsentRef.current ? setIsMicOn(!isMicOn) : setOverlay('PRIVACY'))}
                    className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all duration-300 shrink-0 ${isMicOn ? ui.micOn : ui.micOff}`}
                  >
                    {isMicOn ? <Mic size={16} /> : <MicOff size={16} />}
                  </button>

                  {isMicOn && (
                    <div className="flex items-center gap-2 md:gap-3">
                      {(brandConfig.visualizer_mode || DEFAULT_VISUALIZER_MODE) === 'pill'
                        ? <ParticlePill
                            // Was an inline copy of the old `(isAgentSpeaking || isUserSpeaking)
                            // ? 'speaking' : agentState` rule, which drifted the moment the states
                            // were re-cut by speaker (2026-09-01) and never carried the reaction
                            // precedence at all. One resolver, one answer — the enclosing
                            // `isMicOn &&` still stands in for the mute rule on this pill.
                            state={visualizerState}
                            size={36}
                            dotColor={ui.pillDotColor}
                          />
                        : <Visualizer volumeRef={currentVolume} />
                      }
                      <span className={`text-[9px] md:text-[10px] w-20 tracking-widest uppercase ${ui.pillStatus} font-medium ml-1 flex items-center`}>
                        {agentState === 'thinking' ? (
                          <span className="flex items-center gap-1.5 text-blue-500">
                            <Loader2 size={10} className="animate-spin" /> Thinking
                          </span>
                        ) : agentState === 'speaking' ? (
                          <span className={ui.pillStatusStrong}>Speaking</span>
                        ) : (
                          'Listening'
                        )}
                      </span>
                    </div>
                  )}
                </div>

                <div className={`flex items-center gap-1 border-l ${ui.pillDivider} pl-2 ml-1 h-full`}>
                  <button
                    onClick={() => setCcEnabled(!ccEnabled)}
                    className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center transition-all shrink-0 ${ccEnabled ? ui.ccOn : ui.ccOff}`}
                    title="Toggle Subtitles"
                  >
                    <div className="text-[9px] md:text-[10px] font-bold tracking-widest border-b-2 border-current leading-none pb-0.5">CC</div>
                  </button>
                  <button
                    onClick={() => setIsKeyboardOpen(true)}
                    className={`w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center ${ui.pillIconBtn} transition-colors shrink-0`}
                    title="Type a message"
                  >
                    <Keyboard size={16} />
                  </button>
                  {/* 📸 SPEC-5/6: upload a garment/watch photo (gated per tenant) */}
                  {brandConfig.feature_flags?.garment_upload && (
                    <button
                      onClick={() => { imageUploadRef.current?.click(); setUploadHint(false); }}
                      className={`relative w-8 h-8 md:w-10 md:h-10 rounded-full flex items-center justify-center shrink-0 transition-all ${uploadHint ? 'bg-black text-white ring-2 ring-black/30 animate-pulse' : ui.pillIconBtn}`}
                      title="Share a photo of your own piece or watch"
                    >
                      <Camera size={16} />
                    </button>
                  )}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>}

      {/* 📸 SPEC-5/6: hidden file input for garment/watch upload */}
      <input
        ref={imageUploadRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleImageUpload(e, 'garment')}
      />


      {/* 📸 SPEC-5/6: "analysing" state while the look is being generated */}
      <AnimatePresence>
        {isAnalysing && !styledLook && (
          <motion.div
            key="analysing-look"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[160] bg-black/80 backdrop-blur-sm flex flex-col items-center justify-center pointer-events-auto"
          >
            <Loader2 size={40} className="animate-spin text-white/80" />
            <p className="mt-6 text-white/80 tracking-widest uppercase text-xs font-medium">Styling your look…</p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 📸 SPEC-5/6: generated styled-look composite overlay */}
      <AnimatePresence>
        {styledLook && (
          <motion.div
            key="styled-look"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.5 } }}
            className="fixed inset-0 z-[170] bg-black flex flex-col items-center justify-center pointer-events-auto p-6"
          >
            <button
              onClick={() => setStyledLook(null)}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>
            {/* 📸 Subtle feedback while a NEW pose/view generates — the current image stays visible. */}
            {isAnalysing && (
              <div className="absolute top-6 left-6 flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full px-4 py-2">
                <Loader2 size={14} className="animate-spin text-white/80" />
                <span className="text-white/80 text-[11px] tracking-widest uppercase">Creating another view…</span>
              </div>
            )}
            {styledLook.image && (
              <img
                src={styledLook.image}
                alt="Your styled look"
                className="max-h-[72vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
              />
            )}
            {styledLook.pieces?.length > 0 && (
              <div className="mt-5 flex flex-wrap items-center justify-center gap-2 max-w-3xl">
                {styledLook.pieces.map((p, i) => (
                  <span key={i} className="text-white/70 text-[11px] tracking-wide uppercase border border-white/20 rounded-full px-3 py-1">
                    {p?.title || 'Piece'}
                  </span>
                ))}
              </div>
            )}
            {/* 🎬 SPEC-7: manual trigger to render the look as a Veo film (deterministic fallback to
                the agent's spoken offer). Disabled + shows progress while rendering. */}
            <button
              onClick={() => {
                if (isAnimating) return;
                setIsAnimating(true);
                try { ws.current?.send(JSON.stringify({ type: 'generate_video' })); } catch (e) { }
              }}
              disabled={isAnimating}
              className="mt-6 flex items-center gap-2 bg-white text-black hover:bg-white/90 disabled:opacity-60 disabled:cursor-not-allowed rounded-full px-6 py-2.5 text-[11px] tracking-widest uppercase font-medium transition-colors"
            >
              {isAnimating
                ? (<><Loader2 size={14} className="animate-spin" /> Bringing it to life…</>)
                : (<><Film size={14} /> See it in motion</>)}
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 🎬 SPEC-7: generated Veo try-on film — plays full-screen with audio. */}
      <AnimatePresence>
        {styledVideo && (
          <motion.div
            key="styled-video"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { duration: 0.5 } }}
            className="fixed inset-0 z-[180] bg-black flex flex-col items-center justify-center pointer-events-auto p-6"
          >
            <button
              onClick={() => setStyledVideo(null)}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors"
              title="Close"
            >
              <X size={18} />
            </button>
            {styledVideo.video && (
              <video
                src={styledVideo.video}
                autoPlay
                loop
                playsInline
                controls
                className="max-h-[80vh] max-w-[92vw] object-contain rounded-lg shadow-2xl"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* SPEC-26 Phase 5: last child and z-[300] — above every other surface in the app, including
          the consent gate and the full-screen try-on film. A device held the wrong way must see
          this and nothing else. */}
      {orientationLock}

    </div>
  );
};

const ProductCard = ({ item, onClick }) => {
  // Watch-specific keys win first (keeps the watches demo unchanged); otherwise fall back to
  // the brand-agnostic envelope subtitle (e.g. "100% Wool · Micro Puppytooth" for suits).
  const displayAttr =
    item.specifications?.Hands ||
    item.specifications?.Dial_Color ||
    item.specifications?.Caliber_Type ||
    item.specifications?.Shape ||
    item.subtitle ||
    "";
  return (
    <motion.div layoutId={item.id} onClick={onClick} className="group relative h-[220px] md:h-[260px] lg:h-[300px] bg-white rounded-xl md:rounded-2xl overflow-hidden cursor-pointer ring-1 ring-black/5 hover:ring-black/20 shadow-sm transition-all flex flex-col justify-end isolate">
      <div className="absolute inset-0 p-3 md:p-4 pb-16 md:pb-20 flex items-center justify-center bg-neutral-50/50">
        {/* 🌟 ADDED loading="lazy" HERE */}
        <img src={item.image_url || item.image_uri} alt={item.title} loading="lazy" className="max-w-full max-h-full object-contain opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-700 mix-blend-multiply" />
      </div>
      <div className="relative z-10 bg-gradient-to-t from-white via-white/90 to-transparent pt-12 p-3 md:p-5">
        <div className="transform translate-y-1 md:translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
          <h3 className="text-neutral-900 font-medium text-xs md:text-base leading-tight mb-1 truncate">{item.title}</h3>
          <div className="flex justify-between items-end">
            <p className="text-neutral-600 text-[10px] md:text-xs font-light tracking-wide">{priceDisplay(item)}</p>
            <span className="text-[8px] md:text-[9px] uppercase tracking-widest text-neutral-600 border border-black/10 bg-white/80 backdrop-blur px-1.5 md:px-2 py-0.5 rounded-sm truncate max-w-[60px] md:max-w-[100px] font-medium">
              {displayAttr}
            </span>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const Visualizer = ({ volumeRef }) => {
  const barsRef = useRef([]);
  // The bar heights are the JS half of a treatment whose gaps and widths are Tailwind breakpoints
  // (`md:` on the wrapper below), so this reads the same composition rule the rest of the app does
  // rather than its own `window.innerWidth < 768`. Two benefits over the raw read: it agrees with
  // the chrome on a narrow desktop window, and it is no longer a layout-triggering DOM measurement
  // taken once per animation frame — the value is resolved at render and closed over.
  const isMobile = useBreakpoint() === 'mobile';

  useEffect(() => {
    let frame;
    let time = 0;

    const loop = () => {
      time += 0.15;
      const vol = volumeRef.current || 0;
      const baseHeight = isMobile ? 3 : 6;
      const maxHeight = isMobile ? 18 : 28;
      const isActive = vol > 0.01;

      barsRef.current.forEach((bar, i) => {
        if (!bar) return;

        if (isActive) {
          const wave = Math.sin(time + i * 0.6) * 0.5 + 0.5;
          const height = baseHeight + (vol * (maxHeight - baseHeight) * wave);
          bar.style.height = `${height}px`;
          bar.style.opacity = '1';
        } else {
          bar.style.height = `${baseHeight / 2}px`;
          bar.style.opacity = '0.3';
        }
      });

      frame = requestAnimationFrame(loop);
    };

    loop();
    return () => cancelAnimationFrame(frame);
  }, [volumeRef, isMobile]);

  return (
    <div className="flex items-center gap-[2px] md:gap-[3px] h-4 md:h-6">
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          ref={el => barsRef.current[i] = el}
          className="w-[3px] md:w-1 bg-black rounded-full"
          style={{ transition: 'height 0.1s ease-out, opacity 0.3s ease' }}
        />
      ))}
    </div>
  );
};

export default App;